import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, Clock, ChevronRight, Mail, Phone, Send, Menu, X, ArrowRight, ArrowLeft, ArrowUpRight, BookOpen, Instagram, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PAU Logo Images
const PAU_LOGOS = {
  green: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/dtc2a6zl_260207_PAU_LOGO_TYPE_G.png",
  white: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/zhbi3k0f_260207_PAU_LOGO_TYPE_W.png",
  black: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/s4ketawi_260207_PAU_LOGO_TYPE_B.png",
  red: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/6cw1wpfj_260207_PAU_LOGO_TYPE_R.png",
  trackWhite: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/48nk16b7_260207_PAU_LOGO_TRACK_W.png",
  trackGRW: "https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/6rd6px31_260207_PAU_LOGO_TRACK_G-R-W.png",
};

// Rude Cat Mascot
const RUDE_CAT = "https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/aghnl5gl_260207_PAU_RudeCat_B-W%20%282%29.webp";

const RudeCat = ({ size = "md", className = "", style = {} }) => {
  const sizes = {
    xs: "w-8 h-8",
    sm: "w-12 h-12",
    md: "w-16 h-16",
    lg: "w-24 h-24",
    xl: "w-32 h-32",
    xxl: "w-48 h-48",
  };
  return (
    <img 
      src={RUDE_CAT} 
      alt="PAU Rude Cat" 
      className={`${sizes[size]} object-contain opacity-80 hover:opacity-100 transition-opacity ${className}`}
      style={style}
    />
  );
};

// PAU Logo Component
const PAULogo = ({ variant = "green", className = "" }) => (
  <img 
    src={PAU_LOGOS[variant]} 
    alt="PAU" 
    className={`h-10 md:h-12 ${className}`}
  />
);

// Navigation Component
const Navigation = ({ variant = "dark" }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Colors based on variant (dark = light text for dark bg, light = dark text for light bg)
  const isLight = variant === "light";
  const linkColor = isLight ? "text-[#1A1714]/70 hover:text-[#1A1714]" : "text-[#E8E4DC]/80 hover:text-[#E8E4DC]";
  const mobileIconColor = isLight ? "text-[#1A1714]" : "text-[#E8E4DC]";
  const ctaBg = isLight ? "bg-[#1A1714] text-[#E8E4DC] hover:bg-[#2A2724]" : "bg-[#8B9A7D] text-[#1A1714] hover:bg-[#9AAA8D]";
  
  const navLinks = [
    { name: "TRAINING", href: "/training" },
    { name: "CAMPS", href: "/camps" },
    { name: "COACHES", href: "/coaches" },
    { name: "COMMUNITY", href: "/community" },
    { name: "MAGAZINE", href: "https://pau-mag.com", external: true, highlight: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-transparent" data-testid="main-navigation">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <PAULogo variant={isLight ? "black" : "green"} />
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${link.highlight ? 'text-[#B8352B] hover:text-[#D84535]' : linkColor} transition-colors text-sm tracking-[0.2em]`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`${linkColor} transition-colors text-sm tracking-[0.2em]`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
            
            {/* CTA Button */}
            <Link
              to="/apply"
              className={`${ctaBg} px-6 py-3 text-sm font-medium tracking-wider transition-colors`}
              data-testid="nav-cta"
            >
              BOOK A TRIAL
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`lg:hidden ${mobileIconColor} p-2`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-[#3A3633] pt-4 bg-[#1A1714]/95 backdrop-blur-md -mx-6 px-6" data-testid="mobile-menu">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block py-3 ${link.highlight ? 'text-[#B8352B] hover:text-[#D84535]' : 'text-[#E8E4DC]/65 hover:text-[#E8E4DC]'} transition-colors text-sm tracking-[0.2em]`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block py-3 text-[#E8E4DC]/65 hover:text-[#E8E4DC] transition-colors text-sm tracking-[0.2em]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/apply"
              className="block mt-4 py-4 bg-[#8B9A7D] text-[#1A1714] text-center font-medium tracking-wider"
              onClick={() => setMobileMenuOpen(false)}
            >
              BOOK A TRIAL
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 px-6 bg-[#1A1714] border-t border-[#3A3633]" data-testid="footer">
    <div className="max-w-6xl mx-auto">
      {/* Main Footer Content */}
      <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-8">
        <div className="flex items-center gap-3">
          <PAULogo variant="green" className="h-6 opacity-60" />
          <span className="text-[#E8E4DC]/50 text-sm">© 2026 PAU</span>
        </div>
        
        {/* Footer Links */}
        <div className="flex flex-wrap gap-6 text-[#E8E4DC]/50 text-sm">
          <Link to="/training" className="hover:text-[#E8E4DC] transition-colors">Training</Link>
          <Link to="/camps" className="hover:text-[#E8E4DC] transition-colors">Camps</Link>
          <Link to="/athletes" className="hover:text-[#E8E4DC] transition-colors">Athletes</Link>
          <Link to="/coaches" className="hover:text-[#E8E4DC] transition-colors">Coaches</Link>
          <Link to="/coaching" className="hover:text-[#E8E4DC] transition-colors">Coaching</Link>
          <Link to="/apply" className="hover:text-[#E8E4DC] transition-colors">Apply</Link>
        </div>
        
        <div className="flex items-center gap-6 text-[#E8E4DC]/50 text-sm">
          <a href="mailto:active@pauuniverse.com" className="hover:text-[#E8E4DC] transition-colors">
            active@pauuniverse.com
          </a>
          <span>Zürich, Switzerland</span>
        </div>
      </div>
      
      {/* Non-profit Support Note */}
      <div className="pt-6 border-t border-[#3A3633]/50 flex items-start justify-between">
        <div>
          <p className="text-[#E8E4DC]/40 text-xs leading-relaxed max-w-2xl">
            PAU is a non-profit initiative. We're always grateful for support that helps athletes train, travel, and compete.
          </p>
          <p className="text-[#E8E4DC]/30 text-xs mt-2 font-mono tracking-wide">
            IBAN: CH61 0900 0000 1676 6975 6
          </p>
        </div>
        <RudeCat size="lg" className="hidden md:block -rotate-12 opacity-60" />
      </div>
    </div>
  </footer>
);

// ============================================
// TRAINING PAGE - Club Operations & Pricing
// ============================================
const TrainingPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Video Hero Header */}
      <section className="relative">
        <div className="h-[50vh] md:h-[60vh] relative overflow-hidden">
          <video 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/e79m1g4c_C0009_00086400.mp4"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/30 to-transparent" />
        </div>
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Zürich-Based Track Club</p>
            <h1 className="flex items-center gap-4 mb-4">
              <PAULogo variant="white" className="h-12 md:h-16 lg:h-20" />
              <span className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase">Training</span>
            </h1>
            <p className="text-[#E8E4DC]/80 text-lg md:text-xl max-w-2xl">
              Structured training cycles, coaching feedback, and a team environment that raises standards.
            </p>
          </div>
        </div>
      </section>

      {/* What Athletes Can Expect */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">What Athletes Can Expect</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Structured Training Cycles</h3>
              <p className="text-[#E8E4DC]/60">Clear phases, progression, and intent behind every session.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Coaching Feedback</h3>
              <p className="text-[#E8E4DC]/60">Accountability and guidance to keep you on track.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Team Environment</h3>
              <p className="text-[#E8E4DC]/60">A culture that raises standards and values ambition.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Consistency & Craft</h3>
              <p className="text-[#E8E4DC]/60">We value showing up, doing the work, and getting better.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Image - Athletes at fence */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-[16/9] overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/n6kuxobu_26-08-04_PAU_260804-Fronwald_NR_7009_Highres.webp"
              alt="Athletes at the track"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Weekly Schedule */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Weekly Structure — Zürich</h2>
          <div className="grid gap-4">
            {[
              { day: 'Monday', activity: 'Track Training', location: 'Sportanlage Fronwald' },
              { day: 'Tuesday', activity: 'Gym', location: 'Balboa Underground' },
              { day: 'Wednesday', activity: 'Active Recovery / Mobility', location: 'Program provided for individual training' },
              { day: 'Thursday', activity: 'Track Training', location: 'Sportanlage Fronwald' },
              { day: 'Friday', activity: 'Gym', location: 'Balboa Underground' },
              { day: 'Saturday', activity: 'Community Training (Open)', location: 'Sportanlage Fronwald' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-4 border-b border-[#3A3633] last:border-0">
                <div className="flex items-center gap-8">
                  <span className="text-[#E8E4DC] font-medium w-28">{item.day}</span>
                  <span className="text-[#E8E4DC]/70">{item.activity}</span>
                </div>
                {item.location && <span className="text-[#E8E4DC]/40 text-sm hidden md:block">{item.location}</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Image */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/1ghb54z1_26-08-04_PAU_260804-Fronwald_NR_6875_Highres.webp"
              alt="Coach talking with athletes"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Training Image - Track sunset */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/tmv8v586_IMG_0263.webp"
              alt="Track at sunset"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em]">Seasonal Membership</h2>
            <RudeCat size="sm" className="-rotate-6" />
          </div>
          <p className="text-[#E8E4DC]/60 mb-12 max-w-2xl">
            Membership runs in 6-month seasons. Clear commitment, consistent training, sustainable coaching.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Performance */}
            <div className="p-8 bg-[#252220] border border-[#8B9A7D]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Tier 1</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-2">Performance</h3>
              <p className="text-[#E8E4DC] text-3xl font-light mb-4">CHF 600<span className="text-lg text-[#E8E4DC]/50"> / season</span></p>
              <ul className="space-y-3 text-[#E8E4DC]/60 text-sm mb-8">
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Higher individual support</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Gym training included</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Competition licensing</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Individualised schedules</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Competition support</li>
              </ul>
              <Link to="/apply" className="block w-full py-3 bg-[#8B9A7D] text-[#1A1714] text-center font-medium">
                Apply Now
              </Link>
            </div>

            {/* Regular */}
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/40 text-xs uppercase tracking-wider mb-2">Tier 2</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-2">Regular</h3>
              <p className="text-[#E8E4DC] text-3xl font-light mb-4">CHF 400<span className="text-lg text-[#E8E4DC]/50"> / season</span></p>
              <ul className="space-y-3 text-[#E8E4DC]/60 text-sm mb-8">
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Full group training access</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Gym training included</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Coaching feedback</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Community & structure</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Partner perks</li>
              </ul>
              <Link to="/apply" className="block w-full py-3 border border-[#E8E4DC]/30 text-[#E8E4DC] text-center font-medium hover:bg-[#E8E4DC]/10">
                Apply Now
              </Link>
            </div>

            {/* Drop-in */}
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/40 text-xs uppercase tracking-wider mb-2">Tier 3</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-2">Drop-in</h3>
              <p className="text-[#E8E4DC] text-3xl font-light mb-4">CHF 300<span className="text-lg text-[#E8E4DC]/50"> / season</span></p>
              <ul className="space-y-3 text-[#E8E4DC]/60 text-sm mb-8">
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> 1 training per week</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> 4 sessions per month</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Flexible participation</li>
                <li className="flex items-start gap-2"><span className="text-[#8B9A7D]">+</span> Ideal for speed training as cross-training from team sports, Hyrox, etc.</li>
              </ul>
              <Link to="/apply" className="block w-full py-3 border border-[#E8E4DC]/30 text-[#E8E4DC] text-center font-medium hover:bg-[#E8E4DC]/10">
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Perks */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Member Perks</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-5 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/80 text-sm">50% discount at lululemon (orders via PAU)</p>
            </div>
            <div className="p-5 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/80 text-sm">Products from beverage sponsors</p>
            </div>
            <div className="p-5 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/80 text-sm">Discounted gym access via IbriAthletics</p>
            </div>
            <div className="p-5 bg-[#252220] border border-[#3A3633]">
              <p className="text-[#E8E4DC]/80 text-sm">Nutritional consulting access</p>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Training */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633] bg-[#252220]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">1-on-1 Coaching</p>
              <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
                Personal Training
              </h2>
              <p className="text-[#E8E4DC]/60 mb-4">
                Work directly with our coaches for tailored programming, technique analysis, and race preparation. 
                Available for sprints, speed development (100–800m), and team sports speed coaching.
              </p>
              <p className="text-[#E8E4DC]/50 text-sm mb-6">
                Indoor & outdoor sessions available at various gyms, tracks & grassfields across Zurich.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://wa.me/41772221425?text=Hi%2C%20I'm%20interested%20in%20personal%20training"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 font-medium hover:bg-[#20BD5A] transition-colors rounded"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Book via WhatsApp
                </a>
                <Link
                  to="/coaches"
                  className="inline-flex items-center gap-2 border border-[#E8E4DC]/30 text-[#E8E4DC] px-6 py-3 font-medium hover:bg-[#E8E4DC]/10 transition-colors"
                >
                  Meet the Coaches
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 bg-[#1A1714] border border-[#3A3633] rounded-xl">
                <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Noé Ramos</h3>
                <p className="text-[#8B9A7D] text-xs mb-2">Coach</p>
                <a href="https://www.instagram.com/raaamoossss/" target="_blank" rel="noopener noreferrer" className="text-[#E8E4DC]/50 text-xs hover:text-[#E8E4DC]">@raaamoossss</a>
              </div>
              <div className="p-5 bg-[#1A1714] border border-[#3A3633] rounded-xl">
                <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Nialing Jobarteh</h3>
                <p className="text-[#8B9A7D] text-xs mb-2">Coach</p>
                <a href="https://www.instagram.com/nialing.jxh/" target="_blank" rel="noopener noreferrer" className="text-[#E8E4DC]/50 text-xs hover:text-[#E8E4DC]">@nialing.jxh</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
            Ready to Train?
          </h2>
          <p className="text-[#E8E4DC]/60 text-lg mb-8">
            Training is open to all levels. Join our WhatsApp for session times and updates.
          </p>
          
          {/* Primary CTA - WhatsApp */}
          <a
            href="https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#25D366] text-white font-medium hover:bg-[#20BD5A] transition-colors rounded mb-8"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Join PAU Community
          </a>
          
          {/* Alternative Contact */}
          <div className="pt-6 border-t border-[#3A3633]">
            <p className="text-[#E8E4DC]/40 text-xs uppercase tracking-[0.2em] mb-4">Or contact us directly</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="tel:+41772221425"
                className="flex items-center gap-2 text-[#E8E4DC]/70 hover:text-[#E8E4DC] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-sm">077 222 14 25</span>
              </a>
              <a 
                href="https://www.instagram.com/pautrackrunning/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#E8E4DC]/70 hover:text-[#E8E4DC] transition-colors"
              >
                <Instagram size={16} />
                <span className="text-sm">@pautrackrunning</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// CAMPS PAGE - Camps & Performance
// ============================================
const CampsPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Image Hero Header */}
      <section className="relative">
        <div className="h-[50vh] md:h-[60vh] relative overflow-hidden">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/tojermn1_Screenshot%202026-03-17%20at%2000.57.39%20%281%29.webp"
            alt="Athlete in sprint start position"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/40 to-transparent" />
        </div>
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">June – September</p>
            <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-4">
              Zurich Summer Base
            </h1>
            <p className="text-[#E8E4DC]/80 text-lg md:text-xl max-w-2xl">
              A logistics and infrastructure base for professional athletes training in Switzerland.
            </p>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Who It's For</h2>
          <div className="max-w-3xl">
            <p className="text-[#E8E4DC]/70 text-lg leading-relaxed mb-6">
              Professional athletes who need a base in Europe for the summer season. Whether you come with a coach 
              or need training support — PAU provides the infrastructure so you can focus on performance.
            </p>
            <ul className="space-y-3 text-[#E8E4DC]/60">
              <li className="flex items-start gap-3">
                <span className="text-[#8B9A7D]">→</span>
                <span>Pro athletes preparing for European meets</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#8B9A7D]">→</span>
                <span>Athletes with or without coaching support</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#8B9A7D]">→</span>
                <span>Teams looking for a Swiss training base</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* What PAU Provides */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">What PAU Provides</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Housing</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Accommodation arranged and managed for your stay.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Food</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Meals and nutrition support throughout the camp.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Local Transport</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Getting around Zurich and to training facilities.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Race Transport</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Transport to meets and competition coordination.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Facility Access</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Track, gym, and recovery facility access.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Documentation</h3>
              <p className="text-[#E8E4DC]/60 text-sm">PAU Magazine documents your season with stories and content.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Athlete Image - Focus */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/ljgkpsia_Screenshot%202026-06-28%20at%2014.17.02%20%281%29.webp"
              alt="Athlete with headphones - focus and preparation"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8B9A7D] font-medium">01</span>
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Inquire</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Tell us about your team, dates, and needs.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8B9A7D] font-medium">02</span>
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Plan</h3>
              <p className="text-[#E8E4DC]/60 text-sm">We build a logistics package tailored to your schedule.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-4">
                <span className="text-[#8B9A7D] font-medium">03</span>
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Arrive</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Everything's ready. You focus on performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Athlete Images - Two Column */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/4ch7u85k_Screenshot%202026-03-19%20at%2015.43.30%20%281%29.webp"
                alt="Athlete walking on track at stadium"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/zuv6s7wp_Screenshot%202026-06-28%20at%2014.10.49%20%281%29.webp"
                alt="Arame in PAU gear at stadium"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Season Info */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <h3 className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">Season</h3>
              <p className="text-[#E8E4DC] text-2xl font-headline uppercase">June – September</p>
              <p className="text-[#E8E4DC]/50 text-sm mt-2">European track & field season</p>
            </div>
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <h3 className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">Location</h3>
              <p className="text-[#E8E4DC] text-2xl font-headline uppercase">Zürich, Switzerland</p>
              <p className="text-[#E8E4DC]/50 text-sm mt-2">Central European base</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mood Video */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em]">Mood</h2>
            <RudeCat size="md" className="rotate-12" />
          </div>
          <div className="overflow-hidden rounded-xl">
            <video 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/czma2rnf_Track_Cuts_Senegal_w.i.p-1.mov"
              className="w-full h-auto"
              autoPlay
              muted
              loop
              playsInline
              controls
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
            Apply for Summer Base
          </h2>
          <p className="text-[#E8E4DC]/60 text-lg mb-8">
            Tell us about your team and needs. We'll build a logistics package for your season.
          </p>
          <Link
            to="/apply?interest=camps"
            className="inline-block px-10 py-4 bg-[#8B9A7D] text-[#1A1714] font-medium hover:bg-[#9AAA8D] transition-colors"
          >
            Apply for Zurich Summer Base
          </Link>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// TALENT PAGE - Athlete Representation
// ============================================
const TalentPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Hero Section */}
      <section className="px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Athlete Representation</p>
          <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-6">
            PAU Talent
          </h1>
          <p className="text-[#E8E4DC]/70 text-xl md:text-2xl max-w-3xl leading-relaxed">
            We represent athletes—PAU members and external—for modeling, brand campaigns, content collaborations, and appearances.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Services</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Modeling</h3>
              <p className="text-[#E8E4DC]/60">Athletes for fashion, sport, and lifestyle shoots.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Brand Campaigns</h3>
              <p className="text-[#E8E4DC]/60">Authentic partnerships with aligned brands.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Content Collaborations</h3>
              <p className="text-[#E8E4DC]/60">Video, editorial, and social content creation.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Appearances</h3>
              <p className="text-[#E8E4DC]/60">Events, activations, and speaking opportunities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Brands */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">For Brands</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-10 h-10 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-3">
                <span className="text-[#8B9A7D] text-sm font-medium">01</span>
              </div>
              <p className="text-[#E8E4DC]/70 text-sm">You inquire</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-3">
                <span className="text-[#8B9A7D] text-sm font-medium">02</span>
              </div>
              <p className="text-[#E8E4DC]/70 text-sm">PAU proposes athletes</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-3">
                <span className="text-[#8B9A7D] text-sm font-medium">03</span>
              </div>
              <p className="text-[#E8E4DC]/70 text-sm">Booking & terms confirmed</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-[#8B9A7D]/20 border border-[#8B9A7D] flex items-center justify-center mx-auto mb-3">
                <span className="text-[#8B9A7D] text-sm font-medium">04</span>
              </div>
              <p className="text-[#E8E4DC]/70 text-sm">PAU coordinates delivery</p>
            </div>
          </div>
          <p className="text-[#E8E4DC]/40 text-sm mt-8 text-center">
            Premium, efficient, brand-safe.
          </p>
        </div>
      </section>

      {/* Represented Athletes Roster */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Represented Athletes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Athlete Name', specialty: 'Sprints / Modeling', photo: null },
              { name: 'Athlete Name', specialty: 'Distance / Brand Work', photo: null },
              { name: 'Athlete Name', specialty: '400m / Content', photo: null },
              { name: 'Athlete Name', specialty: 'Hurdles / Appearances', photo: null },
            ].map((athlete, i) => (
              <div key={i} className="group bg-[#252220] border border-[#3A3633] overflow-hidden hover:border-[#8B9A7D]/50 transition-colors">
                {/* Photo placeholder */}
                <div className="aspect-[3/4] bg-[#1A1714] flex items-center justify-center">
                  {athlete.photo ? (
                    <img src={athlete.photo} alt={athlete.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-[#3A3633] text-5xl font-headline">
                      {athlete.name.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-4">
                  <h3 className="font-headline text-base text-[#E8E4DC] uppercase mb-1">{athlete.name}</h3>
                  <p className="text-[#8B9A7D] text-xs">{athlete.specialty}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[#E8E4DC]/40 text-sm mt-8 text-center">
            Full roster available on inquiry. <a href="mailto:active@pauuniverse.com?subject=Talent Roster Inquiry" className="text-[#8B9A7D] hover:text-[#E8E4DC]">Contact us</a>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
            Work With PAU Athletes
          </h2>
          <p className="text-[#E8E4DC]/60 text-lg mb-8">
            Brands: tell us what you need. Athletes: join our roster.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:active@pauuniverse.com?subject=Talent Inquiry (Brand)"
              className="px-10 py-4 bg-[#8B9A7D] text-[#1A1714] font-medium hover:bg-[#9AAA8D] transition-colors"
            >
              Brand Inquiry
            </a>
            <Link
              to="/apply"
              className="px-10 py-4 border border-[#E8E4DC]/30 text-[#E8E4DC] font-medium hover:bg-[#E8E4DC]/10 transition-colors"
            >
              Join as Athlete
            </Link>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// ATHLETES PAGE - Full Athlete Ecosystem
// ============================================
const AthletesPage = () => {
  // Athlete card component
  const AthleteCard = ({ name, discipline, country, pbs, bio, photo }) => (
    <div className="bg-[#252220] border border-[#3A3633] overflow-hidden">
      <div className="aspect-[4/5] bg-[#1A1714] flex items-center justify-center">
        {photo ? (
          <img src={photo} alt={name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-[#3A3633] text-5xl font-headline">
            {name.split(' ').map(n => n[0]).join('')}
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-1">{name}</h3>
        <p className="text-[#8B9A7D] text-sm mb-1">{discipline}</p>
        {country && <p className="text-[#E8E4DC]/40 text-xs mb-2">{country}</p>}
        {pbs && <p className="text-[#E8E4DC]/50 text-xs mb-2">{pbs}</p>}
        {bio && <p className="text-[#E8E4DC]/40 text-xs leading-relaxed">{bio}</p>}
      </div>
    </div>
  );

  const clubAthletes = [
    { name: 'Athlete Name', discipline: 'Sprints', country: 'Switzerland', pbs: '', bio: 'Bio placeholder' },
    { name: 'Athlete Name', discipline: '400m', country: 'Switzerland', pbs: '', bio: 'Bio placeholder' },
    { name: 'Athlete Name', discipline: 'Distance', country: 'Switzerland', pbs: '', bio: 'Bio placeholder' },
  ];

  const visitingAthletes = [
    { name: 'Athlete Name', discipline: 'Sprints', country: 'Jamaica', pbs: '', bio: 'Summer base athlete' },
    { name: 'Athlete Name', discipline: '100m / 200m', country: 'USA', pbs: '', bio: 'Summer base athlete' },
    { name: 'Athlete Name', discipline: '400m', country: 'Senegal', pbs: '', bio: 'Summer base athlete' },
  ];

  const representedAthletes = [
    { name: 'Athlete Name', discipline: 'Sprints / Modeling', country: '', pbs: '', bio: 'Available for brand work' },
    { name: 'Athlete Name', discipline: '400m / Content', country: '', pbs: '', bio: 'Available for campaigns' },
  ];

  return (
    <>
      <Navigation />
      <main className="pt-24 bg-[#1A1714] min-h-screen">
        {/* Hero Section */}
        <section className="px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">The Ecosystem</p>
            <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-6">
              Athletes
            </h1>
            <p className="text-[#E8E4DC]/70 text-xl md:text-2xl max-w-3xl leading-relaxed">
              Club athletes, visiting professionals, and represented talent. PAU builds an ecosystem 
              where serious athletes train, compete, and grow together.
            </p>
          </div>
        </section>

        {/* Club Athletes (Zurich-based) */}
        <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-2">Club Athletes</h2>
            <p className="text-[#E8E4DC]/50 text-sm mb-8">Zurich-based members who train year-round with PAU.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {clubAthletes.map((athlete, i) => (
                <AthleteCard key={i} {...athlete} />
              ))}
            </div>
          </div>
        </section>

        {/* Visiting Summer Athletes */}
        <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-2">Visiting Summer Athletes</h2>
            <p className="text-[#E8E4DC]/50 text-sm mb-8">Professional athletes who use PAU as their Zurich summer base (June–September).</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visitingAthletes.map((athlete, i) => (
                <AthleteCard key={i} {...athlete} />
              ))}
            </div>
          </div>
        </section>

        {/* Represented for Talent */}
        <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-2">Represented for Talent</h2>
            <p className="text-[#E8E4DC]/50 text-sm mb-8">Athletes available for brand work, campaigns, and appearances through PAU Talent.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {representedAthletes.map((athlete, i) => (
                <AthleteCard key={i} {...athlete} />
              ))}
            </div>
          </div>
        </section>

        {/* CTAs */}
        <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Join PAU</h3>
                <p className="text-[#E8E4DC]/50 text-sm mb-4">Become a club member and train with us year-round.</p>
                <Link to="/apply?interest=membership" className="text-[#8B9A7D] hover:text-[#E8E4DC] text-sm">
                  Apply for Membership →
                </Link>
              </div>
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Summer Base</h3>
                <p className="text-[#E8E4DC]/50 text-sm mb-4">Apply as a visiting pro for the European season.</p>
                <Link to="/apply?interest=camps" className="text-[#8B9A7D] hover:text-[#E8E4DC] text-sm">
                  Apply for Zurich Summer Base →
                </Link>
              </div>
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Talent Inquiry</h3>
                <p className="text-[#E8E4DC]/50 text-sm mb-4">Book an athlete for brand or content work.</p>
                <Link to="/apply?interest=talent" className="text-[#8B9A7D] hover:text-[#E8E4DC] text-sm">
                  Inquire for Talent →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// COACHES PAGE - Who Leads Training
// ============================================
const CoachesPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Video Hero Header */}
      <section className="relative">
        <div className="h-[40vh] md:h-[50vh] relative overflow-hidden">
          <video 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/rewzrwdt_IMG_0655%202.MOV"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/30 to-transparent" />
        </div>
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-4">
              Coaches
            </h1>
            <p className="text-[#E8E4DC]/80 text-lg md:text-xl max-w-2xl">
              Performance craft, consistency, and athlete-first standards.
            </p>
          </div>
        </div>
      </section>

      {/* Coach Profiles */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Noé Ramos */}
            <div className="bg-[#252220] border border-[#3A3633] overflow-hidden rounded-2xl">
              <div className="aspect-[4/3] bg-[#1A1714] flex items-center justify-center">
                <span className="text-[#3A3633] text-6xl font-headline">NR</span>
              </div>
              <div className="p-8">
                <h2 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-1">Noé Ramos</h2>
                <p className="text-[#8B9A7D] text-sm mb-4">Coach</p>
                <p className="text-[#E8E4DC]/40 text-sm mb-6">
                  Focus: Sprints & speed development (100–800m), speed coaching for team sports
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/apply?interest=coaching&coach=noe"
                    className="inline-flex items-center justify-center gap-2 bg-[#8B9A7D] text-[#1A1714] px-6 py-3 font-medium hover:bg-[#9AAA8D] transition-colors"
                  >
                    Work with Noé
                  </Link>
                  <a
                    href="https://www.instagram.com/raaamoossss/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-[#E8E4DC]/30 text-[#E8E4DC] px-6 py-3 font-medium hover:bg-[#E8E4DC]/10 transition-colors"
                  >
                    <Instagram size={18} /> @raaamoossss
                  </a>
                </div>
              </div>
            </div>

            {/* Nialing Jobarteh */}
            <div className="bg-[#252220] border border-[#3A3633] overflow-hidden rounded-2xl">
              <div className="aspect-[4/3] bg-[#1A1714] overflow-hidden">
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/rtfy59um_Screenshot%202026-09-07%20at%2011.32.00%E2%80%AFAM.png"
                  alt="Nialing Jobarteh"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h2 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-1">Nialing Jobarteh</h2>
                <p className="text-[#8B9A7D] text-sm mb-4">Coach</p>
                <p className="text-[#E8E4DC]/40 text-sm mb-6">
                  Focus: Sprints & speed development (100–800m), speed coaching for team sports
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/apply?interest=coaching&coach=nialing"
                    className="inline-flex items-center justify-center gap-2 bg-[#8B9A7D] text-[#1A1714] px-6 py-3 font-medium hover:bg-[#9AAA8D] transition-colors"
                  >
                    Work with Nialing
                  </Link>
                  <a
                    href="https://www.instagram.com/nialing.jxh/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-[#E8E4DC]/30 text-[#E8E4DC] px-6 py-3 font-medium hover:bg-[#E8E4DC]/10 transition-colors"
                  >
                    <Instagram size={18} /> @nialing.jxh
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Footage */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-video bg-[#1A1714] border border-[#3A3633] rounded-2xl overflow-hidden">
            <iframe
              src="https://www.youtube.com/embed/XA8bQRzhseY?rel=0&modestbranding=1"
              title="PAU Training"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Coach at Track Image */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-[16/9] overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/9mwb791i_IMG_0651.webp"
              alt="Coach at the track with umbrella"
              className="w-full h-full object-cover object-bottom"
            />
          </div>
        </div>
      </section>

      {/* Coaches Images - Two column */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/0wu0t1dn_26-08-04_PAU_260804-Fronwald_NR_6682_Highres%203.webp"
                alt="Coach talking to athletes"
                className="w-full h-auto"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/c3esch8g_IMG_0305%202.webp"
                alt="Starting blocks"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Book Personal Training */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633] bg-[#252220]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <RudeCat size="xl" className="mx-auto mb-6 -rotate-3" />
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">1-on-1 Coaching</p>
            <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
              Book Personal Training
            </h2>
            <p className="text-[#E8E4DC]/60 text-lg max-w-2xl mx-auto">
              Work directly with our coaches. Tailored programming, technique analysis, and race preparation. 
              Also available: speed training for team sports.
            </p>
            <p className="text-[#E8E4DC]/50 text-sm mt-4 max-w-xl mx-auto">
              Indoor & outdoor sessions available at various gyms, tracks & grassfields across Zurich.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <div className="px-4 py-2 bg-[#1A1714] border border-[#3A3633] rounded-lg">
                <p className="text-[#E8E4DC] text-sm font-medium">CHF 100.–</p>
                <p className="text-[#E8E4DC]/50 text-xs">Single session (90 min)</p>
              </div>
              <div className="px-4 py-2 bg-[#1A1714] border border-[#8B9A7D] rounded-lg">
                <p className="text-[#E8E4DC] text-sm font-medium">CHF 900.–</p>
                <p className="text-[#E8E4DC]/50 text-xs">10-pack</p>
              </div>
            </div>
          </div>
          
          {/* Booking Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <a
              href="https://wa.me/41772221425?text=Hi%2C%20I'm%20interested%20in%20booking%20personal%20training"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-[#1A1714] border border-[#3A3633] rounded-2xl text-center hover:border-[#25D366] transition-colors group"
            >
              <div className="w-12 h-12 bg-[#25D366]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#25D366]/30 transition-colors">
                <svg className="w-6 h-6 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">WhatsApp</h3>
              <p className="text-[#E8E4DC]/50 text-sm">077 222 14 25</p>
            </a>
            
            <a
              href="https://www.instagram.com/pautrackrunning/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-[#1A1714] border border-[#3A3633] rounded-2xl text-center hover:border-[#E1306C] transition-colors group"
            >
              <div className="w-12 h-12 bg-[#E1306C]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#E1306C]/30 transition-colors">
                <Instagram size={24} className="text-[#E1306C]" />
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Instagram DM</h3>
              <p className="text-[#E8E4DC]/50 text-sm">@pautrackrunning</p>
            </a>
            
            <Link
              to="/apply?interest=coaching"
              className="p-6 bg-[#1A1714] border border-[#3A3633] rounded-2xl text-center hover:border-[#8B9A7D] transition-colors group"
            >
              <div className="w-12 h-12 bg-[#8B9A7D]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#8B9A7D]/30 transition-colors">
                <svg className="w-6 h-6 text-[#8B9A7D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">Apply Form</h3>
              <p className="text-[#E8E4DC]/50 text-sm">Tell us your goals</p>
            </Link>
          </div>
          
          <p className="text-center text-[#E8E4DC]/40 text-sm">
            Sessions available in Zürich · Remote programming also available
          </p>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// COACHING PAGE - Personal & Group Coaching (Services)
// ============================================
const CoachingPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Video Hero Header */}
      <section className="relative">
        <div className="h-[50vh] md:h-[60vh] relative overflow-hidden">
          <video 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/maaayrpd_IMG_0690%202.MOV"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/30 to-transparent" />
        </div>
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Services</p>
            <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-4">
              Coaching
            </h1>
            <p className="text-[#E8E4DC]/80 text-lg md:text-xl max-w-2xl">
              Personal and group coaching for athletes who want more structure, closer feedback, or a tailored plan.
            </p>
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Coaches</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-[#252220] border border-[#3A3633] rounded-2xl">
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-2">Noé Ramos</h3>
              <p className="text-[#8B9A7D] text-sm mb-4">Coach</p>
              <p className="text-[#E8E4DC]/60 mb-3">Performance-focused coaching with emphasis on technical development and competition preparation.</p>
              <p className="text-[#E8E4DC]/40 text-sm mb-4">Focus: Sprints & speed development (100–800m), team sports</p>
              <a href="https://www.instagram.com/raaamoossss/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#E8E4DC]/50 hover:text-[#E8E4DC] text-sm">
                <Instagram size={16} /> @raaamoossss
              </a>
            </div>
            <div className="p-8 bg-[#252220] border border-[#3A3633] rounded-2xl">
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-2">Nialing Jobarteh</h3>
              <p className="text-[#8B9A7D] text-sm mb-4">Coach</p>
              <p className="text-[#E8E4DC]/60 mb-3">Holistic approach to training, balancing performance goals with athlete wellbeing.</p>
              <p className="text-[#E8E4DC]/40 text-sm mb-4">Focus: Sprints & speed development (100–800m), team sports</p>
              <a href="https://www.instagram.com/nialing.jxh/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#E8E4DC]/50 hover:text-[#E8E4DC] text-sm">
                <Instagram size={16} /> @nialing.jxh
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Formats */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-8">Formats</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Personal Coaching</h3>
              <p className="text-[#E8E4DC]/60 mb-4">Individual programming with direct feedback and tailored plans.</p>
              <p className="text-[#E8E4DC]/40 text-sm">Contact for pricing</p>
            </div>
            <div className="p-8 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Group Coaching</h3>
              <p className="text-[#E8E4DC]/60 mb-4">Structured sessions with coaching oversight in a team setting.</p>
              <p className="text-[#E8E4DC]/40 text-sm">Included in membership tiers</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto text-center">
          <RudeCat size="lg" className="mx-auto mb-6 rotate-6" />
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
            Get Coached
          </h2>
          <p className="text-[#E8E4DC]/60 text-lg mb-8">
            Apply or reach out to discuss coaching options.
          </p>
          <Link
            to="/apply"
            className="inline-block px-10 py-4 bg-[#8B9A7D] text-[#1A1714] font-medium hover:bg-[#9AAA8D] transition-colors"
          >
            Inquire About Coaching
          </Link>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// COMMUNITY PAGE - Saturday Free Training
// ============================================
const CommunityPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      {/* Video Hero Header */}
      <section className="relative">
        <div className="h-[50vh] md:h-[60vh] relative overflow-hidden">
          <video 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/cade7cea_C0039_00088730.mp4"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/30 to-transparent" />
        </div>
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Every Saturday</p>
            <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-4">
              Free Community Training
            </h1>
            <p className="text-[#E8E4DC]/80 text-lg md:text-xl max-w-2xl">
              Open to everyone. Performance-minded, welcoming, no barriers.
            </p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">The Session</h2>
              <div className="space-y-6 text-[#E8E4DC]/70">
                <p>
                  Every Saturday, PAU opens its doors for a free community training session. No membership required, 
                  no experience necessary. Just show up ready to work.
                </p>
                <p>
                  The session is coached, structured, and performance-minded — but welcoming to all levels. 
                  Whether you're returning to sport, trying track for the first time, or just want to train 
                  in a serious environment, Saturday is for you.
                </p>
                <p>
                  This is how many PAU athletes started. It might be how you start too.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">When</p>
                <p className="text-[#E8E4DC] text-xl">Every Saturday</p>
                <p className="text-[#E8E4DC]/50 text-sm">Morning session (time shared upon registration)</p>
              </div>
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Where</p>
                <p className="text-[#E8E4DC] text-xl">Sportanlage Fronwald</p>
                <p className="text-[#E8E4DC]/50 text-sm">Zürich, Switzerland</p>
              </div>
              <div className="p-6 bg-[#252220] border border-[#3A3633]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Cost</p>
                <p className="text-[#E8E4DC] text-xl">Free</p>
                <p className="text-[#E8E4DC]/50 text-sm">No membership required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Image - Athletes on track sunset */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/car09mwp_7b1fc072-2b03-407a-a34d-805dfecbaafc%20%281%29.webp"
              alt="Athletes on track at sunset"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="px-6 md:px-10 py-16 border-t border-[#3A3633]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em]">What to Expect</h2>
            <RudeCat size="xs" className="rotate-6" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Coached Session</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Structured workout with coaching guidance and feedback.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">All Levels</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Beginners to experienced runners. Everyone trains together.</p>
            </div>
            <div className="p-6 bg-[#252220] border border-[#3A3633]">
              <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-3">Serious Environment</h3>
              <p className="text-[#E8E4DC]/60 text-sm">Performance-minded culture, but welcoming and supportive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Images - Two column */}
      <section className="px-6 md:px-10 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/9kd0kqa4_5547dbe7-3cda-46b5-9f2a-52c886dd0fc6.webp"
                alt="Athletes in PAU shirts at track"
                className="w-full h-auto"
              />
            </div>
            <div className="overflow-hidden rounded-xl">
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/8v61ysye_95FD29BD-418F-4C67-A1D5-B404D102EE1D.webp"
                alt="Night training session"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Contact Options */}
      <section className="px-6 md:px-10 py-20 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
            Join Us Saturday
          </h2>
          <p className="text-[#E8E4DC]/60 text-lg mb-10">
            Join the WhatsApp group to get session times, weekly polls, and connect with the community.
          </p>
          
          {/* WhatsApp - Primary CTA */}
          <div className="bg-[#252220] border border-[#8B9A7D] p-8 mb-8 max-w-lg mx-auto">
            {/* QR Code - visible on desktop */}
            <div className="hidden md:block mb-6">
              <img 
                src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5&bgcolor=252220&color=E8E4DC"
                alt="Scan to join PAU Community WhatsApp"
                className="mx-auto"
              />
              <p className="text-[#E8E4DC]/40 text-xs mt-3">Scan with your phone camera</p>
            </div>
            
            {/* WhatsApp Button */}
            <a
              href="https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 w-full px-8 py-4 bg-[#25D366] text-white font-medium hover:bg-[#20BD5A] transition-colors rounded text-lg"
              data-testid="whatsapp-join-btn"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Join PAU Community
            </a>
            <p className="text-[#E8E4DC]/50 text-sm mt-4">Training polls every Sunday</p>
          </div>
          
          {/* Alternative Contact Options */}
          <div className="pt-8 border-t border-[#3A3633]">
            <p className="text-[#E8E4DC]/40 text-xs uppercase tracking-[0.2em] mb-6">Or reach out directly</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              {/* Phone */}
              <a 
                href="tel:+41772221425"
                className="flex items-center gap-3 text-[#E8E4DC]/70 hover:text-[#E8E4DC] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>077 222 14 25</span>
              </a>
              
              {/* Instagram */}
              <a 
                href="https://www.instagram.com/pautrackrunning/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[#E8E4DC]/70 hover:text-[#E8E4DC] transition-colors"
              >
                <Instagram size={20} />
                <span>@pautrackrunning</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// ============================================
// APPLY PAGE - Contact Form
// ============================================
const ApplyPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: 'trial',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get interest from URL params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const interest = params.get('interest');
    if (interest) {
      setFormData(prev => ({ ...prev, interest }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          inquiry: `Interest: ${formData.interest}\n\n${formData.message}`
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Navigation />
        <main className="pt-24 bg-[#1A1714] min-h-screen flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <RudeCat size="xxl" className="mx-auto mb-6" />
            <h1 className="font-headline text-3xl text-[#E8E4DC] uppercase mb-4">Application Received</h1>
            <p className="text-[#E8E4DC]/60 mb-8">Thanks for reaching out. We'll be in touch soon.</p>
            
            {/* WhatsApp Community Invite */}
            <div className="bg-[#252220] border border-[#3A3633] p-6 mb-8 rounded">
              <p className="text-[#E8E4DC]/70 text-sm mb-4">
                Join our WhatsApp community for training updates, weekly polls, and event announcements.
              </p>
              
              {/* QR Code - desktop only */}
              <div className="hidden md:block mb-4">
                <img 
                  src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5&bgcolor=252220&color=E8E4DC"
                  alt="Scan to join PAU WhatsApp"
                  className="mx-auto rounded"
                />
              </div>
              
              <a
                href="https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-[#25D366] text-white font-medium hover:bg-[#20BD5A] transition-colors rounded"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Join PAU Community
              </a>
            </div>
            
            <Link to="/" className="text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors">
              ← Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <main className="pt-24 bg-[#1A1714] min-h-screen">
        <section className="px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-2xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Get Started</p>
            <h1 className="font-headline text-4xl md:text-5xl text-[#E8E4DC] uppercase mb-6">
              Book a Trial / Apply
            </h1>
            <p className="text-[#E8E4DC]/60 text-lg mb-12">
              One form for all inquiries—trial sessions, camp applications, talent representation, or coaching.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[#E8E4DC]/70 text-sm mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full p-4 bg-[#252220] border border-[#3A3633] text-[#E8E4DC] focus:border-[#8B9A7D] outline-none"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-[#E8E4DC]/70 text-sm mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full p-4 bg-[#252220] border border-[#3A3633] text-[#E8E4DC] focus:border-[#8B9A7D] outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-[#E8E4DC]/70 text-sm mb-2">I'm interested in *</label>
                <select
                  value={formData.interest}
                  onChange={(e) => setFormData({...formData, interest: e.target.value})}
                  className="w-full p-4 bg-[#252220] border border-[#3A3633] text-[#E8E4DC] focus:border-[#8B9A7D] outline-none"
                >
                  <option value="trial">Book a Trial Session</option>
                  <option value="saturday">Saturday Community Training</option>
                  <option value="membership">Membership / Pricing</option>
                  <option value="camps">Pro Camp / Zurich Summer Base</option>
                  <option value="coaching">Coaching</option>
                  <option value="talent">Talent Booking</option>
                  <option value="partnership">Partnerships / Sponsors</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[#E8E4DC]/70 text-sm mb-2">Tell us more</label>
                <textarea
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full p-4 bg-[#252220] border border-[#3A3633] text-[#E8E4DC] focus:border-[#8B9A7D] outline-none resize-none"
                  placeholder="Your background, goals, or any questions..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#8B9A7D] text-[#1A1714] font-medium hover:bg-[#9AAA8D] transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Submit Application'}
              </button>
            </form>

            <p className="text-[#E8E4DC]/40 text-sm mt-8 text-center">
              We typically respond within 48 hours.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// HOME PAGE - Landing with Teasers
// ============================================
const Home = () => {
  return (
  <>
    <Navigation />
    
    {/* Hero with Video Background */}
    <section className="min-h-screen flex items-end bg-[#1A1714] relative overflow-hidden" data-testid="hero-section">
      {/* Vimeo Video Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://i.vimeocdn.com/video/2119135350-ca54e7e885388c418b5ea568511052fea0668c39a1d6ca732b69b06ccba03d06-d_1920x1080')` }}
        />
        <iframe
          src="https://player.vimeo.com/video/1163082086?background=1&autoplay=1&loop=1&muted=1&quality=1080p&dnt=1"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
          frameBorder="0"
          allow="autoplay; fullscreen"
          style={{ pointerEvents: 'none' }}
          title="PAU Background Video"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/40 to-transparent" />
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 px-6 md:px-10 pb-16 md:pb-24 max-w-[1400px] w-full">
        <p className="text-[#8B9A7D] text-xs tracking-[0.3em] uppercase mb-4">
          Zürich-Based Modern Track Running Club
        </p>
        <h1 className="mb-6">
          <img 
            src={PAU_LOGOS.white} 
            alt="PAU" 
            className="h-20 sm:h-24 md:h-32 lg:h-40"
          />
        </h1>
        <p className="text-[#E8E4DC]/65 text-lg md:text-xl max-w-xl mb-8">
          Performance culture, disciplined training cycles, and a strong community. 
          From ambitious beginners to competitive performers.
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4">
          <Link 
            to="/apply" 
            className="inline-flex items-center gap-2 bg-[#8B9A7D] text-[#1A1714] px-8 py-4 font-medium hover:bg-[#9AAA8D] transition-colors"
            data-testid="hero-cta"
          >
            Book a Trial <ArrowRight size={18} />
          </Link>
          <Link 
            to="/training" 
            className="inline-flex items-center gap-2 border border-white/30 text-[#E8E4DC] px-8 py-4 font-medium hover:bg-white/10 transition-colors"
            data-testid="hero-explore-btn"
          >
            Explore Training
          </Link>
        </div>
      </div>
    </section>

    {/* Saturday Community Training Banner - High Visibility */}
    <section className="py-12 px-6 bg-[#8B9A7D]" data-testid="saturday-community-banner">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="text-center md:text-left">
          <p className="text-[#1A1714]/70 text-xs uppercase tracking-[0.3em] mb-2">Every Saturday · 10:00</p>
          <h2 className="font-headline text-2xl md:text-3xl lg:text-4xl text-[#1A1714] uppercase">
            Free Community Training
          </h2>
          <p className="text-[#1A1714]/70 mt-2 text-sm md:text-base">
            Open to everyone. No membership required. Sportanlage Fronwald, Zürich.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Link 
            to="/community"
            className="inline-flex items-center gap-2 bg-[#1A1714] text-[#E8E4DC] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors whitespace-nowrap"
            data-testid="saturday-banner-cta"
          >
            Learn More <ArrowRight size={18} />
          </Link>
          <a
            href="https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 font-medium hover:bg-[#20BD5A] transition-colors whitespace-nowrap rounded"
            data-testid="saturday-whatsapp-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Join WhatsApp
          </a>
        </div>
      </div>
    </section>

    {/* Two Pillars Grid */}
    <section className="py-24 px-6 bg-[#1A1714]" data-testid="teasers-section">
      <div className="max-w-5xl mx-auto">
        {/* Rude Cat - floating decoration */}
        <div className="flex justify-end mb-6">
          <RudeCat size="lg" className="rotate-12" />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* TRAINING Teaser */}
          <Link 
            to="/training" 
            className="group relative bg-[#252220] border border-[#3A3633] p-8 md:p-10 hover:border-[#8B9A7D]/50 transition-all duration-300 overflow-hidden rounded-2xl"
            data-testid="teaser-training"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#8B9A7D]/10 blur-3xl group-hover:bg-[#8B9A7D]/20 transition-colors" />
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Club</p>
            <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] mb-4 uppercase">Training</h2>
            <p className="text-[#E8E4DC]/55 mb-6">
              Structured cycles, coaching feedback, and a team that raises standards.
            </p>
            <span className="inline-flex items-center gap-2 text-[#E8E4DC]/65 group-hover:text-[#E8E4DC] transition-colors">
              View Schedule <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>

          {/* CAMPS Teaser */}
          <Link 
            to="/camps" 
            className="group relative bg-[#252220] border border-[#3A3633] p-8 md:p-10 hover:border-[#8B9A7D]/50 transition-all duration-300 overflow-hidden rounded-2xl"
            data-testid="teaser-camps"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#8B9A7D]/10 blur-3xl group-hover:bg-[#8B9A7D]/20 transition-colors" />
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Performance</p>
            <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] mb-4 uppercase">Camps</h2>
            <p className="text-[#E8E4DC]/55 mb-6">
              Immersive training blocks with competition exposure.
            </p>
            <span className="inline-flex items-center gap-2 text-[#E8E4DC]/65 group-hover:text-[#E8E4DC] transition-colors">
              Learn More <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>

          {/* TALENT Teaser - temporarily disabled, in backlog */}
          {/*
          <Link 
            to="/talent" 
            className="group relative bg-[#252220] border border-[#3A3633] p-8 md:p-10 hover:border-[#8B9A7D]/50 transition-all duration-300 overflow-hidden rounded-2xl"
            data-testid="teaser-talent"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#8B9A7D]/10 blur-3xl group-hover:bg-[#8B9A7D]/20 transition-colors" />
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Representation</p>
            <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] mb-4 uppercase">Talent</h2>
            <p className="text-[#E8E4DC]/55 mb-6">
              Athlete representation for brands and campaigns.
            </p>
            <span className="inline-flex items-center gap-2 text-[#E8E4DC]/65 group-hover:text-[#E8E4DC] transition-colors">
              Work With Us <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
          */}

        </div>
      </div>
    </section>

    {/* Contact Strip */}
    <section className="py-16 px-6 bg-[#1A1714] border-t border-[#3A3633]" data-testid="contact-strip">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-[0.3em] mb-4">Get in touch</p>
        
        {/* WhatsApp - Primary CTA */}
        <a 
          href="https://wa.me/41772221425"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 text-2xl md:text-3xl text-[#E8E4DC] hover:text-[#25D366] transition-colors group"
        >
          <svg className="w-8 h-8 md:w-10 md:h-10 text-[#25D366] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Text us on WhatsApp
        </a>
        
        <p className="text-[#E8E4DC]/50 mt-4 mb-6">Zürich, Switzerland</p>
        
        {/* Secondary Links - Email & Instagram */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
          <a 
            href="mailto:active@pauuniverse.com" 
            className="flex items-center gap-2 text-[#E8E4DC]/40 hover:text-[#E8E4DC]/70 transition-colors text-sm"
          >
            active@pauuniverse.com
          </a>
          <a 
            href="https://www.instagram.com/pautrackrunning/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#E8E4DC]/40 hover:text-[#E8E4DC]/70 transition-colors text-sm"
          >
            <Instagram size={16} />
            @pautrackrunning
          </a>
        </div>
      </div>
    </section>

    {/* Second Saturday Reminder CTA */}
    <section className="py-16 px-6 bg-[#252220] border-t border-[#3A3633]" data-testid="saturday-reminder-bottom">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Open to Everyone</p>
        <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
          Saturday Community Training
        </h2>
        <p className="text-[#E8E4DC]/60 mb-8 max-w-xl mx-auto">
          Every Saturday at 10:00. Performance-minded, welcoming, no barriers. 
          Join our WhatsApp to get weekly training polls and updates.
        </p>
        
        {/* QR Code - visible on desktop */}
        <div className="hidden md:block mb-6">
          <img 
            src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5&bgcolor=252220&color=E8E4DC"
            alt="Scan to join PAU WhatsApp"
            className="mx-auto rounded"
          />
          <p className="text-[#E8E4DC]/40 text-xs mt-2">Scan to join on mobile</p>
        </div>
        
        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://chat.whatsapp.com/HcPaymAhKgz89DZiHUzZs5"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 font-medium hover:bg-[#20BD5A] transition-colors rounded"
            data-testid="saturday-reminder-whatsapp"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Join PAU Community
          </a>
          <Link 
            to="/community"
            className="inline-flex items-center gap-2 border border-[#E8E4DC]/30 text-[#E8E4DC] px-8 py-4 font-medium hover:bg-[#E8E4DC]/10 transition-colors"
            data-testid="saturday-reminder-cta"
          >
            Learn More <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </>
  );
};

// ============================================
// TRACKRUNNING PAGE
// ============================================
const TrackRunningPage = () => {
  const [activeLang, setActiveLang] = useState('EN');
  
  const content = {
    EN: {
      tagline: "THIS DIMENSION IS A PLAYGROUND.",
      intro: "Track & sprint training in Zürich. All levels.",
      description: "No fixed coach on deck — we train from the programme and athletes coach each other.",
      schedule: "Mon / Thu / Sat",
      location: "Sportanlage Fronwald",
      locationFull: "Sportanlage Fronwald, Zürich",
      equipment: "Hurdles + starting blocks available",
      membership: "Free",
      howItWorks: "How it works",
      programNote: "Programme shared in group chat. Attendance via polls.",
      philosophyLabel: "Training philosophy",
      philosophyLink: "swepttrack.com/coaches",
      joinCta: "Join PAU",
      memberPerks: "Member perks",
      perk1: "PAU training gear",
      perk2: "PAU partner offers",
      partnerOffer: "Partner offer",
      strengthTitle: "Optional strength training",
      strengthProvider: "IbriAthletics",
      strengthDesc: "For athletes who want a structured strength component alongside track training.",
      hybridPackage: "Hybrid Package",
      hybridPrice: "CHF 129 / month",
      hybridFeature1: "1 coached strength session per week",
      hybridFeature2: "Open Gym access",
      hybridFeature3: "Training planning + nutrition guidelines",
      hybridNote: "Separate service via IbriAthletics",
    },
    DE: {
      tagline: "THIS DIMENSION IS A PLAYGROUND.",
      intro: "Track- & Sprinttraining in Zürich. Für alle Levels.",
      description: "Kein fester Coach vor Ort — wir trainieren nach dem Programm und Athlet:innen coachen sich gegenseitig.",
      schedule: "Mo / Do / Sa",
      location: "Sportanlage Fronwald",
      locationFull: "Sportanlage Fronwald, Zürich",
      equipment: "Hürden + Startblöcke verfügbar",
      membership: "Kostenlos",
      howItWorks: "So funktioniert's",
      programNote: "Programm im Gruppenchat. Teilnahme via Umfragen.",
      philosophyLabel: "Trainingsphilosophie",
      philosophyLink: "swepttrack.com/coaches",
      joinCta: "Join PAU",
      memberPerks: "Member-Vorteile",
      perk1: "Zugang zu PAU Training Gear",
      perk2: "PAU Partner-Angebote",
      partnerOffer: "Partner-Angebot",
      strengthTitle: "Optionales Krafttraining",
      strengthProvider: "IbriAthletics",
      strengthDesc: "Für Athlet:innen, die eine strukturierte Kraftkomponente zum Bahntraining wünschen.",
      hybridPackage: "Hybrid Package",
      hybridPrice: "CHF 129 / Monat",
      hybridFeature1: "1 gecoachte Krafteinheit pro Woche",
      hybridFeature2: "Open Gym Zugang",
      hybridFeature3: "Trainingsplanung + Ernährungsrichtlinien",
      hybridNote: "Separater Service via IbriAthletics",
    },
  };

  const t = content[activeLang];

  return (
  <>
    <Navigation />
    <main className="pt-0 bg-[#1A1714] min-h-screen">
      {/* Video Hero Header */}
      <section className="relative h-[60vh] min-h-[450px] flex items-end overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster="https://img.youtube.com/vi/XA8bQRzhseY/maxresdefault.jpg"
        >
          <source src="https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/04r2gh3h_Track_Cuts_Senegal_w.i.p-1%20%281%29.mov" type="video/quicktime" />
          <source src="https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/04r2gh3h_Track_Cuts_Senegal_w.i.p-1%20%281%29.mov" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-black/40 to-transparent" />
        <div className="relative z-10 w-full px-6 md:px-10 pb-10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl text-[#E8E4DC] uppercase mb-2">
                PAU Track Running
              </h1>
              <p className="text-[#8B9A7D] text-sm uppercase tracking-[0.2em]">Zürich</p>
            </div>
            {/* Language Switcher in Hero */}
            <div className="flex gap-1">
              {['EN', 'DE'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setActiveLang(lang)}
                  className={`w-10 h-10 text-sm font-medium transition-all ${
                    activeLang === lang 
                      ? 'bg-[#E8E4DC] text-[#1A1714]' 
                      : 'bg-[#252220] text-[#E8E4DC]/60 hover:text-[#E8E4DC] hover:bg-[#3A3633]'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Two-Column Layout */}
      <section className="py-12 md:py-16 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr,320px] gap-12 lg:gap-16">
            
            {/* Main Content Column */}
            <div className="order-2 lg:order-1">
              {/* Tagline & Intro */}
              <div className="mb-12">
                <p className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase tracking-wide mb-6">
                  {t.tagline}
                </p>
                <p className="text-[#E8E4DC] text-xl md:text-2xl mb-4">
                  {t.intro}
                </p>
                <p className="text-[#E8E4DC]/65 text-lg leading-relaxed">
                  {t.description}
                </p>
              </div>

              {/* How It Works */}
              <div className="mb-12">
                <h2 className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-4">{t.howItWorks}</h2>
                <p className="text-[#E8E4DC]/70 text-lg mb-4">
                  {t.programNote}
                </p>
                <p className="text-[#E8E4DC]/70">
                  {t.philosophyLabel}:{' '}
                  <a 
                    href="https://swepttrack.com/coaches" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors"
                  >
                    {t.philosophyLink} →
                  </a>
                </p>
              </div>

              {/* Member Perks */}
              <div className="mb-12 p-6 bg-[#8B9A7D]/10 border-l-2 border-[#8B9A7D]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-4">{t.memberPerks}</p>
                <ul className="space-y-3 text-[#E8E4DC]/80">
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[#8B9A7D] rounded-full"></span>
                    <span>{t.perk1}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[#8B9A7D] rounded-full"></span>
                    <span>{t.perk2}</span>
                  </li>
                </ul>
              </div>

              {/* Divider */}
              <div className="border-t border-[#3A3633] my-12"></div>

              {/* IbriAthletics Partner Section */}
              <div>
                <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-6">{t.partnerOffer}</p>
                <div className="p-8 bg-[#252220] border border-[#3A3633] rounded-lg">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                    <div>
                      <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-1">
                        {t.strengthTitle}
                      </h3>
                      <p className="text-[#8B9A7D]">{t.strengthProvider}</p>
                    </div>
                    <a
                      href="https://www.ibriathletics.com/en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
                    >
                      ibriathletics.com <ArrowUpRight size={14} />
                    </a>
                  </div>
                  
                  <p className="text-[#E8E4DC]/65 mb-6">
                    {t.strengthDesc}
                  </p>
                  
                  <div className="p-5 bg-[#1A1714] rounded">
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="text-[#E8E4DC] font-medium">{t.hybridPackage}</span>
                      <span className="text-[#8B9A7D] font-medium">{t.hybridPrice}</span>
                    </div>
                    <ul className="space-y-2 text-[#E8E4DC]/60 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="text-[#8B9A7D]">+</span> {t.hybridFeature1}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-[#8B9A7D]">+</span> {t.hybridFeature2}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-[#8B9A7D]">+</span> {t.hybridFeature3}
                      </li>
                    </ul>
                  </div>
                  
                  <p className="text-[#E8E4DC]/40 text-xs mt-4">
                    {t.hybridNote}. Contact <a href="mailto:active@pauuniverse.com" className="text-[#8B9A7D] hover:text-[#E8E4DC]">active@pauuniverse.com</a> for intro.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar - Key Info */}
            <div className="order-1 lg:order-2">
              <div className="lg:sticky lg:top-28 space-y-6">
                {/* Schedule Card */}
                <div className="p-6 bg-[#252220] border border-[#3A3633]">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-3">Schedule</p>
                  <p className="font-headline text-2xl text-[#E8E4DC] uppercase mb-1">{t.schedule}</p>
                  <p className="text-[#E8E4DC]/60 text-sm">{t.locationFull}</p>
                </div>

                {/* Equipment */}
                <div className="p-6 bg-[#252220] border border-[#3A3633]">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-3">Equipment</p>
                  <p className="text-[#E8E4DC]/80">{t.equipment}</p>
                </div>

                {/* Membership */}
                <div className="p-6 bg-[#252220] border border-[#3A3633]">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.2em] mb-3">Membership</p>
                  <p className="font-headline text-3xl text-[#E8E4DC] uppercase">{t.membership}</p>
                </div>

                {/* Join CTA */}
                <a
                  href="mailto:active@pauuniverse.com?subject=PAU Track Running"
                  className="block w-full p-5 bg-[#8B9A7D] text-[#1A1714] text-center font-medium hover:bg-[#9AAA8D] transition-colors"
                >
                  {t.joinCta} →
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
  );
};

// ============================================
// MAGAZINE PAGE
// ============================================
const MagazinePage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data) {
        setSubscribed(true);
        setEmail('');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Newsletter subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      
      {/* Masthead */}
      <header className="border-b border-[#1A1714]/20 py-6 px-6 md:px-10">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="editorial-caption text-[#1A1714]/50">VOL. I</span>
              <span className="w-px h-4 bg-[#1A1714]/20" />
              <span className="editorial-caption text-[#1A1714]/50">EST. 2026</span>
            </div>
            <span className="editorial-caption text-[#1A1714]/50">ZÜRICH</span>
          </div>
        </div>
      </header>

      {/* Hero Title */}
      <section className="py-12 md:py-16 px-6 md:px-10 border-b border-[#1A1714]/10">
        <div className="max-w-[1200px] mx-auto text-center">
          <h1 className="editorial-headline text-[4rem] sm:text-[6rem] md:text-[8rem] lg:text-[10rem] text-[#1A1714] leading-[0.85] tracking-tight">
            PAU MAG
          </h1>
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/70 mt-6 max-w-xl mx-auto">
            Track & field stories, athlete storytelling, culture, and editorial from the world of movement.
          </p>
        </div>
      </section>

      {/* Featured Article Hero */}
      <section className="py-12 px-6 md:px-10 border-b border-[#1A1714]/10">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image */}
            <a href="/magazine/dakar-2026" className="block group">
              <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/s9u8ubsk_Screenshot%202026-03-22%20at%2016.15.54.png"
                  alt="Dakar 2026"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            </a>
            
            {/* Content */}
            <div className="lg:py-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="editorial-category">Field Report</span>
                <span className="text-[#1A1714]/40 text-xs">·</span>
                <span className="editorial-caption text-[#1A1714]/50">MAR 2026</span>
              </div>
              
              <a href="/magazine/dakar-2026" className="block group">
                <h2 className="article-title text-4xl md:text-5xl lg:text-6xl text-[#1A1714] mb-6 group-hover:text-[#B8352B] transition-colors">
                  DAKAR 2026
                </h2>
              </a>
              
              <p className="editorial-body text-lg text-[#1A1714]/70 mb-8 max-w-lg">
                Senegal is stepping into a rare and historic position. In 2026, Dakar is scheduled to host the Youth Olympic Games — the first Olympic event ever held on African soil.
              </p>
              
              <a 
                href="/magazine/dakar-2026"
                className="editorial-link inline-flex items-center gap-2 text-[#1A1714] font-medium"
              >
                Read the full story <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-6 md:px-10 border-b border-[#1A1714]/10">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-2xl">
            <p className="editorial-body text-xl md:text-2xl text-[#1A1714]/80 leading-relaxed mb-4">
              PAUMAG is where performance meets culture. We publish training philosophy, athlete stories, visual projects, and ideas that make the body feel more intelligent.
            </p>
            <p className="editorial-body text-lg text-[#1A1714]/50">
              Not content for noise — work for people who practice.
            </p>
          </div>
        </div>
      </section>

      {/* Two Column Layout - Articles & Projects */}
      <section className="py-12 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left Column - MAG ARTICLES */}
            <div className="lg:col-span-7">
              {/* Section Header */}
              <div className="flex items-center justify-between pb-4 mb-8 border-b-2 border-[#1A1714]">
                <h3 className="article-title text-xl text-[#1A1714]">MAG ARTICLES</h3>
                <span className="editorial-caption text-[#1A1714]/50">4 Pieces</span>
              </div>

              {/* Articles List */}
              <div className="space-y-0 divide-y divide-[#1A1714]/10">
                
                {/* Article Card - Dimensional Playground */}
                <article className="py-8 first:pt-0">
                  <a href="/magazine/dimensional-playground" className="group grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-4">
                      <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/oxawwx2t__A7A8438.jpg"
                          alt="Dimensional Playground"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-category text-xs">Essay</span>
                        <span className="text-[#1A1714]/40 text-xs">·</span>
                        <span className="editorial-caption text-[#1A1714]/50 text-xs">FEB 2026</span>
                      </div>
                      <h4 className="article-title text-lg md:text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                        DIMENSIONAL PLAYGROUND
                      </h4>
                      <p className="editorial-body text-[#1A1714]/60 text-sm line-clamp-2 hidden md:block">
                        When track running becomes a commons again.
                      </p>
                    </div>
                  </a>
                </article>

                {/* Article Card - Coach Nicolas Interview */}
                <article className="py-8">
                  <a href="/magazine/coach-nicolas" className="group grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-4">
                      <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                        <img 
                          src="/assets/coach-nicolas-stadium.jpeg"
                          alt="Coach Nicolas Interview"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-category text-xs">Interview</span>
                        <span className="text-[#1A1714]/40 text-xs">·</span>
                        <span className="editorial-caption text-[#1A1714]/50 text-xs">MAY 2026</span>
                      </div>
                      <h4 className="article-title text-lg md:text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                        INSIDE DAKAR'S TRACK SCENE
                      </h4>
                      <p className="editorial-body text-[#1A1714]/60 text-sm line-clamp-2 hidden md:block">
                        Coach Nicolas on progress, Teranga, and what it takes.
                      </p>
                    </div>
                  </a>
                </article>

                {/* Article Card - Daouda Interview (NEW) */}
                <article className="py-8">
                  <a href="/magazine/daouda" className="group grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-4">
                      <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                        <img 
                          src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/vnf86hjn_Negative0-07-09%281%29.webp"
                          alt="Daouda Interview"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-category text-xs">Interview</span>
                        <span className="text-[#1A1714]/40 text-xs">·</span>
                        <span className="editorial-caption text-[#1A1714]/50 text-xs">JUL 2026</span>
                      </div>
                      <h4 className="article-title text-lg md:text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                        TEN PERCENT ON THE TRACK
                      </h4>
                      <p className="editorial-body text-[#1A1714]/60 text-sm line-clamp-2 hidden md:block">
                        Daouda on mindset, ownership, and reaching potential.
                      </p>
                    </div>
                  </a>
                </article>

                {/* Article Card - Fatima Editorial */}
                <article className="py-8">
                  <a href="/magazine/fatima-pau-sane" className="group grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-4">
                      <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/797unwbs__A7A8396.jpg"
                          alt="Fatima wearing PAU x SANE ACTIVE"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-category text-xs">Photo Essay</span>
                        <span className="text-[#1A1714]/40 text-xs">·</span>
                        <span className="editorial-caption text-[#1A1714]/50 text-xs">MAR 2026</span>
                      </div>
                      <h4 className="article-title text-lg md:text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                        FATIMA WEARING PAU X SANE ACTIVE
                      </h4>
                      <p className="editorial-body text-[#1A1714]/60 text-sm line-clamp-2 hidden md:block">
                        A visual story from La Corniche, Dakar.
                      </p>
                    </div>
                  </a>
                </article>

                {/* Article Card - Dakar 2026 */}
                <article className="py-8 last:pb-0">
                  <a href="/magazine/dakar-2026" className="group grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-4">
                      <div className="aspect-[4/3] overflow-hidden bg-[#E8E4DC]">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/s9u8ubsk_Screenshot%202026-03-22%20at%2016.15.54.png"
                          alt="Dakar 2026 - Athletes training"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-category text-xs">Field Report</span>
                        <span className="text-[#1A1714]/40 text-xs">·</span>
                        <span className="editorial-caption text-[#1A1714]/50 text-xs">MAR 2026</span>
                      </div>
                      <h4 className="article-title text-lg md:text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                        DAKAR 2026
                      </h4>
                      <p className="editorial-body text-[#1A1714]/60 text-sm line-clamp-2 hidden md:block">
                        A first look at African Olympic ground.
                      </p>
                    </div>
                  </a>
                </article>

              </div>
            </div>

            {/* Right Column - PROJECTS TO SUPPORT */}
            <div className="lg:col-span-5">
              {/* Section Header */}
              <div className="flex items-center justify-between pb-4 mb-8 border-b-2 border-[#B8352B]">
                <h3 className="article-title text-xl text-[#B8352B]">PROJECTS TO SUPPORT</h3>
                <span className="editorial-caption text-[#1A1714]/50">1 Project</span>
              </div>

              {/* Projects List */}
              <div className="space-y-6">

                {/* Project Card - Olympic Retreat Dakar */}
                <article className="group">
                  <a href="/magazine/olympic-retreat-dakar" className="block">
                    <div className="aspect-[16/9] overflow-hidden bg-[#E8E4DC] mb-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/bv0injy9_WhatsApp%20Image%202026-05-11%20at%205.07.07%E2%80%AFPM.jpeg"
                        alt="Olympic Retreat Dakar 2026"
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-[#B8352B]/10 text-[#B8352B] text-xs uppercase tracking-wider">Brand Camp</span>
                      <span className="text-[#1A1714]/40 text-xs">·</span>
                      <span className="editorial-caption text-[#1A1714]/50 text-xs">OCT 2026</span>
                    </div>
                    <h4 className="article-title text-xl text-[#1A1714] mb-2 group-hover:text-[#B8352B] transition-colors">
                      OLYMPIC RETREAT DAKAR 2026
                    </h4>
                    <p className="editorial-body text-[#1A1714]/60 text-sm mb-3">
                      A brand creator camp during the Youth Olympic Games. 12 guests, real stories, real content.
                    </p>
                    <span className="editorial-link text-sm text-[#B8352B]">Explore partnership →</span>
                  </a>
                </article>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-6 md:px-10 border-t border-[#1A1714]/10 bg-[#E5DCC8]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="font-headline text-3xl md:text-4xl text-[#1A1714] uppercase mb-4">
              Stay Close
            </h3>
            <p className="editorial-body text-[#1A1714]/60 mb-8">
              New pieces and drops — when it matters.
            </p>
            
            {subscribed ? (
              <div className="flex items-center justify-center gap-3 text-[#1A1714]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="editorial-body">You're in. We'll be in touch.</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 px-5 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] placeholder-[#1A1714]/40 focus:outline-none focus:border-[#1A1714] transition-colors editorial-body"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-[#1A1714] text-[#F5F3EF] editorial-caption hover:bg-[#B8352B] transition-colors disabled:opacity-50"
                  >
                    {loading ? 'SENDING...' : 'SUBSCRIBE'}
                  </button>
                </form>
                {error && <p className="text-[#B8352B] text-sm">{error}</p>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer Tagline */}
      <section className="py-8 px-6 md:px-10 border-t border-[#1A1714]/10">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="editorial-caption text-[#1A1714]/40">
            PAUMAG · ZÜRICH · ISSUE 01
          </p>
        </div>
      </section>

    </main>
    <Footer />
  </>
  );
};

// ============================================
// ARTICLE PAGE - COACHES FEATURE
// ============================================
const CoachesArticlePage = () => (
  <>
    <Navigation />
    <main className="pt-0 bg-[#1A1714] min-h-screen">
      
      {/* Hero Video */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/64av4q3v_PAU_COACHES_Interview_Horizontal-1.mov" type="video/quicktime" />
          <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/64av4q3v_PAU_COACHES_Interview_Horizontal-1.mov" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-transparent to-transparent" />
        
        <div className="relative z-10 px-6 md:px-10 pb-16 md:pb-24 max-w-[1400px]">
          <div className="flex items-center gap-4 text-[#E8E4DC]/60 text-xs uppercase tracking-[0.3em] mb-6">
            <span>VOL. 01</span>
            <span className="w-8 h-px bg-[#E8E4DC]/30" />
            <span>FEB 2026</span>
          </div>
          <h1 className="font-headline text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#E8E4DC] uppercase leading-[0.9] max-w-4xl">
            The Coaches<br />Behind PAU
          </h1>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          
          {/* Lead */}
          <div className="mb-16">
            <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
              Craft, Care, and Lived Knowledge
            </h2>
            <p className="text-[#E8E4DC]/80 text-xl md:text-2xl leading-relaxed">
              PAU is built on people: on craft, care, and lived knowledge. This coaches feature introduces the minds and hearts shaping our training culture, from distance architecture to sprint science, from strength work to breath and restoration.
            </p>
          </div>

          {/* Intro Body */}
          <div className="prose-pau space-y-8 text-[#E8E4DC]/70 text-lg leading-relaxed mb-16">
            <p>
              Each portrait is a window into the same mission: build athletes with range, dignity, and longevity.
            </p>
            <p>
              We're continuously expanding a 360 support system so athletes can access the full spectrum of guidance they need to progress. Training is only one layer. Around it, we're building pathways to strength work, recovery and bodywork, nutrition support, and personal training so performance becomes sustainable, not accidental.
            </p>
          </div>

          {/* Featured Video with Sound */}
          <div className="mb-16">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Watch the Interview</p>
            <div className="aspect-video overflow-hidden rounded-2xl bg-[#252220]">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/sn7FAqXQgnc?rel=0&modestbranding=1"
                title="PAU Coaches Interview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Coach Profiles */}
          <div className="space-y-16">
            
            {/* Okeile Stewart */}
            <div className="border-t border-[#3A3633] pt-12">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="aspect-square overflow-hidden rounded-2xl bg-[#252220]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/wlzuhtpm_74e290fc-1d6f-4bb6-85c2-1a08eda30233.jpg"
                    alt="Okeile Stewart"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Sprint Program Designer & Mentor</p>
                  <h3 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
                    Okeile Stewart
                  </h3>
                  <p className="text-[#E8E4DC]/50 text-sm mb-4">SWEPT Track Club, Jamaica</p>
                  <p className="text-[#E8E4DC]/70 text-lg leading-relaxed mb-6">
                    Okeile Stewart joins PAU through our official partnership with SWEPT Track Club in Jamaica, one of the most iconic sprint ecosystems on earth. As sprint program designer and mentor, he brings a culture of precision: speed with intent, technique with purpose, and training that respects the athlete. This is craft passed down, then sharpened: Jamaican excellence translated into our context, without losing its soul.
                  </p>
                  <a 
                    href="https://www.instagram.com/swept.tc/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
                  >
                    @swept.tc <Instagram size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Okeile Stewart Interview Video */}
            <div className="border-t border-[#3A3633] pt-12">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Watch: Interview with Okeile</p>
              <div className="aspect-video overflow-hidden rounded-2xl bg-[#252220]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/E93_BxNhH0g?rel=0&modestbranding=1"
                  title="Interview with Okeile Stewart"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Philippe Bandi */}
            <div className="border-t border-[#3A3633] pt-12">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="aspect-square overflow-hidden rounded-2xl bg-[#252220]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_490f13dc-93b4-4e04-9d6d-19130d9963d5/artifacts/q16o5eng_Screenshot%202026-03-08%20at%2018.43.57.png"
                    alt="Philippe Bandi"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Distance Program Designer & Mentor</p>
                  <h3 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
                    Philippe Bandi
                  </h3>
                  <p className="text-[#E8E4DC]/70 text-lg leading-relaxed mb-6">
                    Philippe Bandi is one of the quiet architects behind our distance work: a program designer and mentor who helps our athletes build real foundations: patient, intelligent, and durable. His presence brings structure without rigidity, and ambition without noise. For the distance crew, he's a compass, guiding training decisions with clarity, experience, and long-term thinking.
                  </p>
                  <a 
                    href="https://www.philippebandi.ch/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
                  >
                    philippebandi.ch <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Coach Hari */}
            <div className="border-t border-[#3A3633] pt-12">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="aspect-square overflow-hidden rounded-2xl bg-[#252220]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_490f13dc-93b4-4e04-9d6d-19130d9963d5/artifacts/s4tkhfyt_Screenshot%202026-03-08%20at%2018.44.49.png"
                    alt="Coach Hari"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Head Coach (On-the-Ground Lead)</p>
                  <h3 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
                    Coach Hari
                  </h3>
                  <p className="text-[#E8E4DC]/50 text-sm mb-4">Hariharan Roy Rasiah</p>
                  <p className="text-[#E8E4DC]/70 text-lg leading-relaxed">
                    Coach Hari is the good soul doing the work where it counts: on the track, with the athletes, week after week. As head coach, he holds the daily rhythm: presence, consistency, and care, while keeping standards high and the environment human. He's the bridge between plan and practice, the one who makes the system real in bodies, not just in theory.
                  </p>
                </div>
              </div>
            </div>

            {/* Ibrahima */}
            <div className="border-t border-[#3A3633] pt-12">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="aspect-square overflow-hidden rounded-2xl bg-[#252220]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_490f13dc-93b4-4e04-9d6d-19130d9963d5/artifacts/5bx6jkfv_Screenshot%202026-03-08%20at%2018.46.30.png"
                    alt="Ibrahima"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Strength & Conditioning Coach</p>
                  <h3 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
                    Ibrahima
                  </h3>
                  <p className="text-[#E8E4DC]/70 text-lg leading-relaxed mb-6">
                    Ibrahima leads strength and conditioning with a simple ethos: build the engine, protect the body, and make power repeatable. His work sits underneath everything: acceleration, posture, resilience, confidence, turning training into something athletes can sustain. He brings seriousness without ego, and structure without stiffness: the kind of coaching that makes performance feel safer, cleaner, and more available.
                  </p>
                  <a 
                    href="https://www.ibriathletics.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
                  >
                    ibriathletics.com <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Vanessa Gärtner */}
            <div className="border-t border-[#3A3633] pt-12">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="aspect-square overflow-hidden rounded-2xl bg-[#252220]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_490f13dc-93b4-4e04-9d6d-19130d9963d5/artifacts/arebw7cm_Screenshot%202026-03-08%20at%2018.41.56.png"
                    alt="Vanessa Gärtner"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Yoga & Breathwork Coach</p>
                  <h3 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">
                    Vanessa Gärtner
                  </h3>
                  <p className="text-[#E8E4DC]/70 text-lg leading-relaxed mb-6">
                    Vanessa Gärtner holds the recovery dimension: yoga and breathwork as training, not decoration. Her sessions bring athletes back into alignment, down regulating the nervous system, restoring range, and teaching control under pressure. In a culture that often glorifies grind, she protects the long game: presence, softness, and breath as performance tools.
                  </p>
                  <a 
                    href="https://www.thekitchen.yoga/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
                  >
                    thekitchen.yoga <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Closing Statement */}
          <div className="mt-16 md:mt-24 pt-16 border-t border-[#3A3633] text-center">
            <p className="font-headline text-4xl md:text-5xl lg:text-6xl text-[#E8E4DC] uppercase leading-[0.9] mb-8">
              Built on people.<br />Built to last.
            </p>
            <p className="text-[#E8E4DC]/70 text-xl max-w-xl mx-auto">
              Performance becomes sustainable, not accidental.
            </p>
          </div>

        </div>
      </article>

      {/* Back to Magazine */}
      <section className="py-16 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <Link 
            to="/magazine"
            className="inline-flex items-center gap-2 text-[#E8E4DC]/70 hover:text-[#E8E4DC] transition-colors"
          >
            <ArrowRight size={18} className="rotate-180" /> Back to Magazine
          </Link>
          <span className="text-[#E8E4DC]/50 text-xs uppercase tracking-[0.3em]">Issue 01 · Feb 2026</span>
        </div>
      </section>

    </main>
    <Footer />
  </>
);

// ============================================
// ARTICLE PAGE - COACH NICOLAS INTERVIEW
// ============================================
const CoachNicolasArticlePage = () => (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      {/* Article Header */}
      <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
            <ArrowLeft size={16} /> Back to PAU MAG
          </a>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="editorial-category">Interview</span>
            <span className="text-[#1A1714]/40 text-xs">·</span>
            <span className="editorial-caption text-[#1A1714]/50">MAY 2026</span>
          </div>
          
          <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
            Inside Dakar's Track Scene
          </h1>
          
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
            Coach Nicolas on progress, Teranga, and the future of athletics in Senegal.
          </p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="px-6 md:px-10 py-12">
        <div className="max-w-[1100px] mx-auto">
          <img 
            src="/assets/coach-nicolas-stadium.jpeg"
            alt="Coach Nicolas at Stade Leopold Sedar Senghor"
            className="w-full h-auto"
          />
          <p className="editorial-caption text-[#1A1714]/50 mt-4">Coach Nicolas at Stade Léopold Sédar Senghor, Dakar</p>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-6 md:px-10 pb-20">
        <div className="max-w-[680px] mx-auto">
          <blockquote className="editorial-pullquote my-12">
            "There are talented athletes everywhere — but access to competition changes everything."
          </blockquote>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>In our first PAU Magazine video interview, Coach Nicolas David Cory Yayi, Senegalese coach, PE teacher, and president of Dakar Université Club (athletics), opens the door to the reality of track and field in Dakar.</p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img src="/assets/coach-nicolas-track.jpeg" alt="Coach Nicolas at the track" className="w-full h-auto" />
          </figure>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>He speaks about a group that's growing across levels, training mostly at the stadium and, when possible, at Camp Leclerc's weight room, proof that progress is happening session by session.</p>
            <p>And he highlights something uniquely Senegalese at the heart of it: <strong className="text-[#1A1714]">Teranga</strong>, a culture of welcome that builds respect, cohesion, and an openness that can turn strangers into teammates.</p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img src="/assets/dakar-athletes-walking.jpeg" alt="Athletes on the track" className="w-full h-auto" />
          </figure>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>But alongside the energy, he names the limits that still shape the path forward: tight budgets, visa refusals, and too few high-level competitions.</p>
            <p className="text-[#1A1714] font-medium text-xl">The talent is here; what's needed now are the mechanisms, sponsors, and opportunities to let it travel, test itself, and rise.</p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img src="/assets/dakar-athletes-warmup.jpeg" alt="Athletes warming up in Dakar" className="w-full h-auto" />
          </figure>

          <div className="my-16">
            <p className="editorial-caption text-[#1A1714]/50 mb-4">Watch the Full Interview</p>
            <div className="aspect-video bg-[#1A1714]">
              <iframe src="https://www.youtube.com/embed/4D5ZtATe60E?start=591" title="Coach Nicolas Interview" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>

          <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to PAU MAG</a>
          </div>
        </div>
      </article>
    </main>
    <Footer />
  </>
);

// ============================================
// ARTICLE PAGE - DAOUDA
// ============================================
const DaoudaArticlePage = () => (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      {/* Article Header */}
      <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
            <ArrowLeft size={16} /> Back to PAU MAG
          </a>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="editorial-category">Interview</span>
            <span className="text-[#1A1714]/40 text-xs">·</span>
            <span className="editorial-caption text-[#1A1714]/50">JULY 2026</span>
          </div>
          
          <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
            Ten Percent on the Track
          </h1>
          
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
            Daouda on mindset, ownership, and what it really means to help someone reach their potential.
          </p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="px-6 md:px-10 py-12">
        <div className="max-w-[1100px] mx-auto">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/vnf86hjn_Negative0-07-09%281%29.webp"
            alt="Daouda portrait"
            className="w-full h-auto"
          />
          <p className="editorial-caption text-[#1A1714]/50 mt-4">Daouda, Dakar</p>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-6 md:px-10 pb-20">
        <div className="max-w-[680px] mx-auto">
          <blockquote className="editorial-pullquote my-12">
            "Only ten percent of the work is on the track. Ninety percent is in the head."
          </blockquote>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>Not every coach teaches through answers.</p>
            <p>Some teach through questions, trust, contradiction, and the quiet belief that growth begins long before the stopwatch starts.</p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/61ecvbi7_Screenshot%202026-07-16%20at%2022.22.52.webp" 
              alt="Daouda on balcony overlooking Dakar" 
              className="w-full h-auto" 
            />
          </figure>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>We spent a day driving through Dakar with Daouda, listening to the stories that shaped his philosophy: from a childhood surrounded by sport to an approach that places the mind before the body.</p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/jhiw1n20_Screenshot%202026-07-16%20at%2022.24.10.webp" 
              alt="Daouda in conversation" 
              className="w-full h-auto" 
            />
          </figure>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>His methods aren't always conventional. At times they're challenging, even uncomfortable. But they're rooted in a simple conviction: <strong className="text-[#1A1714]">real performance starts when an athlete takes ownership of their own path.</strong></p>
          </div>

          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/ibuvral6_Screenshot%202026-07-16%20at%2022.24.41.webp" 
              alt="Dakar cityscape" 
              className="w-full h-auto" 
            />
            <p className="editorial-caption text-[#1A1714]/50 mt-4">Dakar, Senegal</p>
          </figure>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p className="text-[#1A1714] font-medium text-xl">This is a conversation about coaching, identity, responsibility, and what it really means to help someone reach their potential.</p>
          </div>

          {/* Video */}
          <div className="my-16">
            <p className="editorial-caption text-[#1A1714]/50 mb-4">Watch the Full Interview</p>
            <div className="aspect-video bg-[#1A1714]">
              <iframe 
                src="https://www.youtube.com/embed/RCz-oVJSM5E?start=2" 
                title="Daouda Interview" 
                className="w-full h-full" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen 
              />
            </div>
          </div>

          <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to PAU MAG</a>
          </div>
        </div>
      </article>
    </main>
    <Footer />
  </>
);

// ============================================
// ARTICLE PAGE - FATIMA AT LA CORNICHE
// ============================================
const FatimaArticlePage = () => (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      {/* Article Header */}
      <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
            <ArrowLeft size={16} /> Back to PAU MAG
          </a>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="editorial-category">Photo Essay</span>
            <span className="text-[#1A1714]/40 text-xs">·</span>
            <span className="editorial-caption text-[#1A1714]/50">MAR 2026</span>
          </div>
          
          <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
            Fatima wearing PAU x SANE ACTIVE
          </h1>
          
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
            A visual story from La Corniche, Dakar, Senegal.
          </p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="px-6 md:px-10 py-12">
        <div className="max-w-[1100px] mx-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/797unwbs__A7A8396.jpg"
            alt="Fatima wearing PAU x SANE ACTIVE at La Corniche"
            className="w-full h-auto"
          />
          <p className="editorial-caption text-[#1A1714]/50 mt-4">Fatima at La Corniche, Dakar</p>
        </div>
      </div>

      {/* Photo Gallery */}
      <article className="px-6 md:px-10 pb-16">
        <div className="max-w-[1100px] mx-auto">
          
          {/* Gallery Grid */}
          <div className="space-y-8">
            {/* Full Width Image 1 */}
            <div className="overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/fbazhrds__A7A8360.jpg"
                alt="Fatima at La Corniche"
                className="w-full h-auto"
              />
            </div>

            {/* Two Column Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/d8orae12__A7A8423.jpg"
                  alt="Fatima at La Corniche"
                  className="w-full h-auto"
                />
              </div>
              <div className="overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/pyo6dhyz__A7A8432.jpg"
                  alt="Fatima at La Corniche"
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Full Width Image 2 */}
            <div className="overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/dqprayx5__A7A8435.jpg"
                alt="Fatima at La Corniche"
                className="w-full h-auto"
              />
            </div>

            {/* Full Width Image 3 */}
            <div className="overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/hhe5hpdn__A7A8438.jpg"
                alt="Fatima at La Corniche"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Credits */}
          <div className="mt-16 md:mt-24 pt-12 border-t border-[#1A1714]/10">
            <p className="editorial-caption text-[#1A1714]/50 mb-8">Credits</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-[#1A1714]/70">
              <div>
                <p className="editorial-caption text-[#1A1714]/40 mb-2">Talent</p>
                <p className="editorial-body text-[#1A1714]">Fatima</p>
              </div>
              <div>
                <p className="editorial-caption text-[#1A1714]/40 mb-2">Location</p>
                <p className="editorial-body text-[#1A1714]">La Corniche, Dakar, Senegal</p>
              </div>
              <div>
                <p className="editorial-caption text-[#1A1714]/40 mb-2">Wearing</p>
                <p className="editorial-body text-[#1A1714]">PAU x SANE ACTIVE</p>
              </div>
              <div>
                <p className="editorial-caption text-[#1A1714]/40 mb-2">Issue</p>
                <p className="editorial-body text-[#1A1714]">PAUMAG Vol. 01</p>
              </div>
            </div>
          </div>

          {/* Back Navigation */}
          <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
          </div>

        </div>
      </article>
    </main>
    <Footer />
  </>
);

// ============================================
// ARTICLE PAGE - OLYMPIC RETREAT DAKAR 2026
// ============================================
const OlympicRetreatArticlePage = () => (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      {/* Article Header */}
      <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
            <ArrowLeft size={16} /> Back to PAU MAG
          </a>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="editorial-category">Field Report</span>
            <span className="text-[#1A1714]/40 text-xs">·</span>
            <span className="editorial-caption text-[#1A1714]/50">JUN 2026</span>
          </div>
          
          <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
            Olympic Retreat Dakar 2026
          </h1>
          
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
            A Brand Creator Camp During the Youth Olympic Games
          </p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="px-6 md:px-10 py-12">
        <div className="max-w-[1100px] mx-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/u2q4zph8_WhatsApp%20Image%202026-05-11%20at%205.07.07%E2%80%AFPM.jpeg"
            alt="Athletes training at Dakar stadium"
            className="w-full h-auto"
          />
          <p className="editorial-caption text-[#1A1714]/50 mt-4">Stade Léopold Sédar Senghor, Dakar, Senegal</p>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-6 md:px-10 pb-20">
        <div className="max-w-[680px] mx-auto">
          
          {/* Lead */}
          <p className="editorial-body text-2xl md:text-3xl text-[#1A1714] leading-relaxed mb-12">
            Dakar is about to host the first Olympic event ever held on African soil. PAUMAG is building something around that moment — not a viewing trip, not a brand activation in the traditional sense. A hosted retreat for 12 guests, designed to produce real stories and real content, rooted in the communities that make Dakar what it is.
          </p>

          <p className="editorial-body text-xl text-[#1A1714]/80 mb-12 font-medium">
            We're calling it the Olympic Retreat.
          </p>

          {/* What it is */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            What it is
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>A curated, small-group camp built around three pillars:</p>
            <ol className="list-decimal list-inside space-y-3 ml-4">
              <li><strong>Daily interactive sports and wellness sessions</strong> — track work, beach movement, recovery</li>
              <li><strong>Curated Youth Olympic Games attendance</strong> — a handpicked schedule of events designed by PAUMAG</li>
              <li><strong>Community moments</strong> — real touchpoints with Dakar's sports and cultural scenes: surf clubs, skate crews, athletics communities, youth initiatives, creative collectives</li>
            </ol>
            <p>PAUMAG acts as the host and editorial platform — capturing stories and producing content throughout.</p>
          </div>

          {/* Image Break 1 */}
          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/zwbg4066_WhatsApp%20Image%202026-05-11%20at%205.07.07%E2%80%AFPM%20%281%29.jpeg"
              alt="Athletes warming up at the stadium"
              className="w-full h-auto"
            />
          </figure>

          {/* Who it's for */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Who it's for
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>Sportswear, performance, wellness, beverage, tech, and lifestyle brands looking for a turnkey Dakar 2026 activation:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Host your talent on the ground during a once-in-a-generation moment</li>
              <li>Generate authentic content with a strong narrative backdrop</li>
              <li>Connect with local communities in a respectful, intentional way</li>
            </ul>
          </div>

          {/* Program rhythm */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Program rhythm
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-4">
            <p><strong>Morning</strong> — Interactive sports program: training, movement, recovery, team sessions</p>
            <p><strong>Afternoon</strong> — Youth Olympic Games attendance based on a curated schedule (finalized once the official JOJ competition calendar is published)</p>
            <p><strong>Evening</strong> — Community experiences, dinners, and curated cultural moments</p>
          </div>

          {/* Image Break 2 */}
          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/7s0nukz7_WhatsApp%20Image%202026-05-11%20at%205.07.07%E2%80%AFPM%20%282%29.jpeg"
              alt="Coach at the track in Dakar"
              className="w-full h-auto"
            />
          </figure>

          {/* The community angle */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            The community angle
          </h3>
          <p className="editorial-body text-sm uppercase tracking-wider text-[#1A1714]/50 mb-4">Key differentiator</p>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p><strong>This is not about using Dakar as a backdrop.</strong></p>
            <p>We design events and touchpoints with local communities during the Games — surf and skate clubs, athletics groups, youth initiatives, creative scenes. The goal: natural, respectful collaboration moments that give guests a real connection to the city and put local communities in the spotlight.</p>
          </div>

          {/* Content & media */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Content & media
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>The retreat is built as a content engine.</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>PAUMAG</strong> creates editorial and social content with guests and local communities — stories, interviews, photo, video</li>
              <li><strong>Creators</strong> are supported to produce as much content as possible for themselves and for the brand — with intentional moments designed for filming without feeling staged</li>
            </ul>
          </div>

          {/* Image Break 3 - Dakar 2026 Logo */}
          <figure className="my-12 -mx-6 md:-mx-16 bg-black p-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/l0vlclab_logotype___dakar_2026_summer_youth_olympics_by_786542_debdxiv-414w-2x.png"
              alt="Dakar 2026 Youth Olympic Games logo"
              className="max-w-md h-auto"
            />
          </figure>

          {/* Two ways to participate */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Two ways to participate
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <div className="p-6 bg-[#1A1714]/5 border-l-4 border-[#1A1714]/20">
              <p className="font-medium text-[#1A1714] mb-2">Model A — Brand brings talent</p>
              <p>Your brand selects and sends its own group (ambassadors, athletes, creators + optional marketing team). PAUMAG hosts and designs the full program, community moments, and content environment.</p>
            </div>
            <div className="p-6 bg-[#1A1714]/5 border-l-4 border-[#1A1714]/20">
              <p className="font-medium text-[#1A1714] mb-2">Model B — Brand sponsors, PAUMAG curates talent</p>
              <p>Your brand finances the retreat. PAUMAG curates the creator lineup (with brand approval) to maximize storytelling, reach, and cultural fit — while still meeting your brand objectives.</p>
            </div>
          </div>

          {/* What brands get */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            What brands get
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-4">
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>A turnkey hosted experience for your talent during Dakar 2026</li>
              <li>A structured itinerary balancing wellness, sport, and culture</li>
              <li>Authentic local community touchpoints and collaborations</li>
              <li>A high-output content environment with a strong narrative backdrop (Youth Olympics + Dakar)</li>
            </ul>
          </div>

          {/* Logistics */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Logistics
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-4">
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Duration:</strong> 5–6 days per retreat</li>
              <li><strong>Group size:</strong> 12 guests + PAUMAG + local support team</li>
              <li><strong>Two slots available:</strong>
                <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                  <li>Slot A — Week 1</li>
                  <li>Slot B — Week 2</li>
                </ul>
              </li>
              <li>Exact dates confirmed once the official JOJ competition calendar is published</li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-16 pt-12 border-t border-[#1A1714]/10 text-center">
            <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
              Next step
            </h3>
            <p className="editorial-body text-lg text-[#1A1714]/80 mb-8">
              Tell us your brand goals — PAUMAG will reply with the full 1-pager and next steps.
            </p>
            <a 
              href="mailto:active@pauuniverse.com?subject=Olympic Retreat Dakar 2026 Inquiry"
              className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-8 py-4 rounded-full font-medium hover:bg-[#2A2724] transition-colors"
            >
              Contact PAUMAG <ArrowUpRight size={18} />
            </a>
          </div>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-[#1A1714]/10 text-center">
            <p className="editorial-caption text-[#1A1714]/40">PAUMAG · ZÜRICH · 2026</p>
          </div>

          {/* Back Navigation */}
          <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
          </div>

        </div>
      </article>
    </main>
    <Footer />
  </>
);

// ============================================
// ARTICLE PAGE - DAKAR 2026
// ============================================
const DakarArticlePage = () => {
  const [activeLang, setActiveLang] = useState('en');
  
  const content = {
    en: {
      title: "Dakar 2026",
      subtitle: "A First Look at African Olympic Ground",
      intro: "Senegal is stepping into a rare and historic position.",
      body: [
        "In 2026, Dakar is scheduled to host the Youth Olympic Games. If delivered as planned, it will mark the first Olympic event ever held on African soil. That matters not only because of symbolism, but because it shifts the geography of global sport. For once, the world is being asked to look toward Africa not only as a reservoir of talent, but as a stage, a host, and a center.",
        "This first film stays close to the track. No interviews yet. No explanation. Just sequences from Stade Leopold Sedar Senghor in Dakar, where movement, rhythm, repetition, and atmosphere begin to speak for themselves. The point is not only to show athletes training. It is to show the environment around performance: the textures, the conditions, the culture, the energy that shapes the body before the stopwatch ever does.",
        "Because performance is never neutral. It is cultural. It is social. It is political.",
        "Track and field is often presented as one of the purest sports in the world. Run, jump, throw. Time does not lie. Distance does not lie. But behind that apparent simplicity sits a more complicated reality. To compete on the biggest stages, athletes usually have to pass through national systems, federations, passports, and funding structures. They are asked to represent a country, and with that comes the assumption of belonging. Yet behind the scenes, those decisions are not always shaped by identity alone. They are often shaped by access, money, opportunity, and politics.",
        "Who gets support? Who gets selected? Who gets to travel? Who gets seen? These are not small questions. They shape careers.",
        "For decades, the main stages of global track and field have been concentrated in Europe, North America, and at times Asia. Meanwhile, Africa has continued to produce extraordinary athletic talent, often under far more difficult conditions. The issue has never been a lack of ability. More often, it has been a lack of access. Visa restrictions, passport inequality, weak institutional support, and federation politics have all limited who gets to appear, compete, and build a career at the highest level.",
        "That is part of the deeper story this series wants to enter. Not with easy slogans. Not with romanticism. But with attention.",
        "Senegal hosting the Youth Olympic Games creates a powerful opening. It places the country, and by extension the continent, at the center of an international sporting conversation. It offers a chance to ask bigger questions about who global sport is built for, who gets left at the margins, and what it would mean to take African sporting worlds seriously on their own terms.",
        "This series begins with observation. Later, we will bring in the voices of coaches, athletes, and people working inside this reality. We will ask what performance means in a place where culture, history, economics, and ambition meet on the same track. We will ask what sport reveals about power. And we will ask what becomes possible when the center begins to shift."
      ],
      closing: "This is only part one. A first look. A first rhythm. A first invitation into the world around the lane lines."
    },
    de: {
      title: "Dakar 2026",
      subtitle: "Ein erster Blick auf olympischen Boden in Afrika",
      intro: "Senegal tritt in einen seltenen und historischen Moment ein.",
      body: [
        "2026 soll Dakar die Olympischen Jugendspiele ausrichten. Wenn alles wie geplant stattfindet, wäre es die erste olympische Veranstaltung überhaupt auf afrikanischem Boden. Das ist nicht nur symbolisch bedeutsam. Es verschiebt auch die Geografie des globalen Sports. Zum ersten Mal wird die Welt aufgefordert, auf Afrika nicht nur als Reservoir von Talent zu blicken, sondern als Bühne, Gastgeber und Zentrum.",
        "Dieser erste Film bleibt nah an der Bahn. Noch keine Interviews. Keine Erklärung. Nur Sequenzen aus dem Stade Leopold Sedar Senghor in Dakar, in denen Bewegung, Rhythmus, Wiederholung und Atmosphäre für sich selbst zu sprechen beginnen. Es geht nicht nur darum, Athletinnen und Athleten beim Training zu zeigen. Es geht darum, das Umfeld von Leistung sichtbar zu machen: die Texturen, die Bedingungen, die Kultur, die Energie, die den Körper formt, lange bevor die Stoppuhr es tut.",
        "Denn Leistung ist niemals neutral. Sie ist kulturell. Sie ist sozial. Sie ist politisch.",
        "Leichtathletik wird oft als eine der reinsten Sportarten der Welt dargestellt. Laufen, springen, werfen. Die Zeit lügt nicht. Die Weite lügt nicht. Doch hinter dieser scheinbaren Einfachheit liegt eine komplexere Realität. Wer auf den größten Bühnen antreten will, muss sich meist durch nationale Systeme, Verbände, Pässe und Förderstrukturen bewegen. Athletinnen und Athleten repräsentieren ein Land, und damit geht oft die Annahme von Zugehörigkeit einher. Hinter den Kulissen werden diese Entscheidungen jedoch nicht nur von Identität geprägt. Sie werden oft von Zugang, Geld, Möglichkeiten und Politik bestimmt.",
        "Wer erhält Unterstützung? Wer wird ausgewählt? Wer darf reisen? Wer wird gesehen? Das sind keine kleinen Fragen. Sie prägen Karrieren.",
        "Über Jahrzehnte lagen die wichtigsten Bühnen der globalen Leichtathletik vor allem in Europa, Nordamerika und teilweise in Asien. Gleichzeitig hat Afrika weiterhin außergewöhnliche Talente hervorgebracht, oft unter deutlich schwierigeren Bedingungen. Das Problem war nie ein Mangel an Fähigkeit. Viel häufiger war es ein Mangel an Zugang. Visabeschränkungen, Ungleichheiten bei Pässen, schwache institutionelle Unterstützung und Verbandspolitik haben immer wieder begrenzt, wer überhaupt erscheinen, antreten und auf höchstem Niveau eine Laufbahn aufbauen kann.",
        "Genau in diese tiefere Geschichte möchte diese Serie eintreten. Nicht mit einfachen Parolen. Nicht mit Romantisierung. Sondern mit Aufmerksamkeit.",
        "Dass Senegal die Olympischen Jugendspiele ausrichtet, eröffnet einen kraftvollen Moment. Das Land und damit auch der Kontinent rücken ins Zentrum eines internationalen sportlichen Gesprächs. Es ist eine Gelegenheit, größere Fragen zu stellen: Für wen ist der globale Sport gebaut? Wer bleibt an den Rändern? Und was würde es bedeuten, afrikanische Sportwelten in ihrer eigenen Logik ernst zu nehmen?",
        "Diese Serie beginnt mit Beobachtung. Später werden wir die Stimmen von Trainerinnen, Trainern, Athletinnen und Athleten sowie von Menschen einbringen, die innerhalb dieser Realität arbeiten. Wir werden fragen, was Leistung an einem Ort bedeutet, an dem Kultur, Geschichte, Ökonomie und Ambition auf derselben Bahn zusammenkommen. Wir werden fragen, was Sport über Macht sichtbar macht. Und wir werden fragen, was möglich wird, wenn sich das Zentrum zu verschieben beginnt."
      ],
      closing: "Dies ist erst der Anfang. Ein erster Blick. Ein erster Rhythmus. Eine erste Einladung in die Welt rund um die Bahn in Dakar."
    },
    fr: {
      title: "Dakar 2026",
      subtitle: "Un premier regard sur le sol olympique africain",
      intro: "Le Sénégal entre dans un moment rare et historique.",
      body: [
        "En 2026, Dakar doit accueillir les Jeux Olympiques de la Jeunesse. Si tout se déroule comme prévu, il s'agira du tout premier événement olympique organisé sur le sol africain. Cela compte, non seulement sur le plan symbolique, mais aussi parce que cela déplace la géographie du sport mondial. Pour une fois, le monde est invité à regarder l'Afrique non seulement comme un réservoir de talents, mais comme une scène, un hôte et un centre.",
        "Ce premier film reste au plus près de la piste. Pas encore d'entretiens. Pas d'explication. Seulement des séquences tournées au Stade Leopold Sedar Senghor à Dakar, où le mouvement, le rythme, la répétition et l'atmosphère commencent à parler d'eux-mêmes. Il ne s'agit pas seulement de montrer des athlètes à l'entraînement. Il s'agit de montrer l'environnement de la performance : les textures, les conditions, la culture, l'énergie qui façonnent le corps bien avant que le chronomètre ne le fasse.",
        "Parce que la performance n'est jamais neutre. Elle est culturelle. Elle est sociale. Elle est politique.",
        "L'athlétisme est souvent présenté comme l'un des sports les plus purs au monde. Courir, sauter, lancer. Le temps ne ment pas. La distance ne ment pas. Mais derrière cette apparente simplicité se cache une réalité plus complexe. Pour concourir sur les plus grandes scènes, les athlètes doivent généralement passer par des systèmes nationaux, des fédérations, des passeports et des structures de financement. On leur demande de représenter un pays, et avec cela vient souvent l'idée d'appartenance. Pourtant, en coulisses, ces décisions ne sont pas toujours déterminées par l'identité seule. Elles sont souvent façonnées par l'accès, l'argent, les opportunités et la politique.",
        "Qui reçoit du soutien ? Qui est sélectionné ? Qui peut voyager ? Qui est vu ? Ce ne sont pas de petites questions. Elles façonnent des carrières.",
        "Pendant des décennies, les principales scènes de l'athlétisme mondial se sont concentrées en Europe, en Amérique du Nord et parfois en Asie. Pendant ce temps, l'Afrique a continué à produire des talents exceptionnels, souvent dans des conditions bien plus difficiles. Le problème n'a jamais été un manque de capacité. Plus souvent, il s'agissait d'un manque d'accès. Les restrictions de visa, les inégalités liées aux passeports, la faiblesse du soutien institutionnel et les politiques fédérales ont limité, encore et encore, ceux qui peuvent apparaître, concourir et construire une carrière au plus haut niveau.",
        "C'est dans cette histoire plus profonde que cette série souhaite entrer. Non pas avec des slogans faciles. Non pas avec romantisme. Mais avec attention.",
        "Le fait que le Sénégal accueille les Jeux Olympiques de la Jeunesse ouvre un moment puissant. Le pays, et à travers lui le continent, se retrouve au centre d'une conversation sportive internationale. C'est une occasion de poser des questions plus vastes : pour qui le sport mondial a-t-il été construit ? Qui reste en marge ? Et que signifierait prendre au sérieux les mondes sportifs africains selon leurs propres termes ?",
        "Cette série commence par l'observation. Plus tard, nous ferons entendre les voix des entraîneurs, des athlètes et de celles et ceux qui travaillent au sein de cette réalité. Nous demanderons ce que signifie la performance dans un lieu où la culture, l'histoire, l'économie et l'ambition se rencontrent sur la même piste. Nous demanderons ce que le sport révèle du pouvoir. Et nous demanderons ce qui devient possible lorsque le centre commence à se déplacer."
      ],
      closing: "Ce n'est que la première partie. Un premier regard. Un premier rythme. Une première invitation dans le monde qui entoure les lignes de couloir."
    }
  };

  const current = content[activeLang];

  return (
    <>
      <Navigation variant="light" />
      <main className="pt-20 editorial-bg min-h-screen">
        {/* Article Header */}
        <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
          <div className="max-w-[900px] mx-auto">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
            
            <div className="flex items-center gap-3 mb-6">
              <span className="editorial-category">Field Report</span>
              <span className="text-[#1A1714]/40 text-xs">·</span>
              <span className="editorial-caption text-[#1A1714]/50">MAR 2026</span>
            </div>
            
            <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
              {current.title}
            </h1>
            
            <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
              {current.subtitle}
            </p>
          </div>
        </header>

        {/* Hero Video */}
        <div className="px-6 md:px-10 py-12">
          <div className="max-w-[1100px] mx-auto">
            <div className="aspect-video overflow-hidden bg-[#1A1714]">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/8B0yWIfuhyY?rel=0&modestbranding=1"
                title="Dakar 2026 - Part One"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="editorial-caption text-[#1A1714]/50 mt-4">Stade Léopold Sédar Senghor, Dakar, Senegal</p>
          </div>
        </div>

        {/* Article Content */}
        <article className="px-6 md:px-10 pb-20">
          <div className="max-w-[680px] mx-auto">
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 mb-12 pb-8 border-b border-[#1A1714]/10">
              {['en', 'de', 'fr'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setActiveLang(lang)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
                    activeLang === lang 
                      ? 'bg-[#1A1714] text-[#EDE6D6]' 
                      : 'text-[#1A1714]/50 hover:text-[#1A1714] border border-[#1A1714]/20'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            {/* Intro */}
            <p className="editorial-body text-2xl md:text-3xl text-[#1A1714] leading-relaxed mb-12">
              {current.intro}
            </p>

            {/* Body paragraphs with images interspersed */}
            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              {current.body.slice(0, 2).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Image Break 1 */}
            <figure className="my-12 -mx-6 md:-mx-16">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/n6nd4570_Screenshot%202026-03-22%20at%2016.14.08.png"
                alt="Athletes training at Stade Leopold Sedar Senghor"
                className="w-full h-auto"
              />
            </figure>

            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              <blockquote className="editorial-pullquote">
                "Performance is never neutral. It is cultural. It is social. It is political."
              </blockquote>
              {current.body.slice(2, 5).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Image Break 2 */}
            <figure className="my-12 -mx-6 md:-mx-16">
              <div className="grid md:grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/3q7rd1yq_Screenshot%202026-03-22%20at%2016.15.54.png"
                  alt="Track training in Dakar"
                  className="w-full h-auto"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/hyqngbkh_Screenshot%202026-03-22%20at%2016.18.17.png"
                  alt="Athletes at Stade Leopold Sedar Senghor"
                  className="w-full h-auto"
                />
              </div>
            </figure>

            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              {current.body.slice(5).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Image Break 3 */}
            <figure className="my-12 -mx-6 md:-mx-16">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/nmp27rm1_Screenshot%202026-03-22%20at%2016.21.26.png"
                alt="Track at Stade Leopold Sedar Senghor"
                className="w-full h-auto"
              />
            </figure>

            {/* Closing */}
            <div className="mt-16 pt-12 border-t border-[#1A1714]/10">
              <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/80">
                {current.closing}
              </p>
            </div>

            {/* Back Navigation */}
            <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
              <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
                <ArrowLeft size={16} /> Back to PAU MAG
              </a>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// ARTICLE PAGE - DIMENSIONAL PLAYGROUND
// ============================================
const ArticlePage = () => (
  <>
    <Navigation variant="light" />
    <main className="pt-20 editorial-bg min-h-screen">
      {/* Article Header */}
      <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
            <ArrowLeft size={16} /> Back to PAU MAG
          </a>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="editorial-category">Essay</span>
            <span className="text-[#1A1714]/40 text-xs">·</span>
            <span className="editorial-caption text-[#1A1714]/50">FEB 2026</span>
          </div>
          
          <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
            Dimensional Playground
          </h1>
          
          <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
            When track running becomes a commons again.
          </p>
        </div>
      </header>

      {/* Hero Video */}
      <div className="px-6 md:px-10 py-12">
        <div className="max-w-[1100px] mx-auto">
          <div className="aspect-video overflow-hidden bg-[#1A1714]">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/XA8bQRzhseY?rel=0&modestbranding=1"
              title="This Dimension Is A Playground"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <p className="editorial-caption text-[#1A1714]/50 mt-4">PAU Track Running, Zürich</p>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-6 md:px-10 pb-20">
        <div className="max-w-[680px] mx-auto">
          
          {/* Lead */}
          <p className="editorial-body text-2xl md:text-3xl text-[#1A1714] leading-relaxed mb-12">
            On cold evenings in Zurich, the floodlights come on and the track stops behaving like a specialist facility. It becomes public ground with precision.
          </p>

          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              The lanes fill with people outside the usual silhouette of an elite squad: former footballers, desk workers, students, mothers, creatives, a few athletes with serious times, and many who have never called themselves runners. They share spikes and stories, warm up to the same rhythm, and listen to cues that move between biomechanics and philosophy.
            </p>
            <p>
              This is PAU — not a product, not a trend, but a proposition: performance can be shared without being diluted. Culture is not decoration here; it is the base.
            </p>
          </div>

          {/* Section: The Missing Middle */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            The Missing Middle
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              Sport often splits into two rooms. One is structured and competitive, built around pathways, calendars, tiers. The other is private and polished: it speaks about wellbeing and community, then quietly prices many people out.
            </p>
            <p>
              Between them sits a wide middle with no clear home — late starters, comebacks, people who want technique and progression, and a body that lasts. PAU exists inside that tension and refuses to accept it as normal.
            </p>
          </div>

          {/* Section: A Bridge */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            A Bridge
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              Running is booming, and run clubs have made it open again — welcoming, often free, built on energy and belonging. Track is the natural next step. The oval is made for learning: technique, speed, pacing, progression.
            </p>
            <p>
              But most spaces are built for intensity, not for layers; they can hold atmosphere, yet struggle to hold a range of ambitions. So we built a bridge: the openness of run clubs, with the depth and seriousness of track.
            </p>
          </div>

          {/* Video Break - Two Videos Side by Side */}
          <figure className="my-12 -mx-6 md:-mx-16">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="aspect-video overflow-hidden bg-[#1A1714]">
                <video
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/q0bzlm2g_Dimensional%20Playground%20Nini%20Reel%20ProRes422HQ_ENCODED.mov" type="video/quicktime" />
                  <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/q0bzlm2g_Dimensional%20Playground%20Nini%20Reel%20ProRes422HQ_ENCODED.mov" type="video/mp4" />
                </video>
              </div>
              <div className="aspect-video overflow-hidden bg-[#1A1714]">
                <video
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/9rjjzz3d_Dimensional%20Playground_Reel_Noe%CC%81-1%20%281%29.mov" type="video/quicktime" />
                  <source src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/9rjjzz3d_Dimensional%20Playground_Reel_Noe%CC%81-1%20%281%29.mov" type="video/mp4" />
                </video>
              </div>
            </div>
          </figure>

          {/* Section: Methodology, Shared */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Methodology, Shared
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              PAU takes performance-level principles and applies them to a different audience: anyone willing to show up. Sprint mechanics, strength and conditioning, recovery — not reserved for the top 1%, but offered to people ready to train with intention.
            </p>
            <p>
              In this frame, performance and wellbeing aren't separate projects; they're the same practice, approached from different angles. The track becomes a laboratory for how you live.
            </p>
          </div>

          {/* Section: Movement Is Transformation */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            Movement Is Transformation
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <blockquote className="editorial-pullquote">
              "Coaching should be accessible, and community should elevate rather than merely include."
            </blockquote>
            <p>
              Movement is transformation, but not the kind that fits into before-and-after pictures. It's a slow shift in posture, breath, patience, nervous system — a return to craft.
            </p>
            <p>
              Athletic development is holistic, and culture and heritage aren't add-ons to performance; they shape it. The track is cultural ground.
            </p>
          </div>

          {/* Section: One Universe */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            One Universe
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              PAU Track Running is the craft in motion; PAU Magazine is the language around it. The track trains the body, the magazine trains the gaze. Two expressions, one commitment: reclaim what belongs to everyone.
            </p>
          </div>

          {/* Section: A Commons */}
          <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
            A Commons
          </h3>
          <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
            <p>
              Movement belongs to the people. What's new is how thoroughly it has been segmented — split into categories, price points, identities, and rooms you're meant to stay inside.
            </p>
            <p>
              We're not interested in those splits. We're interested in a commons: a place where performance and wellbeing meet, where heritage and innovation move in the same stride.
            </p>
          </div>

          {/* Full Width Image Before Closing */}
          <figure className="my-12 -mx-6 md:-mx-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_new-pau-site/artifacts/975bolml__A7A8396.jpg"
              alt="Athlete at sunset ocean"
              className="w-full h-auto"
            />
          </figure>

          {/* Closing Statement */}
          <div className="mt-16 pt-12 border-t border-[#1A1714]/10 text-center">
            <p className="editorial-headline text-3xl md:text-4xl lg:text-5xl text-[#1A1714] leading-[1.1] mb-6">
              This dimension is a playground.
            </p>
            <p className="editorial-body text-xl text-[#1A1714]/60 max-w-xl mx-auto">
              PAU is an invitation to step inside it, as part of a universe in motion.
            </p>
          </div>

          {/* Back Navigation */}
          <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
          </div>

        </div>
      </article>
    </main>
    <Footer />
  </>
);

// ============================================
// EVENTS PAGE
// ============================================
const EventsPage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data) {
        setSubscribed(true);
        setEmail('');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Newsletter subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">PAU IN PUBLIC</p>
          <h1 className="font-headline text-4xl md:text-6xl text-[#E8E4DC] uppercase mb-4">
            Sessions are the foundation.<br />
            <span className="text-[#E8E4DC]/55">Events are the ceremony.</span>
          </h1>
          <p className="text-[#E8E4DC]/55 mb-12">
            Workshops, collaborations, pop-ups, races, and community moments.
          </p>

          <div className="flex flex-wrap gap-4 mb-16">
            {["Open to everyone", "Member priority", "First come, first served"].map((tag) => (
              <span key={tag} className="px-4 py-2 border border-white/20 text-[#E8E4DC]/65 text-sm rounded-full">
                {tag}
              </span>
            ))}
          </div>

          {/* Featured Event - Dakar 2026 */}
          <div className="relative grid lg:grid-cols-2 gap-8 bg-[#252220] rounded-2xl border border-[#3A3633] overflow-hidden mb-12" data-testid="event-dakar">
            {/* BOOKED OUT Stamp */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 -rotate-12">
              <div className="border-4 border-[#E8E4DC] px-8 py-4 rounded-lg bg-[#1A1714]/90">
                <p className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase tracking-wider">Booked Out</p>
              </div>
            </div>
            
            <div className="relative h-64 lg:h-auto min-h-[300px] opacity-60">
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/699b90e604d230bcd964643f/e4f04f5c9_WhatsAppImage2026-01-27at11052AM.jpg"
                alt="Track training in Dakar"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-[#1A1714]/80 px-3 py-1 text-xs text-[#E8E4DC]/80 uppercase tracking-wider rounded-full">
                Training Camp
              </div>
            </div>
            
            <div className="p-8 lg:p-12 opacity-60">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Featured Event</p>
              <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-6">DAKAR 2026</h2>
              <p className="text-[#E8E4DC]/65 leading-relaxed mb-8">
                Join PAU in Dakar for an intensive training block built around speed, endurance, 
                holistic performance, and cultural exchange. Sessions at Stade Léopold Sédar Senghor. 
                Structured track work, recovery support, workshops, and real community on and off the track.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                {[
                  { label: "Date", value: "15 Apr – 3 May" },
                  { label: "Location", value: "Dakar, Senegal" },
                ].map((detail) => (
                  <div key={detail.label}>
                    <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-wider mb-1">{detail.label}</p>
                    <p className="text-[#E8E4DC] font-medium">{detail.value}</p>
                  </div>
                ))}
              </div>

              <p className="text-[#E8E4DC]/50 text-sm">
                This event is fully booked.
              </p>
            </div>
          </div>

          {/* Get Notified Section */}
          <div className="py-16 border-t border-[#3A3633]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <p className="text-[#E8E4DC]/50 text-sm mb-2">More events coming soon.</p>
                <p className="text-[#E8E4DC]/80 text-xl">
                  Get notified about upcoming events.
                </p>
              </div>
              
              {subscribed ? (
                <div className="flex items-center gap-3 text-[#8B9A7D]">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>You're on the list. We'll keep you posted.</span>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <form onSubmit={handleSubscribe} className="flex gap-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email"
                      required
                      className="px-6 py-4 bg-transparent border border-[#3A3633] rounded-full text-[#E8E4DC] placeholder-[#E8E4DC]/40 focus:outline-none focus:border-[#8B9A7D] transition-colors w-64"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 bg-[#8B9A7D] text-[#1A1714] px-8 py-4 rounded-full font-medium hover:bg-[#6B7A5D] transition-colors disabled:opacity-50"
                    >
                      {loading ? 'SENDING...' : 'NOTIFY ME'}
                    </button>
                  </form>
                  {error && <p className="text-red-400 text-sm">{error}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
  );
};

// ============================================
// GOODS PAGE
// ============================================
const GoodsPage = () => {
  const shopifyInitialized = useRef(false);
  
  // Load Shopify Buy Button script
  useEffect(() => {
    // Skip if already initialized
    if (shopifyInitialized.current) return;
    
    const scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
    
    function ShopifyBuyInit() {
      if (shopifyInitialized.current) return;
      
      if (window.ShopifyBuy && window.ShopifyBuy.UI) {
        const client = window.ShopifyBuy.buildClient({
          domain: 'es0zcv-6c.myshopify.com',
          storefrontAccessToken: 'c09fe5a9cf89e25f78985a9f364f36eb',
        });
        
        window.ShopifyBuy.UI.onReady(client).then(function (ui) {
          if (shopifyInitialized.current) return;
          
          const node = document.getElementById('collection-component-1773355871989');
          if (!node) return;
          
          shopifyInitialized.current = true;
          
          ui.createComponent('collection', {
            id: '692161675639',
            node: node,
            moneyFormat: 'CHF%20%7B%7Bamount%7D%7D',
            options: {
              "collection": {
                "contents": {
                  "products": true
                },
                "productCount": 4
              },
              "product": {
                "styles": {
                  "product": {
                    "@media (min-width: 601px)": {
                      "max-width": "calc(50% - 40px)",
                      "margin-left": "40px",
                      "margin-bottom": "50px",
                      "width": "calc(50% - 40px)"
                    },
                    "img": {
                      "height": "calc(100% - 15px)",
                      "position": "absolute",
                      "left": "0",
                      "right": "0",
                      "top": "0"
                    },
                    "imgWrapper": {
                      "padding-top": "calc(75% + 15px)",
                      "position": "relative",
                      "height": "0"
                    }
                  },
                  "title": {
                    "font-family": "Montserrat, sans-serif",
                    "font-size": "20px",
                    "color": "#E8E4DC"
                  },
                  "button": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "16px",
                    "padding-top": "16px",
                    "padding-bottom": "16px",
                    "color": "#1A1714",
                    ":hover": {
                      "color": "#1A1714",
                      "background-color": "#6B7A5D"
                    },
                    "background-color": "#8B9A7D",
                    ":focus": {
                      "background-color": "#6B7A5D"
                    },
                    "border-radius": "40px",
                    "padding-left": "25px",
                    "padding-right": "25px"
                  },
                  "quantityInput": {
                    "font-size": "16px",
                    "padding-top": "16px",
                    "padding-bottom": "16px"
                  },
                  "price": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "15px",
                    "color": "#E8E4DC"
                  },
                  "compareAt": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "12.75px",
                    "color": "#E8E4DC"
                  },
                  "unitPrice": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "12.75px",
                    "color": "#E8E4DC"
                  }
                },
                "buttonDestination": "modal",
                "contents": {
                  "options": false
                },
                "text": {
                  "button": "View product"
                },
                "googleFonts": [
                  "Montserrat"
                ]
              },
              "productSet": {
                "styles": {
                  "products": {
                    "@media (min-width: 601px)": {
                      "margin-left": "-40px"
                    }
                  }
                }
              },
              "modalProduct": {
                "contents": {
                  "img": false,
                  "imgWithCarousel": true,
                  "button": false,
                  "buttonWithQuantity": true
                },
                "styles": {
                  "product": {
                    "@media (min-width: 601px)": {
                      "max-width": "100%",
                      "margin-left": "0px",
                      "margin-bottom": "0px"
                    }
                  },
                  "button": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "16px",
                    "padding-top": "16px",
                    "padding-bottom": "16px",
                    "color": "#1A1714",
                    ":hover": {
                      "color": "#1A1714",
                      "background-color": "#6B7A5D"
                    },
                    "background-color": "#8B9A7D",
                    ":focus": {
                      "background-color": "#6B7A5D"
                    },
                    "border-radius": "40px",
                    "padding-left": "25px",
                    "padding-right": "25px"
                  },
                  "quantityInput": {
                    "font-size": "16px",
                    "padding-top": "16px",
                    "padding-bottom": "16px"
                  },
                  "title": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "26px",
                    "color": "#1A1714"
                  },
                  "price": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "21px",
                    "color": "#1A1714"
                  },
                  "compareAt": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "17.85px",
                    "color": "#1A1714"
                  },
                  "unitPrice": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "17.85px",
                    "color": "#1A1714"
                  },
                  "description": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "15px",
                    "color": "#1A1714"
                  }
                },
                "googleFonts": [
                  "Montserrat"
                ],
                "text": {
                  "button": "Add to cart"
                }
              },
              "modal": {
                "styles": {
                  "modal": {
                    "background-color": "#8b9a7d"
                  }
                }
              },
              "option": {
                "styles": {
                  "label": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold"
                  },
                  "select": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold"
                  }
                },
                "googleFonts": [
                  "Montserrat"
                ]
              },
              "cart": {
                "styles": {
                  "button": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "font-size": "16px",
                    "padding-top": "16px",
                    "padding-bottom": "16px",
                    "color": "#1A1714",
                    ":hover": {
                      "color": "#1A1714",
                      "background-color": "#6B7A5D"
                    },
                    "background-color": "#8B9A7D",
                    ":focus": {
                      "background-color": "#6B7A5D"
                    },
                    "border-radius": "40px"
                  },
                  "title": {
                    "color": "#1A1714"
                  },
                  "header": {
                    "color": "#1A1714"
                  },
                  "lineItems": {
                    "color": "#1A1714"
                  },
                  "subtotalText": {
                    "color": "#1A1714"
                  },
                  "subtotal": {
                    "color": "#1A1714"
                  },
                  "notice": {
                    "color": "#1A1714"
                  },
                  "currency": {
                    "color": "#1A1714"
                  },
                  "close": {
                    "color": "#1A1714",
                    ":hover": {
                      "color": "#1A1714"
                    }
                  },
                  "empty": {
                    "color": "#1A1714"
                  },
                  "noteDescription": {
                    "color": "#1A1714"
                  },
                  "discountText": {
                    "color": "#1A1714"
                  },
                  "discountIcon": {
                    "fill": "#1A1714"
                  },
                  "discountAmount": {
                    "color": "#1A1714"
                  },
                  "cart": {
                    "background-color": "#8b9a7d"
                  },
                  "footer": {
                    "background-color": "#8b9a7d"
                  }
                },
                "text": {
                  "total": "Subtotal",
                  "button": "Checkout"
                },
                "contents": {
                  "note": true
                },
                "googleFonts": [
                  "Montserrat"
                ]
              },
              "toggle": {
                "styles": {
                  "toggle": {
                    "font-family": "Montserrat, sans-serif",
                    "font-weight": "bold",
                    "background-color": "#8B9A7D",
                    ":hover": {
                      "background-color": "#6B7A5D"
                    },
                    ":focus": {
                      "background-color": "#6B7A5D"
                    }
                  },
                  "count": {
                    "font-size": "16px",
                    "color": "#1A1714",
                    ":hover": {
                      "color": "#1A1714"
                    }
                  },
                  "iconPath": {
                    "fill": "#1A1714"
                  }
                },
                "googleFonts": [
                  "Montserrat"
                ]
              },
              "lineItem": {
                "styles": {
                  "variantTitle": {
                    "color": "#1A1714"
                  },
                  "title": {
                    "color": "#1A1714"
                  },
                  "price": {
                    "color": "#1A1714"
                  },
                  "fullPrice": {
                    "color": "#1A1714"
                  },
                  "discount": {
                    "color": "#1A1714"
                  },
                  "discountIcon": {
                    "fill": "#1A1714"
                  },
                  "quantity": {
                    "color": "#1A1714"
                  },
                  "quantityIncrement": {
                    "color": "#1A1714",
                    "border-color": "#1A1714"
                  },
                  "quantityDecrement": {
                    "color": "#1A1714",
                    "border-color": "#1A1714"
                  },
                  "quantityInput": {
                    "color": "#1A1714",
                    "border-color": "#1A1714"
                  }
                }
              }
            },
          });
        });
      }
    }

    function loadScript() {
      const script = document.createElement('script');
      script.async = true;
      script.src = scriptURL;
      script.onload = ShopifyBuyInit;
      document.head.appendChild(script);
    }

    if (window.ShopifyBuy) {
      if (window.ShopifyBuy.UI) {
        ShopifyBuyInit();
      } else {
        loadScript();
      }
    } else {
      loadScript();
    }
  }, []);

  return (
    <>
      <Navigation />
      <main className="pt-24 bg-[#1A1714] min-h-screen">
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">GOODS</p>
            <h1 className="font-headline text-4xl md:text-6xl text-[#E8E4DC] uppercase mb-6">
              Wear The Work.
            </h1>
            <p className="text-[#E8E4DC]/55 text-xl max-w-md mb-12">
              Gear that moves with purpose.
            </p>
            
            {/* Shopify Collection */}
            <div id="collection-component-1773355871989" className="min-h-[400px]"></div>

            {/* Brand Collaboration CTA */}
            <div className="mt-16 pt-16 border-t border-[#3A3633] text-center">
              <p className="text-[#E8E4DC]/70 text-lg max-w-2xl mx-auto mb-6">
                We're a track club, and we want our athletes to feel good — on and off the track. 
                If you're a brand interested in collaborating on apparel or gear, we'd love to hear from you.
              </p>
              <a 
                href="mailto:active@pauuniverse.com?subject=Brand Collaboration Inquiry"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors"
              >
                Reach out <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// PARTNERS PAGE
// ============================================
const PartnersPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Partners</p>
          <h1 className="font-headline text-4xl md:text-6xl text-[#E8E4DC] uppercase mb-6">
            Partner with PAU
          </h1>
          <p className="text-[#E8E4DC]/65 text-xl max-w-2xl mb-4">
            Paid brand collaborations: campaigns, content, and product projects.
          </p>
          
          {/* Key Statement */}
          <div className="p-6 bg-[#8B9A7D]/10 border border-[#8B9A7D]/20 mb-8 max-w-2xl">
            <p className="text-[#E8E4DC] font-medium">
              <strong>For brands and agencies:</strong> paid collaborations with clear deliverables, timelines, and usage rights.
            </p>
          </div>
          
          {/* SEO Intro */}
          <p className="text-[#E8E4DC]/55 text-lg max-w-3xl mb-12">
            PAU partners with brands seeking authentic connections to athletics, culture, and storytelling. Our collaborations span editorial content, training access, athlete visibility, and creative production. We work with sports brands, lifestyle companies, and agencies looking for real athletes in real conditions — not staged campaigns.
          </p>

          {/* What Partners Get */}
          <div className="mb-16 p-10 md:p-14 bg-[#252220] rounded-2xl border border-[#3A3633]">
            <h2 className="text-sm text-[#E8E4DC]/70 uppercase tracking-[0.3em] mb-8">What Partners Get</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <span className="text-[#8B9A7D] text-xl">→</span>
                <div>
                  <h3 className="text-[#E8E4DC] font-medium mb-1">Content deliverables</h3>
                  <p className="text-[#E8E4DC]/55">Editorial + short-form content. Photo, video, written — depending on scope.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#8B9A7D] text-xl">→</span>
                <div>
                  <h3 className="text-[#E8E4DC] font-medium mb-1">Usage rights</h3>
                  <p className="text-[#E8E4DC]/55">As agreed. Defined scope, duration, and channels.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#8B9A7D] text-xl">→</span>
                <div>
                  <h3 className="text-[#E8E4DC] font-medium mb-1">Access moments</h3>
                  <p className="text-[#E8E4DC]/55">Camp, training, competition windows. Real environments, real athletes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#8B9A7D] text-xl">→</span>
                <div>
                  <h3 className="text-[#E8E4DC] font-medium mb-1">Reporting</h3>
                  <p className="text-[#E8E4DC]/55">Basic performance + recap. What was delivered, how it performed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Two Dimensions */}
          <h2 className="text-sm text-[#E8E4DC]/70 uppercase tracking-[0.3em] mb-6">PAU Dimensions</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">Dimension 01</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-3">Trackrunning</h3>
              <p className="text-[#E8E4DC]/55 mb-4">
                A Zürich-based track club. Three sessions per week, structured programming, 
                and a supportive community for athletes. Real training environments for real content.
              </p>
              <a href="/trackrunning" className="text-[#8B9A7D] text-sm hover:text-[#E8E4DC] transition-colors">
                Learn more →
              </a>
            </div>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">Dimension 02</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-3">PAUMAG</h3>
              <p className="text-[#E8E4DC]/55 mb-4">
                Stories, photography, and cultural perspective from the world of movement. 
                Editorial production that treats athletes as cultural subjects.
              </p>
              <a href="/magazine" className="text-[#8B9A7D] text-sm hover:text-[#E8E4DC] transition-colors">
                Read PAUMAG →
              </a>
            </div>
          </div>

          {/* Partnership Formats */}
          <div className="mb-16">
            <h2 className="text-sm text-[#E8E4DC]/70 uppercase tracking-[0.3em] mb-8">Partnership Formats</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { num: "01", title: "Integrated Content", desc: "Training life, race weekends, editorial features. Your brand woven into authentic narrative." },
                { num: "02", title: "Product Collaboration", desc: "Real athletes, real conditions. Authentic feedback and creative partnerships." },
                { num: "03", title: "Sponsored Series", desc: "Clearly disclosed editorial or training series. Your name on meaningful content." },
                { num: "04", title: "Visual Projects", desc: "Collaborations that push creative boundaries. Magazine-quality storytelling." },
                { num: "05", title: "Camp Access", desc: "Brand presence at training camps. Content opportunities with elite athletes." },
                { num: "06", title: "Events", desc: "Title sponsorship for PAU events—bringing the community together." },
              ].map((format) => (
                <div key={format.num} className="p-6 bg-[#252220] rounded-2xl border border-[#3A3633] hover:border-[#8B9A7D]/30 transition-colors">
                  <p className="text-[#8B9A7D] text-sm mb-2">{format.num}</p>
                  <h3 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">{format.title}</h3>
                  <p className="text-[#E8E4DC]/50 text-sm">{format.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16 p-10 bg-[#1A1714] border border-[#3A3633] rounded-2xl">
            <h2 className="text-sm text-[#E8E4DC]/70 uppercase tracking-[0.3em] mb-8">FAQ — Partnerships</h2>
            <div className="space-y-6">
              <div className="border-b border-[#3A3633] pb-6">
                <h3 className="text-[#E8E4DC] font-medium mb-2">What are typical timelines?</h3>
                <p className="text-[#E8E4DC]/55">Depends on scope. Simple content collaborations: 2–4 weeks. Larger campaigns or camp access: 2–3 months lead time.</p>
              </div>
              <div className="border-b border-[#3A3633] pb-6">
                <h3 className="text-[#E8E4DC] font-medium mb-2">What deliverables are included?</h3>
                <p className="text-[#E8E4DC]/55">Defined in advance. Typically: photo/video assets, editorial coverage, social content. All specified in the agreement.</p>
              </div>
              <div className="border-b border-[#3A3633] pb-6">
                <h3 className="text-[#E8E4DC] font-medium mb-2">What budgets do you work with?</h3>
                <p className="text-[#E8E4DC]/55">We work across ranges. Share your goals and budget, and we'll tell you what's possible.</p>
              </div>
              <div className="border-b border-[#3A3633] pb-6">
                <h3 className="text-[#E8E4DC] font-medium mb-2">How do we get started?</h3>
                <p className="text-[#E8E4DC]/55">Email active@pauuniverse.com with your brand, goals, and timeline. We respond within 48 hours.</p>
              </div>
            </div>
          </div>

          {/* Current Priority Projects */}
          <div className="mb-16">
            <h2 className="text-sm text-[#E8E4DC]/70 uppercase tracking-[0.3em] mb-8">Current Priority Projects</h2>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <div className="grid lg:grid-cols-[1fr,auto] gap-8 items-start">
                <div>
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-3">Partnership Opportunity</p>
                  <h3 className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase mb-4">
                    Support Youth Olympics in Dakar
                  </h3>
                  <p className="text-[#E8E4DC]/65 text-lg leading-relaxed mb-6">
                    PAU supports athletes and programs that create real pathways to elite sport. This project focuses on enabling participation and preparation around the Youth Olympics in Dakar—through training, logistics, and on-the-ground support.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to="/magazine/olympic-retreat-dakar"
                      className="inline-flex items-center gap-2 bg-[#E8E4DC] text-black px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                    >
                      Read the project <ArrowUpRight size={16} />
                    </Link>
                    <a
                      href="#partner-cta"
                      className="inline-flex items-center gap-2 border border-[#8B9A7D] text-[#8B9A7D] px-6 py-3 rounded-full font-medium hover:bg-[#8B9A7D]/10 transition-colors"
                    >
                      Partner with PAU
                    </a>
                  </div>
                </div>
                <div className="hidden lg:block w-48 h-48 bg-[#1A1714] rounded-xl overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/bv0injy9_WhatsApp%20Image%202026-05-11%20at%205.07.07%E2%80%AFPM.jpeg"
                    alt="Youth Olympics Dakar 2026"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div id="partner-cta" className="text-center">
            <p className="text-[#E8E4DC]/70 text-lg mb-8">
              Interested in collaborating? Get in touch directly.
            </p>
            <a
              href="mailto:active@pauuniverse.com?subject=Partnership Inquiry"
              className="inline-flex items-center gap-2 bg-[#E8E4DC] text-black px-8 py-4 rounded-full font-medium hover:bg-white transition-colors"
            >
              Get in Touch <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

// Keep SponsorsPage as alias for backwards compatibility
const SponsorsPage = PartnersPage;

// ============================================
// WHY PAU PAGE
// ============================================
const WhyPauPage = () => (
  <>
    <Navigation />
    <main className="pt-0 bg-[#1A1714] min-h-screen">
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center px-6 md:px-10 pt-32 pb-24">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/why-pau-hero.jpg')" }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1714]/70 via-[#1A1714]/60 to-[#1A1714]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">Why PAU Exists</p>
          <h1 className="font-headline text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#E8E4DC] uppercase leading-[0.9] mb-8">
            This Dimension<br />Is A Playground
          </h1>
          <p className="text-[#E8E4DC]/70 text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed">
            Where movement, culture, and community come together. A track club. A magazine. One universe.
          </p>
        </div>
      </section>

      {/* The Founding Idea */}
      <section className="py-24 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">The Founding Idea</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
            Two worlds. One universe.
          </h2>
          <div className="space-y-6 text-[#E8E4DC]/70 text-lg leading-relaxed">
            <p>
              PAU began with a simple belief: performance can be serious without becoming narrow, and human without losing its edge.
            </p>
            <p>
              We wanted to build something that merged the depth of a real track club with the openness and cultural richness of a magazine. A place where training and storytelling live side by side. Where the body and the mind both have room to grow.
            </p>
            <p>
              Athletics is one of the clearest foundations of human movement. It teaches rhythm, coordination, posture, and self-knowledge. But too often, the culture around it stays closed. PAU exists to help open that field again—not by lowering standards, but by widening what's possible.
            </p>
          </div>
        </div>
      </section>

      {/* Inside PAUUNIVERSE */}
      <section className="py-24 px-6 md:px-10 bg-[#252220]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">Inside PAUUNIVERSE</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            The Track. The Magazine.
          </h2>
          <p className="text-[#E8E4DC]/70 text-lg mb-12 max-w-2xl">
            Two dimensions that move together. The track trains the body. The magazine trains the gaze. Both belong to the same universe.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* PAUTRACKRUNNING */}
            <div className="p-8 bg-[#1A1714] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Dimension 01</p>
              <h3 className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase mb-4">PAUTRACKRUNNING</h3>
              <p className="text-[#E8E4DC]/70 leading-relaxed mb-6">
                A Zürich-based track club. Three sessions per week at Sportanlage Fronwald. Structured programming, supportive community, and space to grow at your own pace. Whether you're refining technique or finding your rhythm, the track is open.
              </p>
              <a 
                href="/trackrunning"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors"
              >
                Enter PAUTRACKRUNNING <ArrowRight size={16} />
              </a>
            </div>

            {/* PAUMAG */}
            <div className="p-8 bg-[#1A1714] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Dimension 02</p>
              <h3 className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase mb-4">PAUMAG</h3>
              <p className="text-[#E8E4DC]/70 leading-relaxed mb-6">
                The editorial side of PAUUNIVERSE. Stories, photography, and cultural perspective from the world of movement. We explore what it means to train, to transform, to belong. Where the work becomes language.
              </p>
              <a 
                href="/magazine"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors"
              >
                Explore PAUMAG <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* International Outlook */}
      <section className="py-24 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">International Outlook</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
            Zurich. Kingston. Dakar.
          </h2>
          <p className="text-[#E8E4DC]/70 text-lg leading-relaxed">
            PAU looks toward exchange across Switzerland, Jamaica, Senegal, and beyond. We believe the future of sport depends on stronger bridges between systems, communities, and ways of knowing. Our coaches and collaborators bring perspectives from across the globe.
          </p>
        </div>
      </section>

      {/* Founder Perspective */}
      <section className="py-24 px-6 md:px-10 bg-[#252220]">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">Founder Perspective</p>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Portrait */}
            <div className="order-2 md:order-1">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="/assets/founder-portrait.jpg" 
                  alt="Noé Daouda Ramos - Founder of PAUUNIVERSE" 
                  className="w-full h-full object-cover"
                  data-testid="founder-portrait"
                />
              </div>
              <p className="text-[#E8E4DC]/50 text-sm mt-4">Noé Daouda Ramos · Founder</p>
            </div>
            {/* Text */}
            <div className="order-1 md:order-2">
              <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
                PAU is shaped by a life lived across disciplines and places
              </h2>
              <div className="space-y-6 text-[#E8E4DC]/70 text-lg leading-relaxed">
                <p>
                  As an athlete, I trained and competed across several sports, always close to movement and always learning through the body. That path took me across countries and continents and showed me that sport is always shaped by culture, access, and environment.
                </p>
                <p>
                  I wanted to create a space where training and culture could live together. Where the work on the track and the stories we tell about it would reinforce each other. That's what PAUUNIVERSE is—a home for both.
                </p>
                <p className="text-[#E8E4DC] font-medium">
                  This dimension is a playground. You are welcome inside it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Belief */}
      <section className="py-24 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">What We Believe</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
            Movement belongs to everyone
          </h2>
          
          <div className="space-y-6 text-[#E8E4DC]/70 text-lg leading-relaxed">
            <p>
              Performance and wellbeing aren't separate projects—they're the same practice, approached from different angles. The track becomes a laboratory for how you live.
            </p>
            <p>
              Culture and heritage aren't add-ons to performance; they shape it. The track is cultural ground. The magazine gives it voice.
            </p>
            <p className="text-[#E8E4DC] font-medium">
              We're building a commons: a place where serious training, meaningful stories, and international exchange can move together.
            </p>
          </div>
        </div>
      </section>

      {/* Official Club */}
      <section className="py-24 px-6 md:px-10 bg-[#252220]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">Official Club</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
            Registered with Swiss Athletics
          </h2>
          <div className="space-y-4 text-[#E8E4DC]/70 text-lg leading-relaxed">
            <p>
              PAU is a registered Swiss athletics club based in Zürich. Members can compete under the PAU license, and we guide athletes through the club pathway and licensing steps.
            </p>
            <p className="text-[#E8E4DC]">
              To join or learn more, email active@pauuniverse.com.
            </p>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">The Community</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            Movement. People. Places.
          </h2>
          <p className="text-[#E8E4DC]/70 text-lg mb-12 max-w-2xl">
            From Zürich to Dakar, the PAU community comes together across borders—training, connecting, and building something bigger than any one place.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-gym.jpeg" 
                alt="PAU community training at the gym"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-africa-duo.jpg" 
                alt="PAU members in Africa"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <video 
                src="/assets/community-video.mp4"
                className="w-full h-full object-cover"
                controls
                loop
                playsInline
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-africa-group.jpg" 
                alt="PAU international community gathering"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-weltklasse.jpeg" 
                alt="PAU at Weltklasse Zürich"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-kaex.jpg" 
                alt="PAU x KAEX training session"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-track-group.jpg" 
                alt="PAU track training group"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="/assets/community-event.jpg" 
                alt="PAU community event"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6 md:px-10 border-t border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Train with us */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633] hover:border-[#8B9A7D]/50 transition-colors">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-4">Train with us</h3>
              <p className="text-[#E8E4DC]/60 text-sm mb-6">
                Step onto the track. Three sessions per week in Zürich. All you need is the willingness to show up.
              </p>
              <a 
                href="/trackrunning"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
              >
                Join Training <ArrowRight size={14} />
              </a>
            </div>

            {/* Explore PAUMAG */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633] hover:border-[#8B9A7D]/50 transition-colors">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-4">Read PAUMAG</h3>
              <p className="text-[#E8E4DC]/60 text-sm mb-6">
                Stories, ideas, and cultural perspectives from the world of movement. Where the work becomes language.
              </p>
              <a 
                href="/magazine"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
              >
                Explore Stories <ArrowRight size={14} />
              </a>
            </div>

            {/* Connect with us */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633] hover:border-[#8B9A7D]/50 transition-colors">
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-4">Get in touch</h3>
              <p className="text-[#E8E4DC]/60 text-sm mb-6">
                Questions, collaborations, or just want to say hello? We'd love to hear from you.
              </p>
              <a 
                href="mailto:active@pauuniverse.com"
                className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors text-sm"
              >
                Get in Touch <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

    </main>
    <Footer />
  </>
);

// ============================================
// SPONSORSHIP DECK PAGE
// ============================================
const DeckPage = () => (
  <>
    <Navigation />
    <main className="pt-24 bg-[#1A1714] min-h-screen">
      
      {/* Hero */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">PAU</p>
          <h1 className="font-headline text-5xl md:text-7xl lg:text-8xl text-[#E8E4DC] uppercase mb-6">
            Sponsorship &<br />Partner Deck
          </h1>
          <p className="text-[#E8E4DC]/70 text-xl md:text-2xl mb-8">Zürich-based track club for independent athletes</p>
          <div className="flex flex-wrap gap-4 text-[#E8E4DC]/50 text-sm">
            <span>Swiss Athletics</span>
            <span>·</span>
            <span>Free membership</span>
            <span>·</span>
            <span>Mon / Thu / Sat</span>
          </div>
        </div>
      </section>

      {/* The Club */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-headline text-3xl md:text-5xl text-[#E8E4DC] uppercase mb-6">
            Built for independent athletes
          </h2>
          <p className="text-[#E8E4DC]/70 text-lg max-w-3xl mb-12">
            PAU is a Zürich-based track club for athletes who already know how to train and want the right environment 
            to progress: a strong training culture, reliable infrastructure, a clear weekly programme, and the option 
            to compete under a club license.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Training days", value: "Mon / Thu / Sat" },
              { label: "Location", value: "Sportanlage Fronwald" },
              { label: "Structure", value: "High-agency" },
              { label: "Club", value: "Swiss Athletics" },
            ].map((stat) => (
              <div key={stat.label} className="p-6 bg-[#252220] rounded-2xl border border-[#3A3633]">
                <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-wider mb-2">{stat.label}</p>
                <p className="text-[#E8E4DC] font-medium">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Model */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Training Model</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            Structure without supervision
          </h2>
          <p className="text-[#E8E4DC]/50 text-lg mb-12">Built for high-agency athletes.</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-sm mb-2">01</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-4">Weekly Programme</h3>
              <p className="text-[#E8E4DC]/55">Shared every Sunday evening. Clear structure for the week ahead.</p>
            </div>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-sm mb-2">02</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-4">Athlete-led Sessions</h3>
              <p className="text-[#E8E4DC]/55">Not every session is coached on-site. Athletes form groups by level and discipline.</p>
            </div>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-sm mb-2">03</p>
              <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase mb-4">Long-term Independence</h3>
              <p className="text-[#E8E4DC]/55">The system builds athletes who can own their development.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Design */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Program Design</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            International-level methodology
          </h2>
          <p className="text-[#E8E4DC]/50 text-lg mb-8">Programs designed by experienced coaches.</p>
          <p className="text-[#E8E4DC]/70 text-lg max-w-2xl mb-8">
            Our training programs bring international-level methodology to the club. Connections across 
            Zürich, Kingston, and Dakar.
          </p>
          <div className="flex flex-wrap gap-4 text-[#E8E4DC]/50 text-sm">
            <span className="px-4 py-2 bg-[#252220] rounded-full">Sprint program</span>
            <span className="px-4 py-2 bg-[#252220] rounded-full">Endurance program</span>
            <span className="px-4 py-2 bg-[#252220] rounded-full">Strength training (partner offer)</span>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Who We Are</p>
          <h2 className="font-headline text-3xl md:text-5xl text-[#E8E4DC] uppercase mb-8">
            This dimension is a playground.
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6 text-[#E8E4DC]/70">
              <p>
                PAU is designed for high-agency athletes who value structure and community—without needing 
                constant supervision. We provide the infrastructure, the programme, and the culture. 
                Athletes bring their own drive.
              </p>
              <p>
                Support remains available, and coaching is present regularly—but the system is intentionally 
                built to develop long-term independence and consistency.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Membership", value: "Free" },
                { label: "Sessions", value: "3x per week" },
                { label: "Member club", value: "Swiss Athletics" },
                { label: "Competition", value: "Club license option" },
              ].map((stat) => (
                <div key={stat.label} className="p-5 bg-[#252220] rounded-xl border border-[#3A3633]">
                  <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className="text-[#E8E4DC] font-medium">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Vision */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Partnership Value</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            Support independent athletes
          </h2>
          <p className="text-[#E8E4DC]/50 text-lg mb-12">Real athletes. Real training. Real environment.</p>
          <p className="text-[#E8E4DC]/70 text-lg max-w-3xl mb-12">
            Partners who work with PAU support a model built for athletes who already know how to train. 
            Your brand aligns with structure, independence, and long-term athletic development.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] font-medium mb-2">01</p>
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Authentic environment</h3>
              <p className="text-[#E8E4DC]/55">High-agency athletes training with intention. No ego, just work.</p>
            </div>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] font-medium mb-2">02</p>
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">Editorial platform</h3>
              <p className="text-[#E8E4DC]/55">PAUMAG provides stories and cultural perspective from the world of track.</p>
            </div>
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] font-medium mb-2">03</p>
              <h3 className="font-headline text-xl text-[#E8E4DC] uppercase mb-3">International reach</h3>
              <p className="text-[#E8E4DC]/55">Connections across Zürich, Kingston, and Dakar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Background Direction - Foundation */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633] bg-[#8B9A7D]/5">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Long-term Direction</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-8">
            Building infrastructure for independent athletes
          </h2>
          <div className="max-w-3xl space-y-6 text-[#E8E4DC]/70 text-lg">
            <p>
              Long-term, PAU aims to support independent athletes and coaches who need infrastructure, 
              not just exposure. Our model is built around high-agency athletes who value structure and 
              community—without constant supervision.
            </p>
            <p>
              Partners who invest in PAU invest in a system designed to develop long-term independence 
              and consistency in athletic development.
            </p>
          </div>
        </div>
      </section>

      {/* The Platform - Two Dimensions */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">The Platform</p>
          <h2 className="font-headline text-3xl md:text-5xl text-[#E8E4DC] uppercase mb-4">
            Two dimensions.
          </h2>
          <p className="text-[#E8E4DC]/50 text-2xl mb-12">One universe.</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Dimension 01 */}
            <div className="p-8 md:p-10 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Dimension 01</p>
              <h3 className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase mb-2">PAUTRACKRUNNING</h3>
              <p className="text-[#E8E4DC]/50 mb-6">The Work</p>
              <p className="text-[#E8E4DC]/70 mb-6">
                A Zürich-based track club for independent athletes. Three sessions per week at Sportanlage Fronwald. 
                Structured programming from international-level coaches. Built for athletes who already know how to train.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Mon / Thu / Sat", "Weekly programme", "Athlete-led sessions", "Swiss Athletics license"].map((item) => (
                  <span key={item} className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">{item}</span>
                ))}
              </div>
            </div>

            {/* Dimension 02 */}
            <div className="p-8 md:p-10 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Dimension 02</p>
              <h3 className="font-headline text-2xl md:text-3xl text-[#E8E4DC] uppercase mb-2">PAUMAG</h3>
              <p className="text-[#E8E4DC]/50 mb-6">The Signal</p>
              <p className="text-[#E8E4DC]/70 mb-6">
                Stories, philosophy, and cultural perspective from the world of track and field. 
                Where the work becomes language.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Field reports", "Photo essays", "International stories", "Dakar 2026 coverage"].map((item) => (
                  <span key={item} className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">{item}</span>
                ))}
              </div>
            </div>
          </div>
          
          <p className="text-[#E8E4DC]/50 text-center mt-12">
            Together, they create a clean platform for partners who support independent athletes and authentic athletic culture.
          </p>
        </div>
      </section>

      {/* International Program */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">International Program</p>
          <h2 className="font-headline text-3xl md:text-5xl text-[#E8E4DC] uppercase mb-4">
            Zurich. Kingston. Dakar.
          </h2>
          <p className="text-[#E8E4DC]/50 text-xl mb-12">One network.</p>
          <p className="text-[#E8E4DC]/70 text-lg max-w-3xl mb-12">
            PAU is building a genuine international corridor — connecting Swiss athletes to the world 
            and bringing world-class talent to compete on the European circuit.
          </p>
          
          <div className="space-y-8">
            {/* Jamaica */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <div className="flex items-start gap-4 mb-4">
                <span className="text-3xl">🇯🇲</span>
                <div>
                  <p className="text-[#E8E4DC]/50 text-sm">Jamaica</p>
                  <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase">SWEPT Partnership</h3>
                </div>
              </div>
              <p className="text-[#E8E4DC]/70 mb-6">
                PAU has formalised a strategic partnership with SWEPT Track & Field Club, Kingston. Head Coach Okeile Stewart — 
                who has developed a World Junior Record holder and multiple national champions — brings Olympic-level coaching 
                methodology directly into the PAU system.
              </p>
            </div>

            {/* Cham / Switzerland */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <div className="flex items-start gap-4 mb-4">
                <span className="text-3xl">🇨🇭</span>
                <div>
                  <p className="text-[#E8E4DC]/50 text-sm">Cham, Switzerland</p>
                  <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase">Switzerland Training Camp</h3>
                </div>
              </div>
              <p className="text-[#E8E4DC]/70 mb-6">
                Each summer, SWEPT athletes travel to Cham to compete in the European track circuit under the PAU umbrella. 
                PAU organises their full stay: accommodation at OYM, competition access, training facilities, and integration into our community.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">Full logistics handled by PAU</span>
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">Selected athletes race under PAU colours</span>
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">African athlete pipeline to European circuit</span>
              </div>
            </div>

            {/* Senegal */}
            <div className="p-8 bg-[#252220] rounded-2xl border border-[#3A3633]">
              <div className="flex items-start gap-4 mb-4">
                <span className="text-3xl">🇸🇳</span>
                <div>
                  <p className="text-[#E8E4DC]/50 text-sm">Senegal / West Africa</p>
                  <h3 className="font-headline text-2xl text-[#E8E4DC] uppercase">Senegal Training Camp</h3>
                </div>
              </div>
              <p className="text-[#E8E4DC]/70 mb-6">
                PAU is establishing a training camp in Senegal, opening access to Swiss athletes and building a lasting bridge 
                with West African track talent. Senegal hosts the Dakar 2026 Youth Olympics — we are positioning PAU as a connector 
                between African athletes and the European circuit ahead of that moment.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">Swiss athletes train in Dakar</span>
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">West African athlete pipeline to Europe</span>
                <span className="px-3 py-1 bg-[#1A1714] text-[#E8E4DC]/60 text-sm rounded-full">Dakar 2026 Youth Olympics connection</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dakar 2026 */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633] bg-[#8B9A7D]/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-[#8B9A7D] text-4xl mb-4">◈</p>
          <h2 className="font-headline text-3xl md:text-5xl text-[#E8E4DC] uppercase mb-6">
            Dakar 2026 — Youth Olympic Games
          </h2>
          <p className="text-[#E8E4DC]/70 text-lg max-w-2xl mx-auto">
            The Youth Olympic Games come to Africa for the first time. PAU is building its international infrastructure 
            specifically around this moment — positioning our athletes and partners at the intersection of African track 
            culture and the global stage.
          </p>
        </div>
      </section>

      {/* What Partnerships Enable */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">What Partnerships Enable</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            You fund the engine.
          </h2>
          <p className="text-[#E8E4DC]/50 text-xl mb-12">We build the output.</p>
          <p className="text-[#E8E4DC]/70 text-lg max-w-3xl mb-12">
            Partnerships keep PAUTRACKRUNNING and PAUMAG moving. They fund the operations, the production, and the access. 
            The work stays consistent and the standards stay high.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4">
              <span className="text-[#8B9A7D] text-xl">→</span>
              <div>
                <h4 className="text-[#E8E4DC] font-medium mb-1">Consistent training & coaching</h4>
                <p className="text-[#E8E4DC]/55">Structured sessions, qualified coaches, real progression.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="text-[#8B9A7D] text-xl">→</span>
              <div>
                <h4 className="text-[#E8E4DC] font-medium mb-1">Editorial production</h4>
                <p className="text-[#E8E4DC]/55">Stories, visuals, projects. PAUMAG and beyond.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="text-[#8B9A7D] text-xl">→</span>
              <div>
                <h4 className="text-[#E8E4DC] font-medium mb-1">Access initiatives</h4>
                <p className="text-[#E8E4DC]/55">Keeping the work reachable. Ultimately free for all athletes.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="text-[#8B9A7D] text-xl">→</span>
              <div>
                <h4 className="text-[#E8E4DC] font-medium mb-1">Events & collaborations</h4>
                <p className="text-[#E8E4DC]/55">Public moments that extend the work into culture.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 justify-center mt-12">
            <span className="px-6 py-3 bg-[#252220] rounded-full text-[#E8E4DC]">PAUTRACKRUNNING</span>
            <span className="px-6 py-3 bg-[#252220] rounded-full text-[#E8E4DC]">PAUMAG</span>
            <span className="px-6 py-3 bg-[#8B9A7D]/20 border border-[#8B9A7D]/40 rounded-full text-[#8B9A7D]">Or Both</span>
          </div>
        </div>
      </section>

      {/* Partnership Formats */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Partnership Formats</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            How we work with partners.
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              { num: "01", title: "Integrated Content", desc: "Training life, race weekends, editorial features. Your brand woven into the authentic narrative." },
              { num: "02", title: "Community Activations", desc: "Sessions, workshops, events. Direct engagement with the PAU community." },
              { num: "03", title: "Product Testing & Feedback", desc: "Real athletes, real conditions. Authentic feedback loops with a diverse community." },
              { num: "04", title: "Sponsored Series", desc: "Clearly disclosed editorial or training series. Your name on meaningful content." },
              { num: "05", title: "Visual & Creative Projects", desc: "Collaborations that push creative boundaries. Magazine-quality storytelling." },
              { num: "06", title: "Events", desc: "Title sponsorship for PAU events including PAUTRACKRUNNING FEST — our annual track meet." },
            ].map((format) => (
              <div key={format.num} className="p-6 bg-[#252220] rounded-2xl border border-[#3A3633] hover:border-[#8B9A7D]/30 transition-colors">
                <p className="text-[#8B9A7D] text-sm mb-2">{format.num}</p>
                <h4 className="font-headline text-lg text-[#E8E4DC] uppercase mb-2">{format.title}</h4>
                <p className="text-[#E8E4DC]/50 text-sm mb-4">{format.desc}</p>
                <a href="mailto:active@pauuniverse.com" className="text-[#8B9A7D] text-sm hover:text-[#E8E4DC] transition-colors">
                  Enquire →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Partners With PAU */}
      <section className="py-16 md:py-20 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-[0.3em] mb-4">Who Partners With PAU</p>
          <p className="text-[#E8E4DC] text-xl md:text-2xl">
            Brands · Patrons · Private Donors · People who back the work
          </p>
        </div>
      </section>

      {/* The Team */}
      <section className="py-16 md:py-24 px-6 md:px-10 border-b border-[#3A3633]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">The Team — Zürich</p>
          <h2 className="font-headline text-3xl md:text-4xl text-[#E8E4DC] uppercase mb-4">
            Professionals.
          </h2>
          <p className="text-[#E8E4DC]/50 text-xl mb-8">Doing it for the love.</p>
          <p className="text-[#E8E4DC]/70 text-lg max-w-3xl mb-12">
            Our coaches are elite professionals in their respective fields — and for the past 2 years, they have been showing up 
            for PAU without compensation. Sponsorship means we can finally pay the people who built this.
          </p>
          
          {/* Team */}
          <div>
            <p className="text-[#E8E4DC]/50 text-xs uppercase tracking-[0.3em] mb-6">Team</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { name: "Miles Singleton", role: "Founder" },
                { name: "Noé Daouda Ramos", role: "Founder" },
                { name: "Bill Achempem", role: "Co-founder" },
                { name: "Nialing Jobarteh", role: "Marketing" },
                { name: "Vanessa Gärtner", role: "Recovery Coach & Marketing" },
                { name: "Ibrahima Suso", role: "Strength & Conditioning" },
                { name: "Philippe Bandi", role: "Program Writer — Endurance" },
                { name: "Okeile Stewart", role: "Program Writer — Sprint" },
                { name: "Hariharan Roy Rasiah", role: "Track & Speed Coach" },
              ].map((person) => (
                <div key={person.name} className="p-4 bg-[#252220] rounded-xl border border-[#3A3633]">
                  <p className="text-[#E8E4DC] font-medium text-sm">{person.name}</p>
                  <p className="text-[#E8E4DC]/50 text-xs">{person.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-4">Get Involved</p>
          <h2 className="font-headline text-4xl md:text-6xl text-[#E8E4DC] uppercase mb-8">
            Collaborate with PAU.
          </h2>
          <p className="text-[#E8E4DC]/50 text-lg mb-8">
            Partnerships · Patronage · Collaborations · Press
          </p>
          <a
            href="mailto:active@pauuniverse.com?subject=Partnership Inquiry"
            className="inline-flex items-center gap-2 bg-[#E8E4DC] text-black px-8 py-4 rounded-full font-medium hover:bg-white transition-colors mb-8"
          >
            Start the conversation <ArrowRight size={18} />
          </a>
          <p className="text-[#E8E4DC]/70 mb-2">active@pauuniverse.com</p>
          
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-[#E8E4DC]/50 text-sm">
            <Link to="/" className="hover:text-[#E8E4DC]">Website</Link>
            <Link to="/trackrunning" className="hover:text-[#E8E4DC]">Trackrunning</Link>
            <Link to="/goods" className="hover:text-[#E8E4DC]">Goods</Link>
            <Link to="/magazine" className="hover:text-[#E8E4DC]">Magazine</Link>
          </div>
          
          <div className="flex justify-center gap-6 mt-6">
            <a href="https://instagram.com/pautrackrunning" target="_blank" rel="noopener noreferrer" className="text-[#E8E4DC]/50 hover:text-[#E8E4DC]">
              @pautrackrunning
            </a>
            <a href="https://instagram.com/paumagazine" target="_blank" rel="noopener noreferrer" className="text-[#E8E4DC]/50 hover:text-[#E8E4DC]">
              @paumagazine
            </a>
          </div>

          <div className="mt-16 pt-16 border-t border-[#3A3633]">
            <p className="font-headline text-2xl text-[#E8E4DC] uppercase">PAU</p>
            <p className="text-[#E8E4DC]/50 text-sm mt-2">This dimension is a playground</p>
          </div>
        </div>
      </section>

    </main>
    <Footer />
  </>
);

// ============================================
// PAU x SWEPT CAMPAIGN PAGE
// ============================================
const SweptCampaignPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        telephone: null,
        inquiry: `[SWEPT Campaign Page] ${formData.message}`
      });
      
      if (response.data) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const athletes = [
    { name: "Roshawn Clarke", credential: "400m hurdler, World Under-20 Record holder, Olympian" },
    { name: "Javon Francis", credential: "Olympian & World Championship relay medalist (400m)" },
    { name: "Jura Levy", credential: "Olympian and experienced international sprinter" },
    { name: "Serena Cole", credential: "World Under-20 champion" },
    { name: "Dejour Russell", credential: "Sprint hurdler, former World Under-20 medalist" },
    { name: "Darion Skerritt", credential: "Antigua & Barbuda 200m Champion" },
    { name: "Jason Lewis", credential: "Rising international sprinter" },
  ];

  return (
    <>
      <Navigation variant="light" />
      <main className="pt-20 editorial-bg min-h-screen">
        
        {/* Header */}
        <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
          <div className="max-w-[900px] mx-auto">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
            
            <div className="flex items-center gap-3 mb-6">
              <span className="px-2 py-1 bg-[#B8352B]/10 text-[#B8352B] text-xs uppercase tracking-wider">Support Project</span>
              <span className="text-[#1A1714]/40 text-xs">·</span>
              <span className="editorial-caption text-[#1A1714]/50">AUG 2026</span>
            </div>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-[#1A1714]/40 text-sm uppercase tracking-wider">PAU</span>
              <span className="text-[#1A1714]/20">×</span>
              <span className="text-[#1A1714]/40 text-sm uppercase tracking-wider">SWEPT</span>
            </div>
            
            <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
              Cham 2026: A European Training Base for Sprint Excellence
            </h1>
            
            <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60">
              Elite Jamaican athletes. One month in Switzerland. We need CHF 100,000 to make it happen.
            </p>
          </div>
        </header>

        {/* Hero Image */}
        <div className="px-6 md:px-10 py-12">
          <div className="max-w-[1100px] mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/ktfs7q9s_Rennbahn-3321.jpg"
                alt="OYM Indoor Track"
                className="w-full h-64 md:h-80 object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/12xhkups_Zimmer-Athleten-1215.jpg"
                alt="OYM Athlete Room"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
            <p className="editorial-caption text-[#1A1714]/50 mt-4">OYM — Official Housing & Infrastructure Partner</p>
          </div>
        </div>

        {/* Article Content */}
        <article className="px-6 md:px-10 pb-20">
          <div className="max-w-[680px] mx-auto">
            
            {/* Lead */}
            <p className="editorial-body text-2xl md:text-3xl text-[#1A1714] leading-relaxed mb-12">
              In August 2026, a delegation of elite Jamaican and international athletes will arrive in Cham, Switzerland to establish a European training and competition base. Preparation for the Commonwealth Games in Scotland. One month of focused work.
            </p>

            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              <p>
                SWEPT Track and Field Club — based in Kingston, Jamaica — has built a reputation for developing world-class sprinters and hurdlers. Their athletes have competed at Olympic Games, World Championships, and Commonwealth Games.
              </p>
              <p>
                PAU's role: we provide the storytelling, the network, and the platform to connect this project with people who believe in it. This page is an open call for support.
              </p>
            </div>

            {/* The Budget */}
            <div className="my-16 p-8 bg-[#1A1714] text-[#E8E4DC] rounded-none">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">The Budget</p>
              <p className="font-headline text-5xl md:text-6xl uppercase mb-6">CHF 100,000</p>
              <p className="text-[#E8E4DC]/70 mb-8">What it covers:</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Housing & Infrastructure (OYM)</span>
                  <span>~CHF 46,000</span>
                </div>
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Athlete Flights</span>
                  <span>~CHF 15,000</span>
                </div>
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Food & Daily Expenses</span>
                  <span>~CHF 12,000</span>
                </div>
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Staff & Content Team</span>
                  <span>~CHF 15,000</span>
                </div>
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Training & Competition</span>
                  <span>~CHF 7,000</span>
                </div>
                <div className="flex justify-between border-b border-[#E8E4DC]/20 pb-2">
                  <span className="text-[#E8E4DC]/70">Pocket Money & Contingency</span>
                  <span>~CHF 5,000</span>
                </div>
              </div>
              <p className="text-[#E8E4DC]/50 text-xs mt-6">7 athletes + coaching staff + content team. Full month of August 2026.</p>
            </div>

            {/* OYM Section */}
            <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
              OYM Cham — Housing & Infrastructure
            </h3>
            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              <p>
                We've secured a partnership with OYM in Cham — a world-class athlete facility with indoor track, strength training, recovery spaces, and athlete accommodation. All under one roof, just 30 minutes from Zurich.
              </p>
              <p>
                The deal: CHF 46,000 for 7 people, all-inclusive, for the full month of August. OYM is open to content payment — meaning PAU creates editorial content featuring OYM during the athletes' stay, reducing the cash cost. But we still need to fund the difference.
              </p>
            </div>

            {/* OYM Gallery */}
            <figure className="my-12 -mx-6 md:-mx-16">
              <div className="grid grid-cols-3 gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/u0j0y6ak_Rennbahn-3334.jpg"
                  alt="OYM Training Facility"
                  className="w-full h-40 object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/wq71dgsw_Restaurantbereich-1556.jpg"
                  alt="OYM Recovery Lounge"
                  className="w-full h-40 object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/dntpoeqq_Turf-3223.jpg"
                  alt="OYM Stadium"
                  className="w-full h-40 object-cover"
                />
              </div>
            </figure>

            {/* The Athletes */}
            <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
              The Athletes
            </h3>
            <p className="editorial-body text-lg text-[#1A1714]/80 mb-8">
              Cham 2026 delegation — elite athletes preparing for the Commonwealth Games in Scotland.
            </p>
            
            <div className="space-y-4 mb-12">
              {athletes.map((athlete, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-[#1A1714]/10">
                  <span className="text-[#1A1714] font-medium">{athlete.name}</span>
                  <span className="text-[#1A1714]/50 text-sm">{athlete.credential}</span>
                </div>
              ))}
            </div>

            <p className="editorial-body text-lg text-[#1A1714]/80">
              Plus coaching staff (Head Coach Okeile Stewart, Coach Duwayne Campbell) and support team (massage therapist Hugh Lewis).
            </p>

            {/* How to Support */}
            <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
              How to Support
            </h3>
            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
              <p>
                We're reaching out to brands, foundations, private donors, and anyone who believes in this project. There are no complicated tiers — we just need money to cover the costs listed above.
              </p>
              <p>
                Every contribution goes directly to making this camp happen: flights, housing, food, training, staff, content.
              </p>
            </div>

            {/* Crowdfunding */}
            <div className="my-12 p-8 bg-[#1A1714]/5 border-l-4 border-[#1A1714]/20">
              <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-2">Coming Soon</p>
              <p className="text-[#1A1714] font-medium text-lg mb-2">Crowdfunding Campaign</p>
              <p className="text-[#1A1714]/70">
                We're launching a crowdfunding campaign on <strong>ibelieveinyou.ch</strong> — Switzerland's sports crowdfunding platform. Link coming soon. Follow PAU for updates.
              </p>
            </div>

            {/* What You Get */}
            <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mt-16 mb-6">
              What Supporters Get
            </h3>
            <div className="editorial-body text-lg text-[#1A1714]/80 space-y-4">
              <p>→ Recognition across PAU editorial content and communications</p>
              <p>→ Updates on athlete progress and camp documentation</p>
              <p>→ For larger contributions: visibility in our magazine coverage, logo placement, athlete engagement opportunities</p>
              <p>→ The knowledge that you helped make something real happen</p>
            </div>

            {/* Contact Form */}
            <div className="mt-16 pt-12 border-t border-[#1A1714]/10">
              <h3 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                Get in Touch
              </h3>
              <p className="editorial-body text-lg text-[#1A1714]/60 mb-8">
                Interested in supporting? Questions? Reach out.
              </p>

              {submitted ? (
                <div className="p-8 bg-[#1A1714]/5 border border-[#1A1714]/10 text-center">
                  <p className="text-[#1A1714] font-medium mb-2">Thank you</p>
                  <p className="text-[#1A1714]/60">We'll be in touch within 48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#1A1714]/70 text-sm mb-2">Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1A1714]/70 text-sm mb-2">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#1A1714]/70 text-sm mb-2">Message</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors resize-none"
                      placeholder="How would you like to help? Any questions?"
                    />
                  </div>
                  {error && (
                    <p className="text-[#B8352B] text-sm">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-8 py-4 font-medium hover:bg-[#2A2724] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Message'} {!submitting && <Send size={18} />}
                  </button>
                </form>
              )}

              {/* Direct Contact */}
              <div className="mt-12 pt-8 border-t border-[#1A1714]/10">
                <p className="text-[#1A1714]/50 text-sm mb-4">Or contact us directly:</p>
                <a 
                  href="mailto:active@pauuniverse.com?subject=SWEPT Cham 2026 Support"
                  className="inline-flex items-center gap-2 text-[#1A1714] hover:text-[#B8352B] transition-colors"
                >
                  <Mail size={18} /> active@pauuniverse.com
                </a>
              </div>
            </div>

            {/* Closing */}
            <div className="mt-16 pt-12 border-t border-[#1A1714]/10 text-center">
              <p className="editorial-headline text-3xl md:text-4xl text-[#1A1714] leading-[1.1] mb-6">
                Help us build the base.
              </p>
              <p className="editorial-body text-lg text-[#1A1714]/60 max-w-xl mx-auto">
                One month. Seven athletes. World-class preparation. Your support makes it real.
              </p>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-[#1A1714]/10 text-center">
              <p className="editorial-caption text-[#1A1714]/40">PAU × SWEPT · CHAM 2026</p>
            </div>

            {/* Back Navigation */}
            <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
              <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
                <ArrowLeft size={16} /> Back to PAU MAG
              </a>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// PAU SUMMER CAMP SUPPORT PAGE
// ============================================
const SummerCampPage = () => {
  const [activeLang, setActiveLang] = useState('EN');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const translations = {
    EN: {
      backToMag: "Back to PAU MAG",
      supportProject: "Support Project",
      title: "PAU × SWEPT Summer Camp at OYM",
      subtitle: "A Switzerland-based high-performance training + competition camp for elite athletes from around the World.",
      location: "Location:",
      supportCamp: "Support the camp",
      meetAthletes: "Meet the athletes",
      base: "Base:",
      trainingBaseTitle: "Training Base: OYM (Cham, Switzerland)",
      trainingBaseText: "The 2026 PAU Summer Camp takes place at OYM in Cham, Switzerland — a high-performance environment that gives the athletes stable housing, meals, and access to training infrastructure in one place.",
      oymCaption: "OYM facilities: Athletics Hall, Indoor Track, Turf Training, Athlete Rooms — Photos: Regine Giesecke",
      ideaTitle: "The Idea",
      ideaIntro: "The PAU Summer Camp is about removing friction — so athletes can perform at the highest level. We take care of the things that distract from training and competition:",
      ideaPoint1: "Travel & logistics coordination — flights, local transport, schedules",
      ideaPoint2: "Funding — so participation is not blocked by money",
      ideaPoint3: "Sustainable athlete visibility — digital profiles, media assets, professional presence",
      ideaPoint4: "Editorial storytelling — real coverage through PAU Magazine, not generic sponsor content",
      ideaGoal: "The goal is simple: let athletes focus on what they do best.",
      athletesTitle: "The Athletes — SWEPT × PAU",
      athletesIntro: "The core group comes from SWEPT Track & Field Club in Kingston, Jamaica — a club with a track record of developing world-class sprinters and hurdlers.",
      jamaicaLabel: "Jamaica — SWEPT Track Club",
      westAfricaTitle: "Additional Athletes — West Africa",
      senegalLabel: "Senegal",
      magazineTitle: "PAU Magazine",
      magazineText1: "PAU Magazine documents the camp with real editorial coverage — not interchangeable sponsor content. We capture training sessions, competition moments, personal stories, and the daily reality of elite preparation.",
      magazineText2: "This visibility matters. Good content creates opportunities: media coverage, sponsor interest, invitations to events. Athletes deserve to be seen — and to be seen well.",
      whyMattersTitle: "Why This Matters",
      whyPoint1: "Stable base — world-class infrastructure, fewer distractions, structured environment",
      whyPoint2: "Recovery — good sleep, nutrition, and support services",
      whyPoint3: "Access to European competitions — proximity to Diamond League, Continental Tour, and other elite meetings",
      whyPoint4: "Team environment — training with peers, shared motivation, collective energy",
      fundingTitle: "Funding (2026)",
      fundingText1: "PAU's commitment is to fully fund the athletes and essential staff we bring, so performance is not limited by logistics or finances.",
      fundingText2: "For 2026, our fundraising goals are CHF 85,000 (minimum), CHF 100,000 (target), and CHF 120,000 (ambitious) — and the more we raise, the more athletes we can fully fund.",
      fundingGoalsLabel: "Fundraising Goals — 2026",
      minimum: "Minimum",
      target: "Target",
      ambitious: "Ambitious",
      fundingFooter: "The more we raise, the more athletes we can fully fund.",
      helpBuildBase: "Help us build the base for the next generation of elite athletes.",
      howToHelpTitle: "How You Can Help",
      helpPoint1: "Financial support — sponsorship or donation of any amount",
      helpPoint2: "Mobility support — transport solutions, vehicle partnerships",
      helpPoint3: "Recovery & performance support — services or products that enable performance",
      helpPoint4: "Introductions — connect us with aligned brands and decision-makers",
      readyToSupport: "Ready to support?",
      yourName: "Your name",
      yourEmail: "your@email.com",
      howToHelp: "How would you like to help?",
      sendMessage: "Send Message",
      sending: "Sending...",
      messageSent: "Message sent.",
      inTouch: "We'll be in touch soon.",
      errorMessage: "Something went wrong. Please try again or email us directly.",
      orContact: "Or contact us directly:",
      crowdfundingTitle: "Bank Transfer",
      crowdfundingSubtitle: "Direct Donation",
      bankName: "POSTFINANCE",
      accountHolder: "PAU ACTIVE CLUB",
      ibanLabel: "IBAN",
      closingTitle: "Help us build the base.",
      closingText: "One summer. Real athletes. World-class preparation. Your support makes it real.",
      switzerland: "Switzerland",
    },
    DE: {
      backToMag: "Zurück zu PAU MAG",
      supportProject: "Support Project",
      title: "PAU × SWEPT Summer Camp bei OYM",
      subtitle: "Ein hochklassiges Trainings- und Wettkampfcamp in der Schweiz für Elite-Athlet:innen aus der ganzen Welt.",
      location: "Ort:",
      supportCamp: "Unterstütze das Camp",
      meetAthletes: "Lerne die Athlet:innen kennen",
      base: "Base:",
      trainingBaseTitle: "Trainingsbasis: OYM (Cham, Schweiz)",
      trainingBaseText: "Das PAU Summer Camp 2026 findet im OYM in Cham, Schweiz statt — einer High-Performance-Umgebung, die den Athlet:innen stabiles Wohnen, Verpflegung und Zugang zur Trainingsinfrastruktur an einem Ort bietet.",
      oymCaption: "OYM facilities: Athletics Hall, Indoor Track, Turf Training, Athlete Rooms — Fotos: Regine Giesecke",
      ideaTitle: "Die Idee",
      ideaIntro: "Beim PAU Summer Camp geht es darum, Hürden zu reduzieren — damit Athlet:innen auf höchstem Niveau performen können. Wir übernehmen die Dinge, die Fokus und Energie vom Training und Wettkampf abziehen:",
      ideaPoint1: "Reise & Logistik-Koordination — Flüge, Transport vor Ort, Zeitpläne",
      ideaPoint2: "Finanzierung — damit die Teilnahme nicht am Geld scheitert",
      ideaPoint3: "Nachhaltige Sichtbarkeit für Athlet:innen — digitale Profile, Media Assets, professioneller Auftritt",
      ideaPoint4: "Editorial Storytelling — echte Berichterstattung über PAU Magazine, nicht generischer Sponsor-Content",
      ideaGoal: "Das Ziel ist simpel: Athlet:innen sollen sich auf das konzentrieren können, was sie am besten können.",
      athletesTitle: "Die Athlet:innen — SWEPT × PAU",
      athletesIntro: "Die Kerngruppe kommt vom SWEPT Track & Field Club in Kingston, Jamaika — einem Club mit nachweislicher Erfolgsbilanz in der Entwicklung von Weltklasse-Sprinter:innen und Hürdenläufer:innen.",
      jamaicaLabel: "Jamaika — SWEPT Track Club",
      westAfricaTitle: "Zusätzliche Athlet:innen — Westafrika",
      senegalLabel: "Senegal",
      magazineTitle: "PAU Magazine",
      magazineText1: "PAU Magazine dokumentiert das Camp mit echter Redaktion — nicht mit austauschbarem Sponsor-Content. Wir halten Trainingseinheiten, Wettkampfmomente, persönliche Stories und den Alltag der Elitevorbereitung fest.",
      magazineText2: "Diese Sichtbarkeit zählt. Guter Content schafft Chancen: Medienberichte, Sponsor-Interesse, Einladungen zu Events. Athlet:innen verdienen es, gesehen zu werden — und zwar im richtigen Licht.",
      whyMattersTitle: "Warum das wichtig ist",
      whyPoint1: "Stabile Basis — Weltklasse-Infrastruktur, weniger Ablenkung, strukturierte Umgebung",
      whyPoint2: "Regeneration — guter Schlaf, Ernährung und Support-Services",
      whyPoint3: "Zugang zu europäischen Wettkämpfen — Nähe zu Diamond League, Continental Tour und weiteren Top-Meetings",
      whyPoint4: "Team-Umfeld — Training mit Peers, gemeinsame Motivation, kollektive Energie",
      fundingTitle: "Finanzierung (2026)",
      fundingText1: "PAU verfolgt das Ziel, die Athlet:innen und das notwendige Kernteam vollständig zu finanzieren — damit Leistung nicht durch Logistik oder Finanzen begrenzt wird.",
      fundingText2: "Für 2026 liegen unsere Fundraising-Ziele bei CHF 85'000 (Minimum), CHF 100'000 (Ziel) und CHF 120'000 (ambitioniert) — und je mehr wir erreichen, desto mehr Athlet:innen können wir vollständig unterstützen.",
      fundingGoalsLabel: "Fundraising-Ziele — 2026",
      minimum: "Minimum",
      target: "Ziel",
      ambitious: "Ambitioniert",
      fundingFooter: "Je mehr wir sammeln, desto mehr Athlet:innen können wir vollständig finanzieren.",
      helpBuildBase: "Hilf uns, die Basis für die nächste Generation von Elite-Athlet:innen zu bauen.",
      howToHelpTitle: "Wie du helfen kannst",
      helpPoint1: "Finanzielle Unterstützung — Sponsoring oder Spende in jeder Höhe",
      helpPoint2: "Mobilitäts-Support — Transportlösungen, Fahrzeug-Partnerschaften",
      helpPoint3: "Recovery & Performance Support — Services oder Produkte, die Leistung ermöglichen",
      helpPoint4: "Introductions — vernetze uns mit passenden Brands und decision-makers",
      readyToSupport: "Bereit zu unterstützen?",
      yourName: "Dein Name",
      yourEmail: "deine@email.com",
      howToHelp: "Wie möchtest du helfen?",
      sendMessage: "Nachricht senden",
      sending: "Wird gesendet...",
      messageSent: "Nachricht gesendet.",
      inTouch: "Wir melden uns bald.",
      errorMessage: "Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreib uns direkt.",
      orContact: "Oder kontaktiere uns direkt:",
      crowdfundingTitle: "Banküberweisung",
      crowdfundingSubtitle: "Direkte Spende",
      bankName: "POSTFINANCE",
      accountHolder: "PAU ACTIVE CLUB",
      ibanLabel: "IBAN",
      closingTitle: "Hilf uns, die Basis zu bauen.",
      closingText: "Ein Sommer. Echte Athlet:innen. Weltklasse-Vorbereitung. Deine Unterstützung macht es möglich.",
      switzerland: "Schweiz",
    },
  };

  const t = translations[activeLang];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        telephone: null,
        inquiry: `[Summer Camp Page] ${formData.message}`
      });
      
      if (response.data) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const athletes = [
    { name: "Roshawn Clarke", url: "https://swepttrack.com/athletes/69df8eb774d46aa34bdc72cf", thumbnail: null },
    { name: "Serena Cole", url: "https://swepttrack.com/athletes/69df8eb774d46aa34bdc72d1", thumbnail: null },
    { name: "Jason Lewis", url: "https://swepttrack.com/athletes/6a394b86f15ec9729ab0f942", thumbnail: null },
    { name: "Javier Brown", url: "https://swepttrack.com/athletes/6a394b86f15ec9729ab0f943", thumbnail: null },
    { name: "Javon Francis", url: "https://swepttrack.com/athletes/6a394b86f15ec9729ab0f944", thumbnail: null },
    { name: "Dejour Russell", url: "https://swepttrack.com/athletes/69df8eb774d46aa34bdc72d0", thumbnail: null },
    { name: "Coach Okeile Stewart", url: "https://swepttrack.com/coaches", isCoach: true, thumbnail: null },
  ];

  const westAfricaAthletes = [
    { name: "Arame Touré", country: "Senegal", url: "https://worldathletics.org/athletes/senegal/ndeye-arame-14678321", thumbnail: null },
    { name: "Coach Daouda Ndoye", url: "https://www.instagram.com/davidndoye70/", isCoach: true, thumbnail: null },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Navigation variant="light" />
      <main className="pt-20 editorial-bg min-h-screen">
        
        {/* Header */}
        <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
          <div className="max-w-[900px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm">
                <ArrowLeft size={16} /> {t.backToMag}
              </a>
              
              {/* Language Switcher */}
              <div className="flex gap-1">
                {['EN', 'DE'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      activeLang === lang 
                        ? 'bg-[#1A1714] text-[#EDE6D6]' 
                        : 'text-[#1A1714]/50 hover:text-[#1A1714]'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-3 mb-6">
              <span className="px-2 py-1 bg-[#B8352B]/10 text-[#B8352B] text-xs uppercase tracking-wider">{t.supportProject}</span>
              <span className="text-[#1A1714]/40 text-xs">·</span>
              <span className="editorial-caption text-[#1A1714]/50">AUG 2026</span>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="px-6 md:px-10 py-12 md:py-16 border-b border-[#1A1714]/10">
          <div className="max-w-[900px] mx-auto">
            <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
              {t.title}
            </h1>
            
            <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60 max-w-2xl mb-4">
              {t.subtitle}
            </p>
            
            <p className="text-[#1A1714] text-lg mb-8">
              Location: <strong>OYM, Cham (Switzerland)</strong> <span className="text-[#1A1714]/50">·</span> <span className="text-[#1A1714]/50 text-sm uppercase tracking-wider">August 2026</span>
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button 
                onClick={() => scrollTo('how-to-help')}
                className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors"
              >
                Support the camp <ArrowRight size={18} />
              </button>
              <button 
                onClick={() => scrollTo('athletes')}
                className="inline-flex items-center gap-2 border border-[#1A1714]/30 text-[#1A1714] px-6 py-3 font-medium hover:bg-[#1A1714]/5 transition-colors"
              >
                Meet the athletes
              </button>
            </div>
          </div>
        </section>

        {/* Callout Box */}
        <div className="px-6 md:px-10 py-6 bg-[#1A1714]">
          <div className="max-w-[900px] mx-auto">
            <p className="text-[#E8E4DC] text-center font-medium">
              <span className="text-[#8B9A7D]">{t.base}</span> <a href="https://www.oym.ch/" target="_blank" rel="noopener noreferrer" className="hover:text-[#8B9A7D] transition-colors">OYM</a> — Cham, {t.switzerland}
            </p>
          </div>
        </div>

        {/* Article Content */}
        <article className="px-6 md:px-10 py-12">
          <div className="max-w-[680px] mx-auto">
            
            {/* Training Base: OYM */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.trainingBaseTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  {t.trainingBaseText}
                </p>
              </div>
              
              {/* OYM Image after section */}
              <div className="mt-8 aspect-[16/9] overflow-hidden">
                <img 
                  src="/assets/oym/oym-exterior.jpg" 
                  alt="OYM facility exterior" 
                  className="w-full h-full object-cover"
                />
              </div>
            </section>
          </div>

          {/* OYM Facility Gallery - Full Width */}
          <div className="max-w-[1400px] mx-auto mb-16">
            <div className="grid md:grid-cols-2 gap-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/oym-athletikhalle.jpg" 
                  alt="OYM Athletics Hall" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/u0j0y6ak_Rennbahn-3334.jpg" 
                  alt="OYM Indoor Track" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/oym-turf.jpg" 
                  alt="OYM Turf Area" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb54de79-1923-432b-9599-e9d133241868/artifacts/12xhkups_Zimmer-Athleten-1215.jpg" 
                  alt="Athlete Rooms" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
            <p className="editorial-caption text-[#1A1714]/40 mt-3 text-center">OYM facilities: Athletics Hall, Indoor Track, Turf Training, Athlete Rooms — Photos: Regine Giesecke</p>
          </div>

          <div className="max-w-[680px] mx-auto">
            {/* The Idea */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.ideaTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  {t.ideaIntro}
                </p>
                <ul className="space-y-3 pl-4">
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.ideaPoint1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.ideaPoint2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.ideaPoint3}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.ideaPoint4}</span>
                  </li>
                </ul>
                <p className="text-[#1A1714]/60 italic">
                  {t.ideaGoal}
                </p>
              </div>
              
              {/* Image grid after The Idea */}
              <div className="mt-8 grid md:grid-cols-2 gap-2">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="/assets/oym/article-img-2.png" 
                    alt="Athletes in action" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="/assets/oym/article-img-3.png" 
                    alt="Training session" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Athletes in Action Gallery */}
          <div className="max-w-[1200px] mx-auto mb-16">
            <p className="editorial-caption text-[#1A1714]/40 mb-4 px-6">SWEPT Track Club athletes</p>
            <div className="grid md:grid-cols-2 gap-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/female-sprinters.jpg" 
                  alt="Female sprinters racing on blue track" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/mona-relay.jpg" 
                  alt="MONA athlete with relay baton" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/russell-portrait.jpg" 
                  alt="Russell - Jamaica Athletics (Nairobi 2017)" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/golden-hour-sprint.jpg" 
                  alt="Athlete sprinting at golden hour" 
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          <div className="max-w-[680px] mx-auto">
            {/* The Athletes */}
            <section id="athletes" className="mb-16 scroll-mt-24">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.athletesTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 mb-8 space-y-4">
                <p>
                  {t.athletesIntro} <a href="https://swepttrack.com" target="_blank" rel="noopener noreferrer" className="text-[#B8352B] underline hover:text-[#1A1714]">swepttrack.com</a>
                </p>
              </div>
            </section>
          </div>

          {/* Athlete Photo Gallery */}
          <div className="max-w-[1200px] mx-auto mb-12">
            <div className="grid md:grid-cols-3 gap-2">
              <div className="relative group">
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1714]">
                  <img 
                    src="/assets/oym/roshawn-clarke.webp" 
                    alt="Roshawn Clarke - SWEPT Track Club" 
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <p className="text-[#1A1714]/60 text-sm text-center py-2">Roshawn Clarke</p>
              </div>
              <div className="relative group">
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1714]">
                  <img 
                    src="/assets/oym/three-athletes-finish.jpg" 
                    alt="Emanuel Archibald" 
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <p className="text-[#1A1714]/60 text-sm text-center py-2">Emanuel Archibald</p>
              </div>
              <div className="relative group">
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1714]">
                  <img 
                    src="/assets/oym/jamaican-3.jpg" 
                    alt="Dejour Russell" 
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <p className="text-[#1A1714]/60 text-sm text-center py-2">Dejour Russell</p>
              </div>
            </div>
          </div>

          <div className="max-w-[680px] mx-auto">
              
              <div className="bg-[#1A1714]/5 p-6 mb-8">
                <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-4">{t.jamaicaLabel}</p>
                <div className="space-y-3">
                  {athletes.map((athlete, i) => (
                    <a 
                      key={i}
                      href={athlete.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between py-3 border-b border-[#1A1714]/10 hover:bg-[#1A1714]/5 px-2 -mx-2 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {/* Thumbnail placeholder */}
                        <div className="w-10 h-10 rounded-full bg-[#1A1714]/10 flex items-center justify-center flex-shrink-0">
                          {athlete.thumbnail ? (
                            <img src={athlete.thumbnail} alt={athlete.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-[#1A1714]/30 text-xs">{athlete.name.charAt(0)}</span>
                          )}
                        </div>
                        <span className={`${athlete.isCoach ? 'text-[#1A1714]/70' : 'text-[#1A1714] font-medium'}`}>
                          {athlete.name}
                        </span>
                      </div>
                      <ArrowUpRight size={16} className="text-[#1A1714]/30 group-hover:text-[#B8352B] transition-colors" />
                    </a>
                  ))}
                </div>
              </div>

            {/* West Africa Athletes */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.westAfricaTitle}
              </h2>
              
              <div className="bg-[#1A1714]/5 p-6">
                <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-4">Senegal</p>
                <div className="space-y-3">
                  {westAfricaAthletes.map((athlete, i) => (
                    <a 
                      key={i}
                      href={athlete.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between py-3 border-b border-[#1A1714]/10 hover:bg-[#1A1714]/5 px-2 -mx-2 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {/* Thumbnail placeholder */}
                        <div className="w-10 h-10 rounded-full bg-[#1A1714]/10 flex items-center justify-center flex-shrink-0">
                          {athlete.thumbnail ? (
                            <img src={athlete.thumbnail} alt={athlete.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-[#1A1714]/30 text-xs">{athlete.name.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <span className={`${athlete.isCoach ? 'text-[#1A1714]/70' : 'text-[#1A1714] font-medium'}`}>
                            {athlete.name}
                          </span>
                          {athlete.country && (
                            <span className="text-[#1A1714]/40 text-sm ml-2">({athlete.country})</span>
                          )}
                        </div>
                      </div>
                      <ArrowUpRight size={16} className="text-[#1A1714]/30 group-hover:text-[#B8352B] transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Image after West Africa */}
              <div className="mt-8 aspect-[16/9] overflow-hidden">
                <img 
                  src="/assets/oym/article-img-4.png" 
                  alt="Track and field competition" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </section>

            {/* PAU Magazine */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.magazineTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  {t.magazineText1}
                </p>
                <p>
                  {t.magazineText2}
                </p>
              </div>
              
              {/* Editorial images grid */}
              <div className="mt-8 grid md:grid-cols-2 gap-4">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="/assets/oym/article-img-5.png" 
                    alt="SWEPT athlete training session" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="/assets/oym/article-img-6.png" 
                    alt="SWEPT Track Club Athletes" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <p className="editorial-caption text-[#1A1714]/40 mt-2">SWEPT Track Club athletes — real coverage, real stories.</p>
            </section>

            {/* Why This Matters */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.whyMattersTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.whyPoint1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.whyPoint2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.whyPoint3}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.whyPoint4}</span>
                  </li>
                </ul>
              </div>
              
              {/* Image after Why This Matters */}
              <div className="mt-8 aspect-[16/9] overflow-hidden">
                <img 
                  src="/assets/oym/article-img-7.png" 
                  alt="Athletes competing" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </section>

            {/* Funding */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.fundingTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  {t.fundingText1}
                </p>
                <p>
                  {t.fundingText2}
                </p>
                <div className="my-8 p-6 bg-[#1A1714] text-[#E8E4DC]">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">{t.fundingGoalsLabel}</p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[#E8E4DC]/70">{t.minimum}</span>
                      <span className="font-headline text-2xl">CHF 85'000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#E8E4DC]/70">{t.target}</span>
                      <span className="font-headline text-3xl text-[#8B9A7D]">CHF 100'000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#E8E4DC]/70">{t.ambitious}</span>
                      <span className="font-headline text-2xl">CHF 120'000</span>
                    </div>
                  </div>
                  <p className="text-[#E8E4DC]/50 text-sm mt-6 pt-4 border-t border-[#E8E4DC]/20">
                    {t.fundingFooter}
                  </p>
                </div>
              </div>
            </section>
          </div>
          
          {/* Image gallery before support banner */}
          <div className="max-w-[1200px] mx-auto mb-8">
            <div className="grid md:grid-cols-3 gap-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/article-img-8.png" 
                  alt="Track competition" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/article-img-9.png" 
                  alt="Athletes racing" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/assets/oym/article-img-10.png" 
                  alt="Sprint finish" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Support Banner - OYM Restaurant / Community Space */}
          <div className="mb-16">
            <div className="aspect-[21/7] overflow-hidden relative">
              <img 
                src="/assets/oym/oym-restaurant.jpg" 
                alt="OYM Restaurant - Community Space" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <p className="text-[#E8E4DC] text-2xl md:text-3xl font-headline max-w-2xl">
                  {t.helpBuildBase}
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-[680px] mx-auto px-6 md:px-10">
            {/* How You Can Help */}
            <section id="how-to-help" className="mb-16 scroll-mt-24">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                {t.howToHelpTitle}
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.helpPoint1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.helpPoint2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.helpPoint3}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#B8352B] mt-1">→</span>
                    <span>{t.helpPoint4}</span>
                  </li>
                </ul>
              </div>

              {/* Contact CTA */}
              <div className="mt-10 p-8 bg-[#1A1714]/5 border-l-4 border-[#B8352B]">
                <p className="text-[#1A1714] font-medium text-lg mb-4">{t.readyToSupport}</p>
                
                {submitted ? (
                  <div className="text-center py-4">
                    <p className="text-[#1A1714] font-medium mb-2">{t.messageSent}</p>
                    <p className="text-[#1A1714]/60">{t.inTouch}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors text-sm"
                        placeholder={t.yourName}
                      />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors text-sm"
                        placeholder={t.yourEmail}
                      />
                    </div>
                    <textarea
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors resize-none text-sm"
                      placeholder={t.howToHelp}
                    />
                    {error && (
                      <p className="text-[#B8352B] text-sm">{error}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {submitting ? t.sending : t.sendMessage} {!submitting && <Send size={16} />}
                      </button>
                      <span className="text-[#1A1714]/40 text-sm">{t.orContact}</span>
                      <a 
                        href="mailto:active@pauuniverse.com?subject=PAU Summer Camp Support"
                        className="inline-flex items-center gap-2 text-[#B8352B] hover:text-[#1A1714] transition-colors text-sm"
                      >
                        <Mail size={16} /> active@pauuniverse.com
                      </a>
                    </div>
                  </form>
                )}
              </div>
            </section>

            {/* Bank Transfer */}
            <section className="mb-16">
              <div className="p-8 bg-[#1A1714] text-[#E8E4DC]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">{t.crowdfundingTitle}</p>
                <p className="text-[#E8E4DC] font-medium text-lg mb-4">{t.crowdfundingSubtitle}</p>
                <div className="space-y-2">
                  <p className="text-[#E8E4DC]/70 text-sm">{t.bankName}</p>
                  <p className="text-[#E8E4DC] font-medium">{t.accountHolder}</p>
                  <div className="mt-4 pt-4 border-t border-[#E8E4DC]/20">
                    <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-1">{t.ibanLabel}</p>
                    <p className="font-mono text-lg text-[#E8E4DC] tracking-wider">CH61 0900 0000 1676 6975 6</p>
                  </div>
                </div>
              </div>
              
              {/* Final image */}
              <div className="mt-8 aspect-[16/9] overflow-hidden relative">
                <img 
                  src="/assets/oym/article-img-11.png" 
                  alt="Arame Touré" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="text-[#1A1714]/50 text-sm text-center mt-2">Arame Touré</p>
            </section>

            {/* Footer */}
            <div className="mt-16 pt-12 border-t border-[#1A1714]/10 text-center">
              <p className="editorial-headline text-3xl md:text-4xl text-[#1A1714] leading-[1.1] mb-6">
                {t.closingTitle}
              </p>
              <p className="editorial-body text-lg text-[#1A1714]/60 max-w-xl mx-auto">
                {t.closingText}
              </p>
            </div>

            {/* Back Navigation */}
            <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
              <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
                <ArrowLeft size={16} /> {t.backToMag}
              </a>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// FOUNDATION HUB PAGE
// ============================================
const FoundationHubPage = () => {
  return (
    <>
      <Navigation variant="light" />
      <main className="pt-20 editorial-bg min-h-screen">
        
        {/* Hero Section */}
        <header className="px-6 md:px-10 py-16 md:py-24 border-b border-[#1A1714]/10">
          <div className="max-w-[900px] mx-auto">
            <p className="text-[#8B9A7D] text-xs uppercase tracking-[0.3em] mb-6">Foundation</p>
            <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-6">
              PAU Foundation
            </h1>
            <p className="editorial-subhead text-xl md:text-2xl text-[#1A1714]/60 max-w-2xl mb-8">
              Fund athletes, camps, and the PAU Foundation (in formation).
            </p>
            
            {/* SEO Intro Paragraph */}
            <div className="editorial-body text-lg text-[#1A1714]/70 max-w-2xl">
              <p>
                The PAU Foundation supports elite athletes, training camps, and grassroots sports initiatives. Through direct funding and strategic partnerships, we enable athletes to focus on performance without financial barriers. Our projects span high-performance training environments, competition travel support, and athlete visibility through editorial storytelling. The foundation is currently in formation, with a CHF 50,000 target for registration and operational setup.
              </p>
            </div>
          </div>
        </header>

        {/* Projects Section */}
        <section className="px-6 md:px-10 py-16">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-12">
              Projects to Support
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Project Card 1 - Building the PAU Foundation */}
              <article className="group bg-white border border-[#1A1714]/10 hover:border-[#1A1714]/30 transition-colors">
                <div className="aspect-[16/9] overflow-hidden bg-[#1A1714]">
                  <img 
                    src="/assets/oym/pau-foundation-hero.png"
                    alt="Athlete in starting blocks representing PAU Foundation's mission to fund athletic excellence"
                    className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-1 bg-[#B8352B]/10 text-[#B8352B] text-xs uppercase tracking-wider">Foundation</span>
                    <span className="text-[#1A1714]/40 text-xs">·</span>
                    <span className="text-[#1A1714]/50 text-xs">CHF 50,000</span>
                  </div>
                  <h3 className="article-title text-xl text-[#1A1714] mb-3 group-hover:text-[#B8352B] transition-colors">
                    Building the PAU Foundation
                  </h3>
                  <p className="editorial-body text-[#1A1714]/60 mb-6">
                    CHF 50,000 to register and operationalize the PAU Foundation. Funds cover legal setup, governance, and the infrastructure to sustainably support athletes and projects.
                  </p>
                  <a 
                    href="/magazine/pau-foundation" 
                    className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors text-sm"
                  >
                    Learn more & support <ArrowRight size={16} />
                  </a>
                </div>
              </article>

              {/* Project Card 2 - PAU Summer Camp */}
              <article className="group bg-white border border-[#1A1714]/10 hover:border-[#1A1714]/30 transition-colors">
                <div className="aspect-[16/9] overflow-hidden bg-[#1A1714]">
                  <img 
                    src="/assets/oym/summer-camp-title.png"
                    alt="PAU Summer Camp at OYM featuring elite athletes training in Switzerland"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-1 bg-[#8B9A7D]/20 text-[#8B9A7D] text-xs uppercase tracking-wider">Camp</span>
                    <span className="text-[#1A1714]/40 text-xs">·</span>
                    <span className="text-[#1A1714]/50 text-xs">AUG 2026</span>
                  </div>
                  <h3 className="article-title text-xl text-[#1A1714] mb-3 group-hover:text-[#B8352B] transition-colors">
                    PAU × SWEPT Summer Camp at OYM
                  </h3>
                  <p className="editorial-body text-[#1A1714]/60 mb-6">
                    High-performance training camp in Switzerland for elite athletes from around the world. CHF 85K–120K fundraising goal to fully fund athletes and staff.
                  </p>
                  <a 
                    href="/magazine/summer-camp" 
                    className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors text-sm"
                  >
                    Learn more & support <ArrowRight size={16} />
                  </a>
                </div>
              </article>
              
            </div>
          </div>
        </section>

        {/* How PAU Works Section */}
        <section className="px-6 md:px-10 py-16 bg-[#1A1714]/5">
          <div className="max-w-[900px] mx-auto">
            <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-8">
              How PAU Works
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <p className="text-[#B8352B] text-xs uppercase tracking-wider mb-2">Foundation</p>
                <p className="editorial-body text-[#1A1714]/70">
                  The Foundation funds athletes, projects, and access — removing financial barriers so performance is not limited by logistics.
                </p>
              </div>
              <div>
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">PAUMAG / Production</p>
                <p className="editorial-body text-[#1A1714]/70">
                  Documents the work and makes impact visible. Editorial storytelling that treats athletes as cultural subjects, not marketing objects.
                </p>
              </div>
              <div>
                <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-2">Partners</p>
                <p className="editorial-body text-[#1A1714]/70">
                  Brands and agencies support through paid collaborations with clear deliverables. <a href="/partners" className="text-[#B8352B] hover:underline">Learn about partnerships →</a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links Section */}
        <section className="px-6 md:px-10 py-16 border-t border-[#1A1714]/10">
          <div className="max-w-[900px] mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 bg-[#1A1714] text-[#E8E4DC]">
                <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">For Brands & Agencies</p>
                <p className="text-[#E8E4DC] text-lg mb-4">Interested in paid collaborations?</p>
                <a href="/partners" className="inline-flex items-center gap-2 text-[#8B9A7D] hover:text-[#E8E4DC] transition-colors">
                  View partnership options <ArrowRight size={16} />
                </a>
              </div>
              <div className="p-8 border border-[#1A1714]/10">
                <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-2">Individual Support</p>
                <p className="text-[#1A1714] text-lg mb-4">Want to donate directly?</p>
                <a href="/magazine/pau-foundation#the-ask" className="inline-flex items-center gap-2 text-[#B8352B] hover:text-[#1A1714] transition-colors">
                  Support the Foundation <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-6 md:px-10 py-12 bg-[#1A1714]">
          <div className="max-w-[900px] mx-auto text-center">
            <p className="text-[#E8E4DC]/50 text-sm mb-2">Questions about supporting PAU?</p>
            <a href="mailto:active@pauuniverse.com" className="text-[#E8E4DC] text-xl hover:text-[#8B9A7D] transition-colors">
              active@pauuniverse.com
            </a>
          </div>
        </section>
        
      </main>
      <Footer />
    </>
  );
};

// ============================================
// PAU FOUNDATION PAGE
// ============================================
const PauFoundationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        telephone: null,
        inquiry: `[PAU Foundation Page] ${formData.message}`
      });
      
      if (response.data) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Navigation variant="light" />
      <main className="pt-20 editorial-bg min-h-screen">
        
        {/* Header */}
        <header className="border-b border-[#1A1714]/10 py-8 px-6 md:px-10">
          <div className="max-w-[900px] mx-auto">
            <a href="/magazine" className="editorial-link inline-flex items-center gap-2 text-sm mb-8">
              <ArrowLeft size={16} /> Back to PAU MAG
            </a>
            
            <div className="flex items-center gap-3 mb-6">
              <span className="px-2 py-1 bg-[#B8352B]/10 text-[#B8352B] text-xs uppercase tracking-wider">Support Project</span>
              <span className="text-[#1A1714]/40 text-xs">·</span>
              <span className="editorial-caption text-[#1A1714]/50">2026</span>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="px-6 md:px-10 py-12 md:py-16 border-b border-[#1A1714]/10">
          <div className="max-w-[900px] mx-auto">
            <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-[#1A1714] leading-[1.1] mb-4">
              Building the PAU Foundation (CHF 50,000)
            </h1>
            
            <h2 className="text-xl md:text-2xl text-[#1A1714]/70 font-medium mb-6">
              A foundation to fund athletes, camps, and culture (in formation)
            </h2>
            
            <p className="editorial-subhead text-lg text-[#1A1714]/60 max-w-2xl mb-6">
              A funding vehicle that backs the people and projects the market too often overlooks.
            </p>
            
            {/* Status Line */}
            <div className="p-4 bg-[#8B9A7D]/10 border-l-4 border-[#8B9A7D] mb-8">
              <p className="text-[#1A1714]/80">
                <strong>Status:</strong> The PAU Foundation is in formation. CHF 50,000 funds registration and operational setup. Until registration is complete, contributions support the foundation setup and PAU's athlete projects via our existing structure.
              </p>
            </div>

            {/* At a Glance */}
            <div className="p-6 bg-[#1A1714] text-[#E8E4DC] mb-8">
              <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-4">At a Glance</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[#E8E4DC]/50">What</p>
                  <p className="text-[#E8E4DC]">Building the PAU Foundation (in formation)</p>
                </div>
                <div>
                  <p className="text-[#E8E4DC]/50">Goal</p>
                  <p className="text-[#E8E4DC] font-medium">CHF 50,000</p>
                </div>
                <div>
                  <p className="text-[#E8E4DC]/50">Purpose</p>
                  <p className="text-[#E8E4DC]">Registration + governance + operational setup</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[#E8E4DC]/50">Unlocks</p>
                  <p className="text-[#E8E4DC]">Athlete funding + camps + mobility + storytelling infrastructure</p>
                </div>
                <div>
                  <p className="text-[#E8E4DC]/50">Contact</p>
                  <a href="mailto:active@pauuniverse.com" className="text-[#8B9A7D] hover:text-[#E8E4DC]">active@pauuniverse.com</a>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
              <button 
                onClick={() => scrollTo('the-ask')}
                className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors"
              >
                Support the Foundation <ArrowRight size={18} />
              </button>
              <a 
                href="/foundation"
                className="inline-flex items-center gap-2 border border-[#1A1714]/30 text-[#1A1714] px-6 py-3 font-medium hover:bg-[#1A1714]/5 transition-colors"
              >
                View all projects
              </a>
            </div>
          </div>
        </section>

        {/* Hero Image */}
        <section className="bg-[#1A1714]">
          <div className="max-w-[1400px] mx-auto">
            <div className="aspect-[21/9] overflow-hidden">
              <img 
                src="/assets/oym/pau-foundation-hero.png" 
                alt="Athlete in starting blocks - the moment before the race" 
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="px-6 md:px-10 py-12">
          <div className="max-w-[680px] mx-auto">
            
            {/* Why This Matters */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                Why This Matters
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  PAU exists to put people before optics — and to put structure behind that conviction.
                </p>
                <p>
                  We organise high-performance camps and competition projects that give athletes the environment, coaching, and exposure they need to progress. In doing so, we also see the fault lines of the system: opportunity is too often determined by geography, economics, and visibility.
                </p>
                <p>
                  Funding in sport tends to follow predictable logic. Support often flows toward what is already visible, what is easy to package, and what offers clear commercial upside. That is not inherently immoral — it is market logic behaving as designed.
                </p>
                <p className="text-[#1A1714] font-medium">
                  But it leaves a gap.
                </p>
                <p className="text-[#B8352B] font-medium text-xl">
                  PAU is here to fill that gap.
                </p>
              </div>
            </section>

            {/* Video 1 */}
            <section className="mb-16">
              <div className="aspect-video w-full overflow-hidden bg-[#1A1714]">
                <iframe
                  src="https://www.youtube.com/embed/4D5ZtATe60E?start=623"
                  title="PAU Foundation Video 1"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </section>

            {/* What We Are Building */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                What We Are Building
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  We want to formally register a <strong>PAU Foundation</strong> — a long-term vehicle to fund the work we do and the work we believe should exist.
                </p>
                
                <div className="p-8 bg-[#1A1714] text-[#EDE6D6] my-8">
                  <p className="text-[#8B9A7D] text-xs uppercase tracking-wider mb-2">Funding Target</p>
                  <p className="font-headline text-4xl md:text-5xl uppercase">CHF 50,000</p>
                  <p className="text-[#E8E4DC]/60 mt-4">
                    This amount is dedicated to the creation and registration of the foundation and the operational setup required to run it properly.
                  </p>
                </div>
              </div>
            </section>

            {/* The Foundation Is Not Only For Sport */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                The Foundation Is Not Only For Sport
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  PAU began in sport because sport is an honest teacher: discipline, repetition, injury, recovery, ambition, doubt. It reveals character. It also reveals inequality — not as theory, but as daily reality.
                </p>
                <p className="text-[#1A1714] font-medium">
                  But PAU has never been only about sport.
                </p>
                <p>
                  We are building bridges between sport and arts, because culture is how communities tell the truth about themselves. And we invest in storytelling because attention is a currency: many athletes and grassroots initiatives are underfunded precisely because they are under-seen.
                </p>
                <p>
                  A foundation gives us the structure to fund all of this with clarity and accountability.
                </p>
              </div>
            </section>

            {/* What The Foundation Will Do */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                What The PAU Foundation Will Do
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-8">
                <p>
                  The PAU Foundation will exist to fund projects across three connected pillars.
                </p>

                {/* Pillar 1 - Sport */}
                <div className="border-l-4 border-[#B8352B] pl-6">
                  <h3 className="font-medium text-[#1A1714] text-xl mb-4">1) Sport: Fund Athletes, Projects, and Camps</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-[#B8352B] mt-1">→</span>
                      <span>Training camps and high-performance environments</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#B8352B] mt-1">→</span>
                      <span>Competition travel support (mobility, logistics, entries)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#B8352B] mt-1">→</span>
                      <span>Athlete welfare support where it directly impacts performance and stability</span>
                    </li>
                  </ul>
                </div>

                {/* Pillar 2 - Arts + Culture */}
                <div className="border-l-4 border-[#8B9A7D] pl-6">
                  <h3 className="font-medium text-[#1A1714] text-xl mb-4">2) Arts + Culture: Build Bridges</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-[#8B9A7D] mt-1">→</span>
                      <span>Creative collaborations that connect sport with music, film, photography, design, and local scenes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#8B9A7D] mt-1">→</span>
                      <span>Community-rooted initiatives where sport and culture reinforce each other</span>
                    </li>
                  </ul>
                </div>

                {/* Pillar 3 - Storytelling */}
                <div className="border-l-4 border-[#1A1714]/30 pl-6">
                  <h3 className="font-medium text-[#1A1714] text-xl mb-4">3) Storytelling: Sustain the Work That Makes Impact Visible</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-[#1A1714]/50 mt-1">→</span>
                      <span>Editorial work and documentation through PAU Magazine</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1A1714]/50 mt-1">→</span>
                      <span>Creative production that treats athletes as cultural subjects — not marketing objects</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1A1714]/50 mt-1">→</span>
                      <span>Long-form narratives that make overlooked talent legible to the world</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Video 2 */}
            <section className="mb-16">
              <div className="aspect-video w-full overflow-hidden bg-[#1A1714]">
                <iframe
                  src="https://www.youtube.com/embed/8B0yWIfuhyY?start=63"
                  title="PAU Foundation Video 2"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </section>

            {/* Why This Structure Matters */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                Why This Structure Matters
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p className="text-[#1A1714]/60 italic">
                  A foundation is not a vibe. It is governance.
                </p>
                <p>
                  We want the PAU Foundation to be built on:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-[#8B9A7D] mt-1">●</span>
                    <span><strong>Clear criteria</strong> for support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#8B9A7D] mt-1">●</span>
                    <span><strong>Transparent allocation</strong> of funds</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#8B9A7D] mt-1">●</span>
                    <span><strong>Measurable outcomes</strong> where possible, and <strong>honest reporting</strong> always</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#8B9A7D] mt-1">●</span>
                    <span><strong>Long-term relationships</strong> with athletes and projects — not one-off gestures</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* The Ask */}
            <section id="the-ask" className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                The Ask
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  If you believe sport and culture should be funded for <strong>impact</strong> — not only for visibility — this is a moment where your support creates real leverage.
                </p>
                
                <div className="p-8 bg-[#B8352B]/10 border border-[#B8352B]/20 my-8">
                  <p className="font-headline text-2xl md:text-3xl text-[#1A1714] uppercase mb-4">
                    CHF 50,000
                  </p>
                  <p className="text-[#1A1714]/80">
                    To register and set up the PAU Foundation.
                  </p>
                </div>

                <p>
                  If you want to contribute, partner, or introduce us to someone who can help, reach out and be part of building a funding vehicle that backs the people and projects the market too often overlooks.
                </p>
              </div>

              {/* Contact Form */}
              <div className="mt-12 p-8 bg-[#1A1714]/5 border border-[#1A1714]/10">
                <h3 className="font-medium text-[#1A1714] text-xl mb-6">Get in Touch</h3>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#8B9A7D]/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#8B9A7D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-[#1A1714] font-medium mb-2">Message sent.</p>
                    <p className="text-[#1A1714]/60 text-sm">We'll be in touch soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors text-sm"
                        placeholder="Your name"
                      />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <textarea
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 bg-white border border-[#1A1714]/20 text-[#1A1714] focus:border-[#1A1714] focus:outline-none transition-colors resize-none text-sm"
                      placeholder="How would you like to help?"
                    />
                    {error && (
                      <p className="text-[#B8352B] text-sm">{error}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-[#1A1714] text-[#EDE6D6] px-6 py-3 font-medium hover:bg-[#2A2724] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {submitting ? 'Sending...' : 'Send Message'} {!submitting && <Send size={16} />}
                      </button>
                      <span className="text-[#1A1714]/40 text-sm">or</span>
                      <a 
                        href="mailto:active@pauuniverse.com?subject=PAU Foundation Support"
                        className="inline-flex items-center gap-2 text-[#B8352B] hover:text-[#1A1714] transition-colors text-sm"
                      >
                        <Mail size={16} /> active@pauuniverse.com
                      </a>
                    </div>
                  </form>
                )}
              </div>
            </section>

            {/* Video 4 */}
            <section className="mb-16">
              <div className="aspect-video w-full overflow-hidden bg-[#1A1714]">
                <iframe
                  src="https://www.youtube.com/embed/XA8bQRzhseY"
                  title="PAU Foundation Video 4"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </section>

            {/* Closing */}
            <section className="mb-16">
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-6">
                <p>
                  PAU has always been about more than performance.
                </p>
                <p>
                  It is about <strong>access</strong>.
                </p>
                <p>
                  It is about <strong>dignity</strong>.
                </p>
                <p>
                  It is about building a system that does not wait for permission from the market to do what is right.
                </p>
                <p className="text-[#B8352B] font-medium text-xl pt-4">
                  This is where PAU steps in.
                </p>
              </div>
            </section>

            {/* Use of Funds Section */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                Use of Funds (CHF 50,000)
              </h2>
              <div className="p-6 bg-[#1A1714]/5 border border-[#1A1714]/10">
                <p className="editorial-body text-[#1A1714]/70 mb-6">
                  A transparent breakdown of how funds will be allocated:
                </p>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-[#1A1714]/10">
                    <span className="text-[#1A1714]">Legal registration + governance setup</span>
                    <span className="text-[#1A1714]/60 font-mono">CHF 15,000–20,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#1A1714]/10">
                    <span className="text-[#1A1714]">Operational setup (banking, accounting, reporting)</span>
                    <span className="text-[#1A1714]/60 font-mono">CHF 10,000–15,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#1A1714]/10">
                    <span className="text-[#1A1714]">Compliance + admin buffer</span>
                    <span className="text-[#1A1714]/60 font-mono">CHF 5,000–10,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#1A1714]">Pilot reserve for first funded projects</span>
                    <span className="text-[#1A1714]/60 font-mono">CHF 5,000–10,000</span>
                  </div>
                </div>
              </div>
            </section>

            {/* How PAU Works Section */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-6">
                How PAU Works
              </h2>
              <div className="editorial-body text-lg text-[#1A1714]/80 space-y-4">
                <p>
                  <strong>The Foundation</strong> funds athletes, projects, and access — removing financial barriers so performance is not limited by logistics or geography.
                </p>
                <p>
                  <strong>PAUMAG / Production</strong> documents the work and makes impact visible. Editorial storytelling that treats athletes as cultural subjects, not marketing objects.
                </p>
                <p>
                  <strong>Partners</strong> (brands and agencies) support through paid collaborations with agreed deliverables. <a href="/partners" className="text-[#B8352B] hover:underline">Learn about partnerships →</a>
                </p>
                <p className="text-[#1A1714]/60 italic">
                  Three pillars. One mission: fund what matters, document what matters, sustain what matters.
                </p>
              </div>
            </section>

            {/* Internal Links */}
            <section className="mb-16 p-6 bg-[#1A1714]/5 border border-[#1A1714]/10">
              <p className="text-[#1A1714]/50 text-xs uppercase tracking-wider mb-4">Explore More</p>
              <div className="flex flex-wrap gap-4">
                <a href="/foundation" className="text-[#B8352B] hover:underline">All Foundation Projects</a>
                <span className="text-[#1A1714]/30">·</span>
                <a href="/magazine/summer-camp" className="text-[#B8352B] hover:underline">PAU Summer Camp</a>
                <span className="text-[#1A1714]/30">·</span>
                <a href="/partners" className="text-[#B8352B] hover:underline">Partners</a>
                <span className="text-[#1A1714]/30">·</span>
                <a href="/trackrunning" className="text-[#B8352B] hover:underline">Trackrunning</a>
                <span className="text-[#1A1714]/30">·</span>
                <a href="/magazine" className="text-[#B8352B] hover:underline">PAUMAG</a>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-16">
              <h2 className="article-title text-2xl md:text-3xl text-[#1A1714] mb-8">
                FAQ
              </h2>
              <div className="space-y-6">
                <div className="pb-6 border-b border-[#1A1714]/10">
                  <h3 className="text-[#1A1714] font-medium mb-2">Is the foundation registered yet?</h3>
                  <p className="editorial-body text-[#1A1714]/70">Not yet. The PAU Foundation is currently in formation. The CHF 50,000 target covers the registration and operational setup required to formalize it.</p>
                </div>
                <div className="pb-6 border-b border-[#1A1714]/10">
                  <h3 className="text-[#1A1714] font-medium mb-2">Are contributions tax-deductible?</h3>
                  <p className="editorial-body text-[#1A1714]/70">Not at this stage. Tax-deductibility will depend on the final legal structure and jurisdiction. Once registered, we will provide clear guidance.</p>
                </div>
                <div className="pb-6 border-b border-[#1A1714]/10">
                  <h3 className="text-[#1A1714] font-medium mb-2">When will registration happen?</h3>
                  <p className="editorial-body text-[#1A1714]/70">Target: 2026. Timeline depends on reaching the funding goal and completing legal requirements in Switzerland.</p>
                </div>
                <div className="pb-6 border-b border-[#1A1714]/10">
                  <h3 className="text-[#1A1714] font-medium mb-2">Who decides how funds are allocated?</h3>
                  <p className="editorial-body text-[#1A1714]/70">PAU's founding team, with clear criteria for support and transparent reporting. Once formalized, the foundation will have proper governance.</p>
                </div>
                <div className="pb-6 border-b border-[#1A1714]/10">
                  <h3 className="text-[#1A1714] font-medium mb-2">What will be funded first?</h3>
                  <p className="editorial-body text-[#1A1714]/70">Priority: athlete training camps, competition travel, and visibility infrastructure. The <a href="/magazine/summer-camp" className="text-[#B8352B] hover:underline">PAU Summer Camp</a> is our flagship project.</p>
                </div>
                <div>
                  <h3 className="text-[#1A1714] font-medium mb-2">How can brands support vs individuals?</h3>
                  <p className="editorial-body text-[#1A1714]/70">Individuals can donate directly via the Foundation. Brands and agencies looking for deliverables should explore <a href="/partners" className="text-[#B8352B] hover:underline">paid partnerships</a> instead.</p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-16 pt-12 border-t border-[#1A1714]/10 text-center">
              <p className="editorial-headline text-3xl md:text-4xl text-[#1A1714] leading-[1.1] mb-6">
                Build with us.
              </p>
              <p className="editorial-body text-lg text-[#1A1714]/60 max-w-xl mx-auto">
                CHF 50,000 to create a foundation that funds what matters.
              </p>
            </div>

            {/* Back Navigation */}
            <div className="pt-12 mt-12 border-t border-[#1A1714]/10">
              <a href="/magazine" className="editorial-link inline-flex items-center gap-2">
                <ArrowLeft size={16} /> Back to PAU MAG
              </a>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

// ============================================
// APP ROUTER
// ============================================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Main Navigation Pages */}
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/camps" element={<CampsPage />} />
          {/* Talent page temporarily disabled - in backlog */}
          {/* <Route path="/talent" element={<TalentPage />} /> */}
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/coaching" element={<CoachingPage />} />
          <Route path="/athletes" element={<AthletesPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/coaches" element={<CoachesPage />} />
          <Route path="/magazine" element={<MagazinePage />} />
          <Route path="/magazine/dimensional-playground" element={<ArticlePage />} />
          <Route path="/magazine/coach-nicolas" element={<CoachNicolasArticlePage />} />
          <Route path="/magazine/daouda" element={<DaoudaArticlePage />} />
          <Route path="/magazine/fatima-pau-sane" element={<FatimaArticlePage />} />
          <Route path="/magazine/olympic-retreat-dakar" element={<OlympicRetreatArticlePage />} />
          <Route path="/magazine/dakar-2026" element={<DakarArticlePage />} />
          {/* Legacy routes (keep for old links) */}
          <Route path="/trackrunning" element={<TrainingPage />} />
          <Route path="/partners" element={<TalentPage />} />
          <Route path="/goods" element={<GoodsPage />} />
          <Route path="/why-pau" element={<WhyPauPage />} />
          <Route path="/sponsors" element={<TalentPage />} />
          <Route path="/deck" element={<DeckPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
